/*
 * Southern Air Trade — application logic.
 *
 * Ported from Southern Air Trade.dc.html (the design component's logic class). Copy,
 * timings, sound design, animation state machines, era terminals, the magazine, Case
 * Files and Prometheus.OS are carried over unchanged. Only the data layer moved:
 *
 *   products   -> Shopify products (window.SAT_DATA.products, emitted by sat-bootstrap)
 *   cart       -> Shopify Ajax Cart API (/cart.js, /cart/add.js, /cart/change.js)
 *   accounts   -> Shopify customer accounts (/account/login, /register, /logout)
 *   orders     -> real Shopify orders, mapped onto the existing five-step tracker
 *   wishlist   -> still localStorage (sat_wishlist). Shopify has no wishlist primitive
 *                 and a multi-device backend is out of scope; this is a documented
 *                 simplification, not an oversight.
 *   clearance, sealed bids and consignments -> still local, per machine and customer.
 *                 Compartment 41-C is a themed interactive, not financial infrastructure.
 *   Prometheus.OS -> still behind its coming-soon gate (promComingSoon: true).
 */
(function(){
'use strict';
class SATComponent extends SAT.Logic {
  classifiedComingSoon = !(window.SAT_DATA && window.SAT_DATA.settings && window.SAT_DATA.settings.classifiedComingSoon === false);
  state = {
    view: 'site',
    bootLines: [],
    activeRecord: null,
    active70: null,
    active90: null,
    active00: null,
    activeRd: null,
    returnTo: 'site',
    promLines: [],
    promStage: 'chat',
    popups: [],
    crtDrift: false,
    degaussing: false,
    openCase: 0,
    cart: [],
    cartOpen: false,
    query: '',
    activeCat: 'All',
    sortBy: 'Newest Intake',
    detailId: null,
    checkoutOpen: false,
    orderNo: null,
    clearanceOpen: false,
    clearanceCode: '',
    clearanceMsg: '',
    ship: {name:'',addr:'',city:'',zip:'',email:''},
    heroStripStep: 0,
    heroStripNoTrans: false,
    boardStep: [3,3,3,3,3,3],
    boardNoTransition: [false,false,false,false,false,false],
    ambientOn: false,
    page: 'home',
    slideSwapping: false,
    projRolled: false,
    lampOff: false,
    classifiedUnlocked: false,
    codeInput: '',
    codeError: false,
    codePending: null,
    wishlist: [],
    wishlistOpen: false,
    promNotifyEmail: '',
    promNotifyMsg: '',
    promNotifySubmitted: (()=>{ try{ return localStorage.getItem('sat_prom_notified')==='1'; }catch(e){ return false; } })(),
    trackInput: '',
    trackResult: null,
    consignCond: 'Very Good',
    now: Date.now(),
    eraFocus: -1,
    printIssue: false
  };
  _auctionBase = Date.now();

  constructor(props){
    super(props);
    this._boardRows = [
      {cities:['Miami, FL','Homestead, FL','Key West, FL','Jacksonville, FL','Tampa, FL'], codes:['1094','1095','1096','1097','1098']},
      {cities:['Norfolk, VA','Newport News, VA','Portsmouth, VA','Suffolk, VA','Chesapeake, VA'], codes:['2210','2211','2212','2213','2214']},
      {cities:['San Diego, CA','Twentynine Palms, CA','Pendleton, CA','Coronado, CA','El Centro, CA'], codes:['3401','3402','3403','3404','3405']},
      {cities:['Colorado Springs, CO','Peterson AFB, CO','Cheyenne Mtn, CO','Schriever, CO','Pueblo, CO'], codes:['4511','4512','4513','4514','4515']},
      {cities:['Mammoth Lakes, CA','Bishop, CA','China Lake, CA','Ridgecrest, CA','Fresno, CA'], codes:['3472','5602','5603','5604','5605']},
      {cities:['Fayetteville, NC','Fort Bragg, NC','Wilmington, NC','Goldsboro, NC','Jacksonville, NC'], codes:['6180','6181','6182','6183','6184']}
    ].map((r,i)=>{
      const STAT = [
        {status:'CLEARED',  color:'#8FE39C', remark:'ON FILE'},
        {status:'AIRBORNE', color:'#F0C77A', remark:'IN TRANSIT'},
        {status:'BOARDING', color:'#8FE39C', remark:'GATE C‑14'},
        {status:'HOLDING',  color:'#E08A2E', remark:'WX HOLD'},
        {status:'LANDED',   color:'#B7BEC4', remark:'SIGNED FOR'}
      ];
      const cell = (c,j)=>({city:c, code:r.codes[j], ...STAT[(i+j)%STAT.length]});
      return {
      ...r,
      stack: [...r.cities.map(cell), cell(r.cities[0],0)],
      flipDelay: (i*1.3+2.5).toFixed(1)+'s',
      dotStyle: `animation:pulseDot 1.8s ease-in-out infinite;animation-delay:${(i*0.4).toFixed(1)}s`
    };});
    this._dustMotes = Array.from({length:34},(_,i)=>({
      left: (4 + (i*11.3)%92).toFixed(1)+'%',
      top: (6 + (i*17.7)%86).toFixed(1)+'%',
      dur: (5.5 + (i%7)*0.9).toFixed(1)+'s',
      delay: '-'+((i*0.73)%9).toFixed(1)+'s',
      size: (i%5===0 ? '3px' : (i%3===0 ? '2px' : '1px')),
      op: (i%4===0 ? '.85' : '.45')
    }));
    this._sprocket = Array.from({length:14});
    this._rivets = Array.from({length:18});
  }

  heroVideoRef = SAT.React.createRef();
  // one cached handler per index — identity is stable across renders, so a stale
  // closure can never commit the previous plate
  heroPick(i){
    this._heroPicks = this._heroPicks || {};
    if(!this._heroPicks[i]){
      this._heroPicks[i] = ()=>{
        clearInterval(this._heroTimer);
        this.setState({heroPlate:i}, ()=>this.startHeroRotation());
      };
    }
    return this._heroPicks[i];
  }
  startHeroRotation(){
    clearInterval(this._heroTimer);
    this._heroTimer = setInterval(()=>{
      if(this.state.view!=='site' || this.state.page!=='home') return;
      this.setState(s=>({heroPlate:((s.heroPlate||0)+1)%4}));
    }, 7500);
  }
  componentDidMount(){
    // preload the jingle bed so boots start it instantly instead of after a metadata fetch
    try{
      if(this._jingleSrc){
        const a = new Audio(); a.preload = 'auto'; a.src = this._jingleSrc;
        a.load(); this._jingleEl = a; this._jingleLoaded = true;
      }
    }catch(e){}
    this.analyzeJingle();
    this.restoreSession();
    this.restoreCart();
    this.restoreWishlist();
    this.restoreMute();
    this.startAuctionClock();
    this.applyHash();
    this._onHash = ()=>this.applyHash();
    window.addEventListener('hashchange', this._onHash);
    this._onEraKeys = (e)=>{
      if(this.state.page!=='catalog' && this.state.page!=='home') return;
      if((this.state.view && this.state.view !== 'site') || this.state.cartOpen || this.state.wishlistOpen || this.state.detailId) return;
      const t = e.target;
      if(t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      this.eraKeyNav(e);
    };
    window.addEventListener('keydown', this._onEraKeys);
    this.startBoardWave(); this.startGlitchWatcher(); this.startHeroStrip(); this.startCrtDriftWatcher(); this.startPopupWatcher();
    this._projWheel = e=>{
      if(this.state.view!=='view70' && this.state.view!=='list70') return;
      if(e.deltaY > 24 && !this.state.projRolled) this.setProjRolled(true);
      else if(e.deltaY < -24 && this.state.projRolled) this.setProjRolled(false);
    };
    window.addEventListener('wheel', this._projWheel, {passive:true});
    this.startClassifiedRadio();
    this._clockTimer = setInterval(()=>{ if(this.state.view==='site') this.forceUpdate(); }, 1000);
    this.startHeroRotation();
    const cinemagraph = (window.SAT_DATA && window.SAT_DATA.media && window.SAT_DATA.media.cinemagraph) || '';
    if(cinemagraph){
      const v=this.heroVideoRef.current;
      if(v){
        v.src=cinemagraph; v.muted=true; v.loop=true; v.playsInline=true;
        v.play().catch(()=>{});
      }
    }
    const resumeAudio=()=>{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      if(this._ctx.state==='suspended') this._ctx.resume();
    };
    window.addEventListener('pointerdown', resumeAudio);
    window.addEventListener('keydown', resumeAudio);
    window.addEventListener('scroll', resumeAudio, {passive:true});
  }
  // Stock is Shopify's. snippets/sat-bootstrap.liquid emits one record per product in
  // exactly the shape the design component's logic expects — {id, name, cat, price, era,
  // cond, stock, blurb, detail} — plus handle/url/variantId/photo for the storefront
  // wiring. Every _products.find / .filter / .length call site below is unchanged.
  _products = (window.SAT_DATA && window.SAT_DATA.products) || [];
  _eraTiles = 5;   // 1970s, 1980s, 1990s, 2000s, CLASSIFIED — keep in step with the #catalog tiles
  _cats = ['All','Field Gear','Uniforms','Aviation','Comms','Load-Bearing'];
  _caseFiles = [
    {no:'CF‑041', date:'03.14', kind:'FIELD NOTE', region:'FAYETTEVILLE, NC', tabColor:'#C9A227', status:'OPEN',
     title:'Tracing An ALICE Rig Back To Its Unit',
     body:'Six packs came in from a depot lot with the contract stencils still legible under the flap. Cross-referencing the DSA numbers against the 1983 solicitation index put four of them inside a single procurement run — the other two are later commercial copies with the wrong snap hardware and a frame weld that sits a half-inch high.',
     finding:'FINDING: 4 of 6 authenticated to run. 2 reclassified as reproduction, repriced, disclosed on listing.',
     attachments:['stencil-plate-04.tif','dsa-index-1983.pdf','weld-comparison.tif'], clerk:'J. MERRICK',
     article:['Six packs came in from a depot lot with the contract stencils still legible under the flap. Cross-referencing the DSA numbers against the 1983 solicitation index put four of them inside a single procurement run — the other two are later commercial copies with the wrong snap hardware and a frame weld that sits a half-inch high.',
       'The four authenticated packs share a matching stencil font and rivet spacing consistent with the 1983 solicitation — enough to put them on paper as issue, not just issue-pattern. The two outliers use a diamond-mesh nylon that was not in the original spec sheet, a tell we have seen on at least one other commercial reproduction line.',
       'All six are listed with their grade disclosed on the tag. We do not blend an authenticated lot with a reproduction lot to move faster — the two prices sit forty dollars apart for a reason.']},
    {no:'CF‑038', date:'02.02', kind:'PROVENANCE', region:'INTAKE — UNIT C‑14', tabColor:'#8FE39C', status:'OPEN',
     title:'What "Origin Record: Unverified" Actually Means',
     body:'Every item that crosses the counter gets one of four intake grades. Unverified is not a warning — it means the paper trail ends before we can put a unit or a contract behind it. Most surplus lots arrive this way. We publish the grade rather than guess, and we do not upgrade a grade on a customer’s say-so.',
     finding:'STANDING POLICY: grades are never revised without a documentary source on file.',
     attachments:['intake-grading-v4.pdf','counter-log-jan.csv'], clerk:'R. HALE',
     article:['Every item that crosses the counter gets one of four intake grades. Unverified is not a warning — it means the paper trail ends before we can put a unit or a contract behind it. Most surplus lots arrive this way. We publish the grade rather than guess, and we do not upgrade a grade on a customer\u2019s say-so.',
       'The four grades are AUTHENTICATED, PROBABLE, UNVERIFIED and DISPUTED. Unverified is the default, not a downgrade — it is where an item sits until a contract number, a unit marking, or a matching photograph moves it up or down the scale.',
       'Roughly six in ten pieces that cross the counter never leave Unverified, and that is fine. What we will not do is let a customer\u2019s confidence stand in for documentation — a grade only moves when a source goes in the file next to it.']},
    {no:'CF‑036', date:'01.19', kind:'DISPATCH', region:'SOUTHEAST CORRIDOR', tabColor:'#C9A227', status:'OPEN',
     title:'Inside The Fayetteville Intake Route',
     body:'Our busiest domestic corridor runs three pickups a week between Fayetteville, Columbia and the Mammoth Lakes consolidation floor. The paperwork is heavier than the freight: every pallet moves on a manifest that has to survive two state lines and a federal surplus disposition form.',
     finding:'THROUGHPUT: 14 crates outbound weekly. Zero disputed manifests this quarter.',
     attachments:['corridor-map-se.svg','dd1348-sample.pdf','pallet-log-q1.csv'], clerk:'J. MERRICK',
     article:['Our busiest domestic corridor runs three pickups a week between Fayetteville, Columbia and the Mammoth Lakes consolidation floor. The paperwork is heavier than the freight: every pallet moves on a manifest that has to survive two state lines and a federal surplus disposition form.',
       'Each leg carries its own DD Form 1348 and a state-line manifest, so a single pallet can generate more paperwork than the freight itself weighs. Columbia is the swap point — trucks headed north hand off to trucks headed for the consolidation floor without the load touching a second warehouse.',
       'The corridor has run three pickups a week since 2019 without a disputed manifest, which is the only statistic on this route anyone here actually brags about.']},
    {no:'CF‑029', date:'11.27', kind:'RESEARCH', region:'MAMMOTH LAKES, CA', tabColor:'#8FE39C', status:'OPEN',
     title:'The Kneeboard That Came Back Twice',
     body:'An alloy kneeboard sold in 2019, returned as a consignment in 2022, then offered to us a third time by an unrelated seller in Reno. Same anodising wear, same replaced strap. We keep photographs of every outbound item precisely so this kind of loop is visible.',
     finding:'FINDING: item identity confirmed across three transactions. Chain of custody now complete.',
     attachments:['kneeboard-2019.tif','kneeboard-2022.tif','reno-offer.eml'], clerk:'R. HALE',
     article:['An alloy kneeboard sold in 2019, returned as a consignment in 2022, then offered to us a third time by an unrelated seller in Reno. Same anodising wear, same replaced strap. We keep photographs of every outbound item precisely so this kind of loop is visible.',
       'The strap replacement is the giveaway: a slightly darker webbing sewn in with a bar-tack pattern that does not match factory stitching. Combined with the anodising wear pattern on the clip, it was enough to place all three listings as the same physical object.',
       'We photograph every outbound item for exactly this reason. It will not stop a piece from changing hands twice before it comes back to us, but it means we recognize it when it does.']},
    {no:'CF‑022', date:'08.05', kind:'RESTRICTED', region:'[REDACTED]', tabColor:'#C2503A', status:'UNDER SEAL',
     title:'Consignment 4471 — Handling Notes',
     body:'File contents withheld. Summary available to holders of a current clearance code.',
     finding:'ACCESS: clearance code required. Issued with any completed order.',
     attachments:['[SEALED]','[SEALED]'], clerk:'—'},
    {no:'CF‑017', date:'05.30', kind:'RESTRICTED', region:'[REDACTED]', tabColor:'#C2503A', status:'UNDER SEAL',
     title:'Disposition Of The Vientiane Lot',
     body:'File contents withheld. Summary available to holders of a current clearance code.',
     finding:'ACCESS: clearance code required. Issued with any completed order.',
     attachments:['[SEALED]'], clerk:'—'}
  ];
  _heroStrip = [
    {label:'FLIGHT',color:'#F0C77A',minw:'120px',flex:'0 0 auto',divider:'1px dashed rgba(240,199,122,.35)',
     vals:['SAT‑3472','SAT‑1094','SAT‑2210','SAT‑4471']},
    {label:'ROUTING',color:'#F4EFE3',minw:'280px',flex:'1 1 280px',divider:'1px dashed rgba(240,199,122,.35)',
     vals:['MAMMOTH LAKES → ON FILE','MIAMI, FL → ON FILE','NORFOLK, VA → ON FILE','TUCSON, AZ → ON FILE']},
    {label:'LOAD',color:'#F4EFE3',minw:'110px',flex:'0 0 auto',divider:'1px dashed rgba(240,199,122,.35)',
     vals:['14 CRATES','6 PALLETS','22 CRATES','3 CONTAINERS']},
    {label:'STATUS',color:'#8FE39C',minw:'130px',flex:'1 1 auto',divider:'none',
     vals:['WHEELS UP','LOADING','ON APRON','CLEARED']}
  ];
  _skyline = [
    {x:'3%',w:'44px',h:'34%',fill:'linear-gradient(180deg,#EDE7DA,#CFC7B6)',radius:'3px 3px 0 0'},
    {x:'11%',w:'70px',h:'22%',fill:'linear-gradient(180deg,#E4DDCE,#C4BCAA)',radius:'40px 40px 0 0'},
    {x:'20%',w:'34px',h:'48%',fill:'linear-gradient(180deg,#F0EADC,#D2CABA)',radius:'3px'},
    {x:'27%',w:'96px',h:'16%',fill:'linear-gradient(180deg,#DFD8C9,#BEB6A4)',radius:'60px 60px 0 0'},
    {x:'39%',w:'40px',h:'40%',fill:'linear-gradient(180deg,#EDE7DA,#CBC3B2)',radius:'3px 3px 0 0'},
    {x:'47%',w:'58px',h:'26%',fill:'linear-gradient(180deg,#E7E0D1,#C7BFAD)',radius:'3px'},
    {x:'56%',w:'30px',h:'52%',fill:'linear-gradient(180deg,#F2ECDE,#D4CCBB)',radius:'3px 3px 0 0'},
    {x:'80%',w:'66px',h:'30%',fill:'linear-gradient(180deg,#E9E2D3,#C9C1AF)',radius:'3px 3px 0 0'},
    {x:'90%',w:'44px',h:'20%',fill:'linear-gradient(180deg,#E2DBCC,#C2BAA8)',radius:'22px 22px 0 0'}
  ];
  async startHeroStrip(){
    const n = this._heroStrip[0].vals.length;
    while(this._boardRunning){
      await this.sleep(5200);
      if(!this._boardRunning) return;
      const next = this.state.heroStripStep + 1;
      this.setState({heroStripStep: next, heroStripNoTrans:false});
      if(this.isHeroStripVisible()) this.playRollTick();
      if(next >= n){
        await this.sleep(380);
        this.setState({heroStripStep:0, heroStripNoTrans:true});
        setTimeout(()=>this.setState({heroStripNoTrans:false}), 70);
      }
    }
  }
  // ================= CART (Shopify Ajax Cart API) =================
  // The drawer markup, styling, animation and sound are untouched. Only what populates
  // and mutates the cart changed: /cart.js, /cart/add.js and /cart/change.js now hold it,
  // so the crate survives across devices, sessions and the real checkout.
  _cartRoot(){ return (window.SAT_DATA && window.SAT_DATA.routes && window.SAT_DATA.routes.cart) || '/cart'; }
  _byVariant(vid){ return this._products.find(p=>String(p.variantId)===String(vid)) || null; }
  _cartPost(url, body){
    return fetch(url, {
      method:'POST',
      headers:{'Content-Type':'application/json','Accept':'application/json'},
      body: JSON.stringify(body)
    }).then(r=>r.json());
  }
  saveCart(){ /* Shopify holds the cart; nothing to persist locally. */ }
  adoptCart(data){
    const lines = ((data && data.items) || []).map(it=>{
      const p = this._byVariant(it.variant_id);
      return { id: p ? p.id : String(it.variant_id), qty: it.quantity, key: it.key, variantId: it.variant_id };
    });
    this.setState({cart: lines});
    return lines;
  }
  refreshCart(){
    return fetch(this._cartRoot() + '.js', {headers:{'Accept':'application/json'}})
      .then(r=>r.json()).then(d=>this.adoptCart(d)).catch(()=>null);
  }
  restoreCart(){ this.refreshCart(); }
  addToCart(id, qty){
    const p = this._products.find(p=>p.id===id);
    if(p && p.stock<=0){ this.siteTone('d',0.05); return; }
    if(!p || !p.variantId){ this.siteTone('d',0.05); return; }
    qty = qty || 1;
    // Optimistic, so the crate opens on the same frame it did before.
    this.setState(s=>{
      const cart = s.cart.slice();
      const hit = cart.findIndex(l=>l.id===id);
      if(hit>=0) cart[hit] = {...cart[hit], qty:cart[hit].qty+qty};
      else cart.push({id, qty, variantId:p.variantId});
      return {cart, cartOpen:true};
    });
    this.crateLoad();
    this._cartPost(this._cartRoot() + '/add.js', {items:[{id:p.variantId, quantity:qty}]})
      .then(()=>this.refreshCart())
      .catch(()=>this.refreshCart());
  }
  setQty(id, qty){
    qty = Math.max(1, qty);
    const line = this.state.cart.find(l=>l.id===id);
    this.setState(s=>({cart: s.cart.map(l=>l.id===id?{...l,qty}:l)}));
    this.siteClick();
    if(line && line.key){
      this._cartPost(this._cartRoot() + '/change.js', {id:line.key, quantity:qty})
        .then(d=>this.adoptCart(d)).catch(()=>this.refreshCart());
    } else this.refreshCart();
  }
  removeLine(id){
    const line = this.state.cart.find(l=>l.id===id);
    this.setState(s=>({cart: s.cart.filter(l=>l.id!==id)}));
    this.siteTone('d',0.04);
    if(line && line.key){
      this._cartPost(this._cartRoot() + '/change.js', {id:line.key, quantity:0})
        .then(d=>this.adoptCart(d)).catch(()=>this.refreshCart());
    } else this.refreshCart();
  }
  cartLines(){
    return this.state.cart.map(l=>{
      const p = this._products.find(p=>p.id===l.id) || {};
      return {...p, qty:l.qty, lineTotal:'$'+(p.price*l.qty).toLocaleString()};
    });
  }
  subtotalNum(){ return this.state.cart.reduce((t,l)=>{
    const p=this._products.find(p=>p.id===l.id); return t + (p?p.price*l.qty:0);
  },0); }
  visibleProducts(){
    const q = this.state.query.trim().toLowerCase();
    let list = this._products.filter(p=>{
      const catOk = this.state.activeCat==='All' || p.cat===this.state.activeCat;
      const qOk = !q || (p.name+' '+p.cat+' '+p.era+' '+p.id+' '+p.blurb).toLowerCase().includes(q);
      return catOk && qOk;
    });
    if(this.state.sortBy==='Price, Low to High') list = list.slice().sort((a,b)=>a.price-b.price);
    if(this.state.sortBy==='Price, High to Low') list = list.slice().sort((a,b)=>b.price-a.price);
    if(this.state.sortBy==='Oldest Era') list = list.slice().sort((a,b)=>a.era.localeCompare(b.era));
    return list;
  }
  // ================= ORDERS (Shopify) =================
  // Real orders, read off the customer object by snippets/sat-bootstrap.liquid and
  // reshaped into the ledger record the drawer, the tracker and the clearance gate
  // already expect: {no, at, lines:[{id,name,qty,price}], total, user, status, step}.
  nextOrderNumber(){ return (this.readOrders().slice(-1)[0]||{}).no || null; }
  readOrders(){ return (window.SAT_DATA && window.SAT_DATA.orders) || []; }
  writeOrders(){ /* Shopify is the ledger. */ }
  placeOrder(){
    // The chit is a pre-flight; Shopify's checkout takes the address and the money.
    this.playReadyChime();
    const url = (window.SAT_DATA && window.SAT_DATA.routes && window.SAT_DATA.routes.checkout) || '/checkout';
    setTimeout(()=>{ window.location.href = url; }, 240);
  }
  // Shopify's own fulfillment and financial status, mapped onto the five steps the
  // tracker was drawn for. Orders with no fulfillment yet fall back to age, exactly
  // as the original did, so the step tracker never sits empty.
  orderStatusFor(at, order){
    const st = order && order.shopifyStatus;
    if(st === 'delivered')   return {label:'DELIVERED',  color:'#4A5158', note:'signed for', step:4};
    if(st === 'in_transit')  return {label:'IN TRANSIT', color:'#2F6B3A', note:'left the ramp', step:3};
    if(st === 'out_for_delivery') return {label:'IN TRANSIT', color:'#2F6B3A', note:'on the van', step:3};
    if(st === 'fulfilled')   return {label:'CRATED',     color:'#2F6B3A', note:'boxed for Thursday freight', step:2};
    if(st === 'partial')     return {label:'PULLED',     color:'#B07A12', note:'off the shelf, in Hall 2', step:1};
    if(st === 'cancelled')   return {label:'CANCELLED',  color:'#8A3521', note:'withdrawn from the ramp', step:0};
    if(st === 'unpaid')      return {label:'FILED',      color:'#B07A12', note:'awaiting payment', step:0};
    const days = Math.floor((Date.now() - at) / 86400000);
    if(days < 1) return {label:'FILED', color:'#B07A12', note:'awaiting clerk review', step:0};
    if(days < 3) return {label:'PULLED', color:'#B07A12', note:'off the shelf, in Hall 2', step:1};
    if(days < 5) return {label:'CRATED', color:'#2F6B3A', note:'boxed for Thursday freight', step:2};
    if(days < 12) return {label:'IN TRANSIT', color:'#2F6B3A', note:'left the ramp', step:3};
    return {label:'DELIVERED', color:'#4A5158', note:'signed for', step:4};
  }
  // ================= AUCTION CLOCK =================
  startAuctionClock(){
    if(this._auctionTimer) return;
    this._auctionTimer = setInterval(()=>{
      if(this.state.view==='listRd' && this.state.rdSub==='Auction') this.setState({now:Date.now()});
    }, 1000);
  }
  fmtCountdown(ms){
    if(ms<=0) return 'CLOSED';
    const s = Math.floor(ms/1000), d = Math.floor(s/86400), h = Math.floor(s%86400/3600),
          m = Math.floor(s%3600/60), sec = s%60;
    if(d>0) return d+'D '+String(h).padStart(2,'0')+'H';
    if(h>0) return String(h).padStart(2,'0')+'H '+String(m).padStart(2,'0')+'M';
    return String(m).padStart(2,'0')+'M '+String(sec).padStart(2,'0')+'S';
  }
  // ================= DEEP LINKS =================
  _hashFor(s){
    if(s.page==='casefile') return '#case-file/' + (this._caseFiles[s.caseArticleIndex||0]||{}).no;
    return {home:'', manifest:'#manifest', store:'#import', catalog:'#archives', casefiles:'#case-files'}[s.page] ?? '';
  }
  syncHash(){
    const h = this._hashFor(this.state);
    const cur = location.hash;
    if(h !== cur && !(h==='' && cur==='')) {
      try{ history.replaceState(null,'', h || location.pathname); }catch(e){}
    }
  }
  applyHash(){
    const h = decodeURIComponent(location.hash||'');
    if(!h) return;
    const cf = h.match(/^#case-file\/(.+)$/);
    if(cf){
      const i = this._caseFiles.findIndex(c=>c.no.replace(/[‑-]/g,'-')===cf[1].replace(/[‑-]/g,'-'));
      if(i>=0){ this.setState({page:'casefile', caseArticleIndex:i}); return; }
    }
    const map = {'#manifest':'manifest','#import':'store','#archives':'catalog','#case-files':'casefiles'};
    if(map[h]) this.setState({page:map[h]});
  }
  // ================= ERA KEYBOARD NAV =================
  eraKeyNav(e){
    const openers = ['openCatalog70','openCatalog80','openCatalog90','openCatalog00','openCatalogRd'];
    const n = openers.length;
    if(e.key==='ArrowRight' || e.key==='ArrowLeft'){
      e.preventDefault();
      const cur = this.state.eraFocus < 0 ? 0 : this.state.eraFocus;
      const next = e.key==='ArrowRight' ? (cur+1)%n : (cur-1+n)%n;
      this.siteTone('b',0.03);
      this.setState({eraFocus:next});
    } else if(e.key==='Enter' || e.key===' '){
      const i = this.state.eraFocus;
      if(i>=0 && this[openers[i]]){ e.preventDefault(); this[openers[i]](); }
    }
  }
  // ================= WISHLIST =================
  readWishlist(){ try{ return JSON.parse(localStorage.getItem('sat_wishlist')||'[]'); }catch(e){ return []; } }
  writeWishlist(list){ try{ localStorage.setItem('sat_wishlist', JSON.stringify(list)); }catch(e){} }
  restoreWishlist(){
    const w = this.readWishlist().filter(id=>this._products.some(p=>p.id===id));
    if(w.length) this.setState({wishlist:w});
  }
  toggleWishlist(id){
    const has = this.state.wishlist.includes(id);
    const next = has ? this.state.wishlist.filter(x=>x!==id) : [...this.state.wishlist, id];
    this.writeWishlist(next);
    this.siteTone(has?'d':'g', 0.04);
    this.setState({wishlist:next});
  }
  // ================= ORDER TRACKING (public lookup) =================
  trackOrder(){
    const raw = String(this.state.trackInput||'').toUpperCase().replace(/\s+/g,'');
    if(!raw){ this.setState({trackResult:{error:'Enter an order number.'}}); return; }
    const wanted = raw.replace(/^SAT-?/,'');
    const order = this.readOrders().find(o=>String(o.no).toUpperCase().replace(/^SAT-?/,'')===wanted);
    this.siteTone(order?'g':'d', 0.04);
    if(!order){ this.setState({trackResult:{error:'No order on file under that number.'}}); return; }
    const st = this.orderStatusFor(order.at, order);
    this.setState({trackResult:{
      no: order.no, status: st.label, color: st.color, note: st.note, step: st.step,
      count: order.lines.length + (order.lines.length===1?' item':' items'),
      total: '$'+Number(order.total||0).toLocaleString()
    }});
  }
  // ================= PROMETHEUS NOTIFY-ME =================
  readPromNotify(){ try{ return JSON.parse(localStorage.getItem('sat_prom_notify')||'[]'); }catch(e){ return []; } }
  submitPromNotify(){
    const email = String(this.state.promNotifyEmail||'').trim();
    if(!email || !email.includes('@') || !email.includes('.')){ this.setState({promNotifyMsg:'Enter a working address.'}); return; }
    const list = this.readPromNotify();
    if(!list.includes(email)){ list.push(email); try{ localStorage.setItem('sat_prom_notify', JSON.stringify(list)); }catch(e){} }
    try{ localStorage.setItem('sat_prom_notified','1'); }catch(e){}
    this.siteTone('g',0.05);
    this.setState({promNotifySubmitted:true, promNotifyMsg:''});
  }
  openOrders(){
    if(!this.requireAccount('Order history lives with your account. Sign in or open one.')) return;
    this.siteTone('c',0.04);
    this.setState({ordersOpen:true, acctMenu:false});
  }
  closeOrders(){ this.siteTone('g',0.035); this.setState({ordersOpen:false}); }
  // ================= ACCOUNTS (Shopify customer accounts) =================
  // Sign-in, registration and sign-out are Shopify's. The panel, the drawer and the
  // clearance state are the design component's, unchanged. Clearance, bids and
  // consignments have no Shopify primitive and stay on this machine, keyed by customer.
  _acctKey(){
    const c = window.SAT_DATA && window.SAT_DATA.customer;
    return c && c.id ? 'sat_acct_' + c.id : null;
  }
  readLocalAccount(){
    const k = this._acctKey();
    if(!k) return {};
    try{ return JSON.parse(localStorage.getItem(k)||'{}') || {}; }catch(e){ return {}; }
  }
  currentAccount(){
    const c = window.SAT_DATA && window.SAT_DATA.customer;
    if(!c || !c.id) return null;
    const local = this.readLocalAccount();
    return {
      user: c.name || c.email || ('SAT-' + c.id),
      email: c.email || '',
      id: c.id,
      created: c.createdAt || null,
      clearance: !!local.clearance,
      clearedAt: local.clearedAt || null,
      bids: local.bids || [],
      consignments: local.consignments || [],
      orders: (this.readOrders()||[]).map(o=>o.no)
    };
  }
  saveAccount(acct){
    const k = this._acctKey();
    if(!k || !acct) return;
    try{
      localStorage.setItem(k, JSON.stringify({
        clearance: !!acct.clearance,
        clearedAt: acct.clearedAt || null,
        bids: acct.bids || [],
        consignments: acct.consignments || []
      }));
    }catch(e){}
  }
  readAccounts(){ const a = this.currentAccount(); return a ? [a] : []; }
  writeAccounts(){ /* Shopify holds the ledger of who exists. */ }
  readSession(){ const a = this.currentAccount(); return a ? a.user : null; }
  restoreSession(){
    const acct = this.currentAccount();
    if(acct) this.setState({sessionUser:acct.user, classifiedUnlocked:!!acct.clearance});
  }
  openAuth(mode, reason){
    this.siteTone('c',0.04);
    this.setState({authOpen:true, authMode:mode||'signin', authUser:'', authEmail:'', authPass:'', authPass2:'', authMsg:reason||'', authOk:false});
  }
  closeAuth(){ this.siteTone('g',0.035); this.setState({authOpen:false, authMsg:''}); }
  setAuthMode(mode){ this.beep(560,0.04,'sine',0.03); this.setState({authMode:mode, authMsg:'', authOk:false}); }
  submitAuth(){
    // Credentials are Shopify's business, not this machine's. The panel hands off to
    // the real customer-account forms, which own validation, rate limiting and reset.
    const r = (window.SAT_DATA && window.SAT_DATA.routes) || {};
    this.beep(560,0.04,'sine',0.03);
    window.location.href = this.state.authMode==='signup'
      ? (r.register || '/account/register')
      : (r.login || '/account/login');
  }
  signOut(){
    this.beep(300,0.07,'sine',0.04);
    const r = (window.SAT_DATA && window.SAT_DATA.routes) || {};
    window.location.href = r.logout || '/account/logout';
  }
  _counter = {
    contact: { title:'Front Counter', kicker:'HOW TO REACH US',
      lines:[
        'The counter is staffed Tuesday to Saturday, 0800 to 1700 Pacific. Sunday and Monday the building is locked and the phone rings in an empty room.',
        'Telephone (760) 555‑0114. If nobody picks up, we are on the ramp — try again after four.',
        'Written enquiries: Counter 9, Southern Air Trade, Mammoth Lakes CA 93546. We answer letters in the order they arrive, which is not the order they were sent.'
      ],
      rows:[['Telephone','(760) 555‑0114'],['Hours','TUE–SAT · 0800–1700 PT'],['Counter','9 · MAIN FLOOR'],['Reply time','2–9 DAYS']] },
    sourcing: { title:'How Sourcing Works', kicker:'WHERE IT COMES FROM',
      lines:[
        'We buy at government disposal auctions, from closing depots, and from estates that would rather not itemise. Everything is inspected at intake and graded by hand.',
        'Condition is stated plainly. Fuel ghosting, unit markings, and repairs are disclosed on the listing — we would rather lose the sale than argue about it later.',
        'We do not source to order and we do not chase serial numbers. If it is not on the shelf, it is not coming.'
      ],
      rows:[['Intake','AUCTION · DEPOT · ESTATE'],['Grading','BY HAND AT INTAKE'],['Disclosure','FULL, INCLUDING FAULTS'],['Special orders','NOT ACCEPTED']] },
    returns: { title:'Returns & Claims', kicker:'ALL GOODS SOLD AS‑IS',
      lines:[
        'Fourteen days from delivery for anything materially different from its listing. Photograph it before you unpack it further — the photograph settles most claims in your favour.',
        'Wear, patina, faded stencils, and honest age are not faults. They are the reason the price is what it is.',
        'Refunds settle the way you paid, less freight. We do not restock fees. We do not haggle either.'
      ],
      rows:[['Window','14 DAYS FROM DELIVERY'],['Covers','MISDESCRIBED GOODS'],['Excludes','WEAR · AGE · PATINA'],['Freight','NOT REFUNDED']] },
    trade: { title:'Trade Accounts',kicker:'FOR DEPOTS, FILM, AND MUSEUMS',
      lines:[
        'Volume terms for production houses, museums, and dealers moving pallets rather than pieces. Net 45 on approval, and a standing line on anything we hold in depth.',
        'Trade accounts see the restricted index without a clearance code and are telephoned before a lot goes to sealed auction.',
        'Apply at the counter with two references. We will call one of them.'
      ],
      rows:[['Terms','NET 45 ON APPROVAL'],['Minimum','$2,500 PER ORDER'],['Includes','RESTRICTED INDEX'],['Apply','2 TRADE REFERENCES']] },
    track: { title:'Track An Order', kicker:'WHERE YOUR CRATE IS',
      lines:[
        'Every filed order gets a SAT number. Quote it at the counter and a clerk will read you the last line of the movement log.',
        'Freight leaves Thursday. A crate that missed Thursday is a crate sitting in Hall 2, and the log will say so.',
        'Your order number is also your clearance code for the restricted index. Keep it.'
      ],
      rows:[['Reference','YOUR SAT‑#### NUMBER'],['Freight day','THURSDAY'],['Transit','5–12 DAYS'],['Also unlocks','RESTRICTED INDEX']] }
  };
  openCounter(key){ this.siteTone('c',0.04); this.setState({counterKey:key}); }
  closeCounter(){ this.siteTone('g',0.035); this.setState({counterKey:null}); }
  shopCategory(cat){
    this.siteTone('b',0.04);
    this.setState({page:'store', activeCat:cat, query:'', detailId:null});
    window.scrollTo(0,0);
  }
  toggleAcctMenu(){ this.siteTone('g',0.03); this.setState(s=>({acctMenu:!s.acctMenu})); }
  // called whenever a clearance code checks out — remembered against the signed-in account
  grantClearance(){
    const acct = this.currentAccount();
    if(acct){ acct.clearance = true; acct.clearedAt = acct.clearedAt || Date.now(); this.saveAccount(acct); }
    this.setState({classifiedUnlocked:true});
  }
  requireAccount(reason){
    if(this.currentAccount()) return true;
    this.openAuth('signin', reason || 'An account is required for this.');
    return false;
  }
  validClearance(raw){
    // normalise the typographic hyphens/dashes a user might paste off the screen
    const code = String(raw||'').trim().toUpperCase().replace(/\s+/g,'').replace(/[\u2010-\u2015\u2212]/g,'-');
    if(code === 'SAT-3472' || code === 'SAT3472') return true;
    // any order number this machine has actually been issued
    const mine = this.myOrderNumbers();
    return mine.includes(code) || mine.includes(code.replace(/^SAT-?/,'SAT-'));
  }
  myOrderNumbers(){
    const out = [];
    for(const o of (this.readOrders()||[])){
      const n = String(o && (o.no || o)).toUpperCase().replace(/\s+/g,'');
      if(n) out.push(n);
    }
    if(this.state.orderNo) out.push(String(this.state.orderNo).toUpperCase().replace(/\s+/g,''));
    const acct = this.currentAccount();
    if(acct && acct.orders) for(const n of acct.orders) out.push(String(n).toUpperCase().replace(/\s+/g,''));
    return out;
  }
  recordOrderNumber(no){
    // Shopify already records the order. Nothing to file locally.
  }
  submitClearance(){
    if(this.validClearance(this.state.clearanceCode)){
      this.setState({clearanceOpen:false, clearanceCode:'', clearanceMsg:''});
      this.grantClearance();
      this.openCatalogRd(true);
    } else {
      this.setState({clearanceMsg: this.myOrderNumbers().length
        ? 'CODE NOT ON FILE. TRY YOUR SAT ORDER NUMBER.'
        : 'CODE NOT ON FILE. NO ORDERS ISSUED TO THIS TERMINAL.'});
      this.beep(174.61,0.12,'square',0.05);
    }
  }
  isHeroStripVisible(){
    if(this.state.view!=='site' || this.state.page!=='home') return false;
    if(document.visibilityState!=='visible') return false;
    const el = document.querySelector('[data-m="pass-strip"]');
    if(!el) return false;
    const r = el.getBoundingClientRect();
    return r.bottom > 40 && r.top < (window.innerHeight - 40);
  }
  isBoardVisible(){
    const el = document.getElementById('board-frame');
    if(!el) return false;
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < (window.innerHeight || document.documentElement.clientHeight);
  }
  componentWillUnmount(){
    this._boardRunning = false;
    clearInterval(this._heroTimer);
    clearInterval(this._clockTimer);
  }

  async startGlitchWatcher(){
    while(this._boardRunning){
      await this.sleep(30000 + Math.random()*30000);
      if(this.state.view==='site' && Math.random()<0.22){
        const opts=[
          {view:'term80', activeRecord:'lbe'},
          {view:'view90', active90:'rig'},
          {view:'viewrd', activeRd:'x000'}
        ];
        const pick=opts[Math.floor(Math.random()*opts.length)];
        this.setState(pick);
        await this.sleep(140+Math.random()*100);
        this.setState({view:'site'});
      }
    }
  }

  setRowStep(i, val, noTrans){
    if(!this._boardRunning || this.state.view!=='site') return;
    this.setState(s=>{
      const step=s.boardStep.slice(); step[i]=val;
      const nt=s.boardNoTransition.slice(); nt[i]=noTrans;
      return {boardStep:step, boardNoTransition:nt};
    });
    if(!noTrans && this.isBoardVisible()) this.playRollTick();
    if(noTrans){
      setTimeout(()=>{
        this.setState(s=>{ const nt=s.boardNoTransition.slice(); nt[i]=false; return {boardNoTransition:nt}; });
      }, 70);
    }
  }
  async startBoardWave(){
    this._boardRunning = true;
    const n = this._boardRows.length, stagger = 200;
    while(this._boardRunning){
      for(let step=1; step<=5; step++){
        for(let i=0;i<n;i++) setTimeout(()=>this.setRowStep(i,step,false), i*stagger);
        await this.sleep(n*stagger + 2400);
      }
      for(let i=0;i<n;i++) setTimeout(()=>this.setRowStep(i,0,true), i*stagger);
      await this.sleep(n*stagger + 300);
    }
  }

  records80 = {
    lbe: { code:"SAT-L801", name:"ALICE LBE HARNESS SET", price:"$112.00",
      desc:"FULL H-HARNESS, PISTOL BELT, AND TWO AMMO POUCHES. WOODLAND PATTERN. RETRIEVED FROM ARCHIVE TERMINAL, RECORD PARTIALLY UNCONVERTED.",
      stats:[["CONDITION","SERVICE-USED"],["PATTERN","WOODLAND M81"],["ORIGIN RECORD","UNVERIFIED DECADE"],["STATUS","IN STOCK"]] },
    vest: { code:"SAT-V220", name:"PASGT FLAK VEST, OD GREEN", price:"$185.00",
      desc:"FRAGMENTATION PROTECTIVE VEST. SOLID OLIVE DRAB COLORWAY. RETRIEVED FROM ARCHIVE TERMINAL, RECORD PARTIALLY UNCONVERTED.",
      stats:[["CONDITION","SERVICE-WORN"],["COLORWAY","OD GREEN"],["ORIGIN RECORD","UNVERIFIED DECADE"],["STATUS","IN STOCK"]] }
  };
  records70 = {
    radio: { code:"SAT-J774", reel:"BRIEFING REEL 12", name:"AN/PRC-77 SURVIVAL RADIO", price:"$210.00",
      desc:"VHF survival transceiver, standard air-crew withdrawal kit. Whip antenna, canvas carry harness, and hand mic included. Accessory set incomplete per intake log.",
      stats:[["Frequency Band","30-76 MHz VHF"],["Issue","Air Crew Withdrawal Kit"],["Condition","Service-Worn"],["Intake Note","Accessory Set Incomplete"]] }
  };
  records90 = {
    rig: { code:"SAT-R990", name:"MODULAR TACTICAL CHEST RIG", price:"$96.00",
      desc:"Early MOLLE-pattern chest rig recovered from the SAT-R990 intake record. Quick-release buckles, adjustable shoulder straps, modular webbing.",
      stats:[["Mfr.","Field Surplus"],["Pattern","Early MOLLE"],["Condition","Late Production"],["Field Test","4.5 / 5"]] }
  };
  records00 = {
    iotv: { code:"SAT-P452", name:"IOTV PLATE CARRIER, MULTICAM", price:"$265.00",
      desc:"Improved Outer Tactical Vest, MultiCam shell, ESAPI plate cut. GWOT-era rebuild intake, soft armor and side plates not included.",
      stats:[["Pattern","MultiCam"],["Plate Cut","ESAPI / SAPI"],["Issue Era","OIF / OEF"],["Condition","Field Return"]] }
  };
  recordsRd = {
    x000: { code:"SAT-X000", name:"UNCATALOGED FIELD ITEM", price:"WITHHELD",
      desc:"Item pulled from general inventory pending review. Physical description withheld pending clearance. Cross-reference request against the master manifest returned more than one intake record for this serial number.",
      stats:[["INTAKE DATE","1971 // CONFLICT: 1994"],["ORIGIN RECORD","PROJECT DORIAN (UNCONFIRMED)"],["LAST KNOWN COORDINATES","46.2\u00b0N, 118.7\u00b0W \u2014 SEALED"],["ROUTING LOG","CDGB-4 (REDACTED)"],["HANDLING NOTE","DO NOT CROSS-REFERENCE WITH JINGLE KEY"],["STATUS","ACCESS RESTRICTED"]] }
  };

  vol(v){ return this.state.muted ? 0 : v * (this.props.jingleVolume ?? 1); }
  toggleMute(){
    const next = !this.state.muted;
    try{ localStorage.setItem('sat_muted', next?'1':'0'); }catch(e){}
    if(next){
      this.stopPromSpeech();
      try{ if(this._jingleEl){ this._jingleEl.pause(); this._jingleEl.currentTime = 0; } }catch(e){}
      try{ if(this._ambientGain) this._ambientGain.gain.value = 0; }catch(e){}
    }
    this.setState({muted:next}, ()=>{ if(!next) this.siteTone('c',0.045); });
  }
  restoreMute(){
    try{ if(localStorage.getItem('sat_muted')==='1') this.setState({muted:true}); }catch(e){}
  }
  jingleNote(freq){
    // Snaps any tone to the SAT jingle scale (C4, D4, G4, B4), any octave.
    const base=[261.63,293.66,392.00,493.88];
    let best=freq, bestDiff=Infinity;
    for(let oct=-3;oct<=3;oct++) for(const n of base){
      const cand=n*Math.pow(2,oct), diff=Math.abs(Math.log2(cand/freq));
      if(diff<bestDiff){ bestDiff=diff; best=cand; }
    }
    return best;
  }
  beep(freq=880, dur=0.05, type='square', vol=0.07){
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const osc=this._ctx.createOscillator(), gain=this._ctx.createGain();
      osc.type=type; osc.frequency.value=this.jingleNote(freq); gain.gain.value=this.vol(vol);
      osc.connect(gain); gain.connect(this._ctx.destination);
      osc.start(); osc.stop(this._ctx.currentTime+dur);
    }catch(e){}
  }
  thunk(freq=140, dur=0.12, vol=0.12){
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const osc=this._ctx.createOscillator(), gain=this._ctx.createGain();
      osc.type='sine'; osc.frequency.value=freq;
      gain.gain.setValueAtTime(this.vol(vol), this._ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, this._ctx.currentTime+dur);
      osc.connect(gain); gain.connect(this._ctx.destination);
      osc.start(); osc.stop(this._ctx.currentTime+dur);
    }catch(e){}
  }
  sleep(ms){ return new Promise(r=>setTimeout(r,ms)); }

  // ---- Custom jingle bed: one MP3 holding 4 segments (1960s, 2000s, 1980s, 1990s) ----
  _jingleSrc = (window.SAT_DATA && window.SAT_DATA.media && window.SAT_DATA.media.jingle) || '';
  _jingleOrder = { s60:0, s70:0, s2000:1, s80:2, s90:3 };
  // Decode the bed once and find where each of the 4 segments actually starts and stops,
  // so boots skip the leading/trailing dead air instead of holding the logo through silence.
  analyzeJingle(){
    if(this._jingleScan) return this._jingleScan;
    this._jingleScan = (async ()=>{
      try{
        if(!this._jingleSrc) return null;
        const res = await fetch(this._jingleSrc);
        const arr = await res.arrayBuffer();
        const ctx = new (window.AudioContext||window.webkitAudioContext)();
        const buf = await ctx.decodeAudioData(arr);
        const d = buf.getChannelData(0), sr = buf.sampleRate, seg = buf.duration/4;
        const TH = 0.012, marks = [];
        for(let k=0;k<4;k++){
          const s0 = Math.floor(k*seg*sr), s1 = Math.min(d.length, Math.floor((k+1)*seg*sr));
          let on = s0, off = s1-1;
          for(let i=s0;i<s1;i++){ if(Math.abs(d[i])>TH){ on=i; break; } }
          for(let i=s1-1;i>s0;i--){ if(Math.abs(d[i])>TH){ off=i; break; } }
          marks.push({start: on/sr, end: Math.min(off/sr + 0.12, s1/sr)});
        }
        this._jingleMarks = marks;
        return marks;
      }catch(e){ return null; }
    })();
    return this._jingleScan;
  }
  segmentFor(key){
    const idx = this._jingleOrder[key];
    const a = this._jingleEl;
    const dur = (a && a.duration) || 40;
    const seg = dur/4;
    const m = this._jingleMarks && this._jingleMarks[idx];
    return m ? {start:m.start, end:m.end} : {start: idx*seg + 0.05, end:(idx+1)*seg};
  }
  // resolves with the wall-clock length of this key's trimmed segment
  async jingleLength(key){
    await Promise.race([this.analyzeJingle(), this.sleep(2500)]);
    const {start, end} = this.segmentFor(key);
    const rate = (this._jingleEl && this._jingleEl.playbackRate) || 1;
    return Math.max(600, (end-start)*1000/rate - 80);
  }
  // fade the jingle out over ~420ms — used the moment the boot text starts
  fadeJingle(){
    const a = this._jingleEl;
    if(!a || a.paused) return;
    clearInterval(this._jingleFade);
    const start = a.volume, steps = 14;
    let i = 0;
    this._jingleFade = setInterval(()=>{
      i++;
      try{ a.volume = Math.max(0, start * (1 - i/steps)); }catch(e){}
      if(i >= steps){ clearInterval(this._jingleFade); try{ a.pause(); a.currentTime = 0; a.volume = start; }catch(e){} }
    }, 30);
  }
  playJingle(key, holdMs){
    const idx = this._jingleOrder[key];
    if(idx === undefined || this.state.muted) return;
    try{
      if(this._jingleEl){ this._jingleEl.pause(); }
      const a = this._jingleEl || new Audio();
      this._jingleEl = a;
      if(!this._jingleLoaded){ a.src = this._jingleSrc; this._jingleLoaded = true; }
      a.volume = this.state.muted ? 0 : 0.22;
      a.loop = false;
      a.playbackRate = 1;
      const start = () => {
        const {start: s0, end: s1} = this.segmentFor(key);
        a.currentTime = s0;
        a.play().catch(()=>{});
        clearTimeout(this._jingleStop);
        this._jingleStop = setTimeout(()=>{
          try{
            const t0 = a.volume;
            let v = t0;
            const fade = setInterval(()=>{ v -= t0/12; a.volume = Math.max(0,v); if(v<=0){ clearInterval(fade); a.pause(); a.volume=t0; } }, 45);
          }catch(e){}
        }, holdMs || ((s1-s0)*1000/(a.playbackRate||1)));
      };
      if(a.readyState >= 1) start();
      else { a.addEventListener('loadedmetadata', start, {once:true}); a.load(); }
    }catch(e){}
  }
  stopJingle(){
    clearTimeout(this._jingleStop);
    if(this._jingleEl){ try{ this._jingleEl.pause(); }catch(e){} }
  }
  toggleAmbient(){
    if(this.state.ambientOn){ this.stopAmbient(); this.setState({ambientOn:false}); }
    else { this.startAmbient(); this.setState({ambientOn:true}); }
  }
  // ---- Lounge jazz combo: brushed kit, walking bass, rhodes comping, muted-trumpet head ----
  startAmbient(){
    if(this._ambientRunning) return;
    this._ambientRunning = true;
    const AC = window.AudioContext||window.webkitAudioContext;
    this._ctx = this._ctx || new AC();
    const ctx = this._ctx;
    if(ctx.state==='suspended') ctx.resume();

    const master = ctx.createGain();
    master.gain.value = this.vol(0.5);
    const verb = ctx.createConvolver();
    const rlen = Math.floor(ctx.sampleRate*1.5);
    const ir = ctx.createBuffer(2, rlen, ctx.sampleRate);
    for(let c=0;c<2;c++){ const d=ir.getChannelData(c);
      for(let i=0;i<rlen;i++) d[i]=(Math.random()*2-1)*Math.pow(1-i/rlen,2.6); }
    verb.buffer = ir;
    const wet = ctx.createGain(); wet.gain.value = 0.24;
    master.connect(ctx.destination);
    master.connect(wet); wet.connect(verb); verb.connect(ctx.destination);
    this._jazzMaster = master;

    // noise buffer for brushes
    const nlen = Math.floor(ctx.sampleRate*0.5);
    const nb = ctx.createBuffer(1,nlen,ctx.sampleRate);
    const nd = nb.getChannelData(0);
    for(let i=0;i<nlen;i++) nd[i]=Math.random()*2-1;

    const N = n=>440*Math.pow(2,(n-69)/12);
    // Bb6/9 – Gm7 – C7 – F6  (turnaround, F major)
    const bars = [
      {bass:[46,48,50,53], ch:[65,69,72,75], mel:[[72,0,1.1],[74,1.5,0.5],[77,2,1.2]]},
      {bass:[43,45,47,50], ch:[62,65,69,72], mel:[[74,0,0.9],[72,1.5,0.6],[69,2.5,1.0]]},
      {bass:[48,50,52,53], ch:[64,67,70,74], mel:[[76,0,0.8],[74,1,0.5],[71,2,1.2]]},
      {bass:[41,43,45,48], ch:[60,64,69,72], mel:[[69,0,1.4],[65,2,1.6]]}
    ];

    const bassVoice=(f,t,dur)=>{
      const o=ctx.createOscillator(), g=ctx.createGain(), lp=ctx.createBiquadFilter();
      o.type='triangle'; o.frequency.setValueAtTime(f*0.985,t); o.frequency.linearRampToValueAtTime(f,t+0.05);
      lp.type='lowpass'; lp.frequency.value=320;
      g.gain.setValueAtTime(0,t);
      g.gain.linearRampToValueAtTime(0.5,t+0.02);
      g.gain.exponentialRampToValueAtTime(0.0001,t+dur);
      o.connect(lp); lp.connect(g); g.connect(master);
      o.start(t); o.stop(t+dur+0.05);
    };
    const rhodes=(notes,t,dur)=>{
      notes.forEach((n,i)=>{
        const f=N(n);
        [1,2.01].forEach((mult,k)=>{
          const o=ctx.createOscillator(), g=ctx.createGain();
          o.type='sine'; o.frequency.value=f*mult;
          const amp = (k?0.05:0.13)/notes.length;
          g.gain.setValueAtTime(0,t);
          g.gain.linearRampToValueAtTime(amp,t+0.03+i*0.006);
          g.gain.exponentialRampToValueAtTime(0.0001,t+dur);
          o.connect(g); g.connect(master);
          o.start(t); o.stop(t+dur+0.05);
        });
      });
    };
    const trumpet=(n,t,dur)=>{
      const o=ctx.createOscillator(), g=ctx.createGain(), bp=ctx.createBiquadFilter(), lfo=ctx.createOscillator(), lg=ctx.createGain();
      o.type='sawtooth'; o.frequency.setValueAtTime(N(n)*0.99,t); o.frequency.linearRampToValueAtTime(N(n),t+0.08);
      bp.type='bandpass'; bp.frequency.value=N(n)*2.2; bp.Q.value=3.2;
      lfo.frequency.value=5.2; lg.gain.value=N(n)*0.008;
      lfo.connect(lg); lg.connect(o.frequency); lfo.start(t); lfo.stop(t+dur);
      g.gain.setValueAtTime(0,t);
      g.gain.linearRampToValueAtTime(0.085,t+0.09);
      g.gain.setValueAtTime(0.085,t+dur*0.7);
      g.gain.exponentialRampToValueAtTime(0.0001,t+dur);
      o.connect(bp); bp.connect(g); g.connect(master);
      o.start(t); o.stop(t+dur+0.05);
    };
    const brush=(t,amp,freq,dur)=>{
      const s=ctx.createBufferSource(), g=ctx.createGain(), hp=ctx.createBiquadFilter();
      s.buffer=nb; hp.type='highpass'; hp.frequency.value=freq;
      g.gain.setValueAtTime(amp,t);
      g.gain.exponentialRampToValueAtTime(0.0001,t+dur);
      s.connect(hp); hp.connect(g); g.connect(master);
      s.start(t); s.stop(t+dur+0.02);
    };
    const kick=(t)=>{
      const o=ctx.createOscillator(), g=ctx.createGain();
      o.type='sine'; o.frequency.setValueAtTime(90,t); o.frequency.exponentialRampToValueAtTime(42,t+0.14);
      g.gain.setValueAtTime(0.28,t); g.gain.exponentialRampToValueAtTime(0.0001,t+0.2);
      o.connect(g); g.connect(master); o.start(t); o.stop(t+0.25);
    };

    const bpm = 96, beat = 60/bpm, swing = beat*0.17;
    let bar = 0;
    this._jazzNext = ctx.currentTime + 0.15;

    const scheduleBar = (t0, b) => {
      const B = bars[b % bars.length];
      for(let i=0;i<4;i++){
        const t = t0 + i*beat;
        bassVoice(N(B.bass[i]), t, beat*0.92);
        // brushed ride: quarter + swung eighth
        brush(t, 0.05, 6200, 0.12);
        brush(t + beat/2 + swing, 0.032, 7200, 0.09);
        if(i===1 || i===3) brush(t, 0.075, 3600, 0.2);   // snare brush on 2 & 4
        if(i===0) kick(t);
      }
      // comping: off-beat stabs on 2& and 4&
      rhodes(B.ch, t0 + beat*1.5 + swing, beat*0.8);
      rhodes(B.ch, t0 + beat*3.5 + swing, beat*0.7);
      if(b % 2 === 0) rhodes(B.ch, t0 + beat*0.5 + swing, beat*0.5);
      // head every other 4-bar cycle so it breathes
      if(Math.floor(b/4) % 2 === 0) B.mel.forEach(([n,off,dur])=>trumpet(n, t0 + off*beat, dur*beat));
    };

    const loop = () => {
      if(!this._ambientRunning) return;
      while(this._jazzNext < ctx.currentTime + 1.2){
        scheduleBar(this._jazzNext, bar);
        this._jazzNext += beat*4;
        bar++;
      }
      this._ambientTimer = setTimeout(loop, 220);
    };
    loop();
  }
  stopAmbient(){
    this._ambientRunning=false;
    if(this._ambientTimer) clearTimeout(this._ambientTimer);
    if(this._jazzMaster && this._ctx){
      try{
        const t=this._ctx.currentTime;
        this._jazzMaster.gain.cancelScheduledValues(t);
        this._jazzMaster.gain.setValueAtTime(this._jazzMaster.gain.value,t);
        this._jazzMaster.gain.linearRampToValueAtTime(0.0001,t+0.5);
      }catch(e){}
    }
  }
  playRollTick(){
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      if(this._ctx.state==='suspended') this._ctx.resume();
      const t=this._ctx.currentTime;
      // filtered noise burst -> reads as a soft mechanical click, not a tone
      const bufLen = Math.floor(this._ctx.sampleRate*0.02);
      const buf = this._ctx.createBuffer(1, bufLen, this._ctx.sampleRate);
      const data = buf.getChannelData(0);
      for(let i=0;i<bufLen;i++) data[i]=(Math.random()*2-1)*(1-i/bufLen);
      const src = this._ctx.createBufferSource(); src.buffer=buf;
      const filt = this._ctx.createBiquadFilter(); filt.type='bandpass'; filt.frequency.value=2200; filt.Q.value=1.1;
      const gain = this._ctx.createGain();
      gain.gain.setValueAtTime(this.vol(0.05),t);
      gain.gain.exponentialRampToValueAtTime(0.0001,t+0.03);
      src.connect(filt); filt.connect(gain); gain.connect(this._ctx.destination);
      src.start(t); src.stop(t+0.03);
    }catch(e){}
  }
  async playHardwarePowerOn(){
    this.thunk(35,0.15,0.13); // power button click
    await this.sleep(90);
    this.beep(85,0.4,'sawtooth',0.025); // PSU/transformer whine spin-up
  }
  async playPowerOn(){
    await this.playHardwarePowerOn();
    this.thunk(50,0.4,0.2); // degauss thump
    await this.sleep(300);
    this.beep(1000,0.14,'square',0.05); // single POST beep
  }
  startProjectorHum(){
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const osc=this._ctx.createOscillator(), gain=this._ctx.createGain();
      osc.type='sine'; osc.frequency.value=96;
      gain.gain.value=this.vol(0.0065);
      osc.connect(gain); gain.connect(this._ctx.destination);
      osc.start();
      this._humOsc=osc;
      clearInterval(this._slideShiftTimer);
      this._slideShiftTimer = setInterval(()=>{
        if(this.state.view!=='view70' && this.state.view!=='list70'){ return; }
        this.thunk(58+Math.random()*22, 0.06, 0.07);
      }, 4200);
    }catch(e){}
  }
  // ---- Main-site palette: soft FM marimba/bell, octave above the era beeps ----
  siteTone(note, level){
    const map = {c:523.25, d:587.33, g:783.99, b:987.77, chi:1046.5};
    const f = map[note] || 523.25;
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const ctx=this._ctx, t=ctx.currentTime;
      const car=ctx.createOscillator(), mod=ctx.createOscillator();
      const mg=ctx.createGain(), g=ctx.createGain(), lp=ctx.createBiquadFilter();
      car.type='sine'; car.frequency.value=f;
      mod.type='sine'; mod.frequency.value=f*3.01;
      mg.gain.setValueAtTime(f*0.9,t); mg.gain.exponentialRampToValueAtTime(0.5,t+0.1);
      lp.type='lowpass'; lp.frequency.setValueAtTime(5200,t); lp.frequency.exponentialRampToValueAtTime(1300,t+0.5);
      const lvl=this.vol(level||0.05);
      g.gain.setValueAtTime(0.0001,t);
      g.gain.exponentialRampToValueAtTime(lvl,t+0.014);   // felt mallet, not a click
      g.gain.exponentialRampToValueAtTime(lvl*0.34,t+0.16);
      g.gain.exponentialRampToValueAtTime(0.0001,t+0.78); // long woody bloom
      mod.connect(mg); mg.connect(car.frequency);
      car.connect(lp); lp.connect(g); g.connect(ctx.destination);
      mod.start(t); car.start(t); mod.stop(t+0.8); car.stop(t+0.8);

      // sub-octave body — gives the tap weight instead of bluntness
      const sub=ctx.createOscillator(), sg=ctx.createGain();
      sub.type='triangle'; sub.frequency.value=f/2;
      sg.gain.setValueAtTime(0.0001,t);
      sg.gain.exponentialRampToValueAtTime(lvl*0.42,t+0.02);
      sg.gain.exponentialRampToValueAtTime(0.0001,t+0.5);
      sub.connect(sg); sg.connect(ctx.destination);
      sub.start(t); sub.stop(t+0.52);

      // airy shimmer partial, quiet
      const air=ctx.createOscillator(), ag=ctx.createGain();
      air.type='sine'; air.frequency.value=f*4.02;
      ag.gain.setValueAtTime(0.0001,t);
      ag.gain.exponentialRampToValueAtTime(lvl*0.13,t+0.01);
      ag.gain.exponentialRampToValueAtTime(0.0001,t+0.3);
      air.connect(ag); ag.connect(ctx.destination);
      air.start(t); air.stop(t+0.32);
    }catch(e){}
  }
  siteClick(){ this.siteTone('g',0.035); }
  // tactile era transitions: relay clunk + servo sweep + latch
  eraEnter(){
    this.thunk(120,0.09,0.16);
    setTimeout(()=>this.thunk(72,0.12,0.13), 70);
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const ctx=this._ctx, t=ctx.currentTime+0.1;
      const o=ctx.createOscillator(), g=ctx.createGain(), f=ctx.createBiquadFilter();
      o.type='sawtooth'; o.frequency.setValueAtTime(70,t); o.frequency.exponentialRampToValueAtTime(320,t+0.26);
      f.type='lowpass'; f.frequency.setValueAtTime(400,t); f.frequency.exponentialRampToValueAtTime(2600,t+0.26);
      g.gain.setValueAtTime(0.0001,t); g.gain.exponentialRampToValueAtTime(this.vol(0.055),t+0.05);
      g.gain.exponentialRampToValueAtTime(0.0001,t+0.32);
      o.connect(f); f.connect(g); g.connect(ctx.destination);
      o.start(t); o.stop(t+0.34);
    }catch(e){}
    setTimeout(()=>this.thunk(200,0.06,0.1), 330);
  }
  eraExit(){
    this.stopAllEraAudio();
    this.thunk(150,0.07,0.12);
    setTimeout(()=>{
      try{
        this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
        const ctx=this._ctx, t=ctx.currentTime;
        const o=ctx.createOscillator(), g=ctx.createGain(), f=ctx.createBiquadFilter();
        o.type='sawtooth'; o.frequency.setValueAtTime(300,t); o.frequency.exponentialRampToValueAtTime(64,t+0.3);
        f.type='lowpass'; f.frequency.setValueAtTime(2400,t); f.frequency.exponentialRampToValueAtTime(320,t+0.3);
        g.gain.setValueAtTime(0.0001,t); g.gain.exponentialRampToValueAtTime(this.vol(0.05),t+0.04);
        g.gain.exponentialRampToValueAtTime(0.0001,t+0.36);
        o.connect(f); f.connect(g); g.connect(ctx.destination);
        o.start(t); o.stop(t+0.38);
      }catch(e){}
    }, 60);
    setTimeout(()=>this.thunk(90,0.1,0.12), 400);
  }
  siteConfirm(){ this.siteTone('c',0.05); setTimeout(()=>this.siteTone('g',0.045),90); setTimeout(()=>this.siteTone('chi',0.04),185); }
  // ---- 2000s: instant messenger ----
  _imScript = [
    { bot: "hey. you're on the plate carrier listing.", replies: ["Is it still available?", "Just browsing.", "Who is this?"] },
    { bot: "everything on this board is available until it isn't. cash or money order. no trades, no paypal.", replies: ["What condition are the plates?", "Where did this come from?"] },
    { bot: "plates aren't included. and nobody asks where anything came from — that's the whole arrangement.", replies: ["Can I see a picture?"] },
    { bot: "sending one now. don't save it to a shared machine.", replies: [], transfer: {name:'lot4471_ramp.jpg', size:'214 KB'} },
    { bot: "that's the pallet as it sat in Kandahar. the tail number is scratched out because somebody scratched it out, not because i did.", replies: ["I'll take it.", "I need to think about it."] },
    { bot: "i'll mark it held for 48 hours. don't post about this in the thread.", replies: [] }
  ];
  _imBuddies = [
    { name:'SAT_Command',  status:'online', note:'Available' },
    { name:'ramp_rat_88',  status:'idle',   note:'Idle 41 min' },
    { name:'C14_dispatch', status:'away',   note:'Away — on the ramp' },
    { name:'kilo_six',     status:'off',    note:'Offline since 08/14' }
  ];
  // ---- 70s wall telephone (dormant: set the phoneRing prop true to arm it) ----
  _phoneMessage = [
    'Operator: connecting Desk 9.',
    '(line hiss — someone is holding the receiver but not speaking)',
    'Voice: you found the carousel. good. the fourth slide was pulled in 1978 and it has not been logged since.',
    'Voice: if the board shows a flight that is not on the manifest, do not sign for it.',
    '(the line goes dead)'
  ];
  ringOnce(){
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const ctx=this._ctx, t=ctx.currentTime;
      // twin bell: two clappers striking a metal gong, ~20 Hz warble for 1.8s
      for(let i=0;i<36;i++){
        const at = t + i*0.05;
        [1180, 1620].forEach((f,k)=>{
          const o=ctx.createOscillator(), g=ctx.createGain(), bp=ctx.createBiquadFilter();
          o.type='triangle'; o.frequency.setValueAtTime(f*(1+(k?0.004:0)),at);
          bp.type='bandpass'; bp.frequency.value=f; bp.Q.value=9;
          g.gain.setValueAtTime(this.vol(0.05),at);
          g.gain.exponentialRampToValueAtTime(0.0001,at+0.055);
          o.connect(bp); bp.connect(g); g.connect(ctx.destination);
          o.start(at); o.stop(at+0.06);
        });
      }
    }catch(e){}
  }
  startPhoneRing(){
    if(this.state.phoneRinging) return;
    this.setState({phoneRinging:true});
    const cycle = ()=>{
      if(!this.state.phoneRinging) return;
      this.ringOnce();
      this._ringT = setTimeout(cycle, 4200);
    };
    cycle();
  }
  stopPhoneRing(){ clearTimeout(this._ringT); this.setState({phoneRinging:false}); }
  answerPhone(){
    if(!this.state.phoneRinging) return;
    this.stopPhoneRing();
    this.thunk(180,0.06,0.06);
    this.setState({phoneAnswered:true, phoneLine:0});
    const step = ()=>{
      this.setState(s=>{
        const next = s.phoneLine + 1;
        if(next >= this._phoneMessage.length) return null;
        return {phoneLine:next};
      });
      if(this.state.phoneLine < this._phoneMessage.length-1) this._phoneT = setTimeout(step, 3200);
    };
    this._phoneT = setTimeout(step, 3200);
  }
  hangUpPhone(){ clearTimeout(this._phoneT); this.thunk(150,0.05,0.05); this.setState({phoneAnswered:false}); }
  imBuddyChime(){ this.beep(1320,0.07,'sine',0.05); setTimeout(()=>this.beep(1760,0.09,'sine',0.045),90); }
  imDing(){ this.beep(880,0.05,'sine',0.04); setTimeout(()=>this.beep(1240,0.06,'sine',0.035),70); }
  imDoorOpen(){ this.beep(660,0.09,'sine',0.045); setTimeout(()=>this.beep(990,0.11,'sine',0.04),110); }
  scheduleIM(){
    clearTimeout(this._imTimer);
    if(this.props.skipBootSequences) return;
    this._imTimer = setTimeout(()=>{
      if(this.state.view!=='view00') return;
      this.imDoorOpen();
      this.setState({im00Buddy:true});
      setTimeout(()=>{
        if(this.state.view!=='view00') return;
        this.imBuddyChime();
        this.setState({im00Open:true, im00Step:0, im00Log:[{who:'bot', text:this._imScript[0].bot, t:this.imStamp()}]});
      }, 2600);
    }, 4200);
  }
  imStamp(){
    const d=new Date();
    return String(d.getHours()).padStart(2,'0')+':'+String(d.getMinutes()).padStart(2,'0')+':'+String(d.getSeconds()).padStart(2,'0');
  }
  imAdvance(step){
    const next = this._imScript[step];
    if(!next){ this.setState({im00Typing:false}); return; }
    this.imDing();
    this.setState(s=>({
      im00Typing:false, im00Step:step,
      im00Log:[...s.im00Log, {who:'bot', text:next.bot, t:this.imStamp()}],
      im00Xfer: next.transfer ? {...next.transfer, state:'offered', pct:0} : s.im00Xfer
    }));
  }
  imReply(text){
    const step = this.state.im00Step;
    this.imDing();
    this.setState(s=>({im00Log:[...s.im00Log, {who:'me', text, t:this.imStamp()}], im00Typing:true}));
    clearTimeout(this._imReplyTimer);
    this._imReplyTimer = setTimeout(()=>this.imAdvance(step+1), 1400 + text.length*22);
  }
  imAcceptXfer(){
    this.imDing();
    this.setState(s=>({im00Xfer:{...s.im00Xfer, state:'receiving', pct:0}}));
    clearInterval(this._imXferT);
    this._imXferT = setInterval(()=>{
      this.setState(s=>{
        if(!s.im00Xfer || s.im00Xfer.state!=='receiving') return null;
        const pct = Math.min(100, s.im00Xfer.pct + 7 + Math.random()*11);
        if(pct>=100){
          clearInterval(this._imXferT);
          setTimeout(()=>{ this.imDing(); this.imAdvance(this.state.im00Step+1); }, 700);
          return {im00Xfer:{...s.im00Xfer, pct:100, state:'done'}};
        }
        return {im00Xfer:{...s.im00Xfer, pct}};
      });
    }, 260);
  }
  imDeclineXfer(){
    this.thunk(150,0.05,0.05);
    this.setState(s=>({im00Xfer:{...s.im00Xfer, state:'declined'}}));
    setTimeout(()=>this.imAdvance(this.state.im00Step+1), 900);
  }
  toggleBuddyList(){ this.beep(720,0.05,'sine',0.035); this.setState(s=>({im00Buddy:!s.im00Buddy})); }
  closeIM(){ clearTimeout(this._imTimer); clearTimeout(this._imReplyTimer); clearInterval(this._imXferT); this.thunk(140,0.05,0.05); this.setState({im00Open:false}); }
  // a wooden crate set down and shoved into place: hollow body thud, board rattle, drag scrape
  crateLoad(){
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const ctx=this._ctx, t=ctx.currentTime;
      const woodHit=(at,freq,gain,dur)=>{
        const o=ctx.createOscillator(), g=ctx.createGain(), lp=ctx.createBiquadFilter();
        o.type='triangle'; lp.type='lowpass'; lp.frequency.value=1400;
        o.frequency.setValueAtTime(freq,at); o.frequency.exponentialRampToValueAtTime(freq*0.42,at+dur);
        g.gain.setValueAtTime(this.vol(gain),at); g.gain.exponentialRampToValueAtTime(0.0001,at+dur);
        o.connect(lp); lp.connect(g); g.connect(ctx.destination); o.start(at); o.stop(at+dur+0.02);
        // board crack transient
        const nb=ctx.createBuffer(1,Math.floor(ctx.sampleRate*0.05),ctx.sampleRate), d=nb.getChannelData(0);
        for(let i=0;i<d.length;i++) d[i]=(Math.random()*2-1)*Math.pow(1-i/d.length,7);
        const ns=ctx.createBufferSource(); ns.buffer=nb;
        const bp=ctx.createBiquadFilter(); bp.type='bandpass'; bp.frequency.value=freq*6; bp.Q.value=1.1;
        const ng=ctx.createGain(); ng.gain.value=this.vol(gain*0.55);
        ns.connect(bp); bp.connect(ng); ng.connect(ctx.destination); ns.start(at);
      };
      woodHit(t, 168, 0.22, 0.20);          // crate lands
      woodHit(t+0.085, 232, 0.10, 0.13);    // boards settle
      // dragged across the deck
      const dur=0.34, sb=ctx.createBuffer(1,Math.floor(ctx.sampleRate*dur),ctx.sampleRate), sd=sb.getChannelData(0);
      for(let i=0;i<sd.length;i++){ const p=i/sd.length; sd[i]=(Math.random()*2-1)*Math.sin(p*Math.PI)*(0.6+0.4*Math.sin(p*70)); }
      const ss=ctx.createBufferSource(); ss.buffer=sb;
      const sf=ctx.createBiquadFilter(); sf.type='bandpass'; sf.frequency.setValueAtTime(900,t+0.14); sf.frequency.linearRampToValueAtTime(430,t+0.14+dur); sf.Q.value=0.8;
      const sg=ctx.createGain(); sg.gain.value=this.vol(0.075);
      ss.connect(sf); sf.connect(sg); sg.connect(ctx.destination); ss.start(t+0.14);
      woodHit(t+0.46, 140, 0.13, 0.22);     // shoved home
    }catch(e){}
  }
  // one short signature sound per era emblem, throttled so a sweep across the row doesn't stack
  eraHoverSound(k){
    if(!this._eraHoverPlayed) this._eraHoverPlayed = {};
    if(this._eraHoverPlayed[k]) return;
    const now = Date.now();
    if(this._eraHoverAt && now - this._eraHoverAt < 260) return;
    this._eraHoverAt = now;
    this._eraHoverPlayed[k] = true;
    switch(k){
      case '70': this.thunk(64,0.1,0.09); setTimeout(()=>this.thunk(78,0.06,0.06),95); break;      // projector switch + fan catch
      case '80': this.beep(1000,0.09,'square',0.045); break;                                       // POST beep
      case '90': this.beep(1760,0.05,'sine',0.03); setTimeout(()=>this.beep(1180,0.07,'sawtooth',0.028),70); break; // handshake chirp
      case '00': this.beep(340,0.045,'sawtooth',0.035); setTimeout(()=>this.beep(430,0.06,'sawtooth',0.03),90); break; // dial-tone blip
      case '10': this.siteTone('chi',0.05); break;                                                 // soft flat-UI tick
      case 'rd': this.beep(620,0.035,'square',0.03); setTimeout(()=>this.beep(620,0.035,'square',0.03),120); setTimeout(()=>this.beep(620,0.09,'square',0.03),240); break; // morse
    }
  }
  // hover state for the era timeline — each station's machine "powers on"
  eraTimelineVals(){
    const h = this.state.hoverEra;
    const fx = (k,c)=>({
      lift: h===k ? 'translateY(-9px)' : 'translateY(0)',
      glow: h===k ? '0 0 30px '+c+'55, 0 14px 30px rgba(0,0,0,.5)' : '0 6px 16px rgba(0,0,0,.35)',
      scr: h===k ? c : 'rgba(226,236,231,.10)',
      screenGlow: h===k ? 'inset 0 0 18px '+c+'aa, 0 0 16px '+c+'55' : 'none',
      dim: h===k ? 'none' : 'saturate(.35) brightness(.72)',
      tick: h===k ? c : 'rgba(240,199,122,.45)',
      tickSize: h===k ? '15px' : '9px',
      label: h===k ? c : '#F4EFE3',
      // rack-unit chrome
      bay: h===k ? 'rgba(240,199,122,.07)' : 'transparent',
      lamp: h===k ? c : 'rgba(240,199,122,.22)',
      lampGlow: h===k ? '0 0 9px '+c : 'inset 0 1px 1px rgba(0,0,0,.6)',
      stateText: h===k ? 'POWERED' : 'STANDBY',
      stateColor: h===k ? c : '#5f676e',
      plateBg: h===k ? c : 'rgba(240,199,122,.08)',
      plateInk: h===k ? '#12100A' : 'rgba(240,199,122,.62)',
      plateText: h===k ? 'BOOT THIS TERMINAL' : 'OFFLINE',
      enter: ()=>{ this.setState({hoverEra:k}); this.eraHoverSound(k); },
      leave: ()=>this.setState({hoverEra:null})
    });
    return {
      e70: fx('70','#D9A441'), e80: fx('80','#8FE39C'), e90: fx('90','#39FF8F'),
      e00: fx('00','#6FB6F5'), erd: fx('rd','#C9A227')
    };
  }
  setProjRolled(rolled){
    clearTimeout(this._lampTimer);
    this.playSlideChange();
    if(rolled){
      this.setState({projRolled:true});
      this._lampTimer = setTimeout(()=>{
        this.lampClick();
        this.stopProjectorHum();
        this.setState({lampOff:true});
      }, 2000);
    } else {
      this.setState({projRolled:false});
      this._lampTimer = setTimeout(()=>{
        this.thickClick();
        this.startProjectorHum();
        this.setState({lampOff:false});
      }, 1000);
    }
  }
  // ---- Classified: intermittent radio chatter + morse traffic ----
  startClassifiedRadio(){
    clearInterval(this._radioTimer);
    this._radioTimer = setInterval(()=>{
      const v = this.state.view;
      if(v!=='viewrd' && v!=='listrd' && v!=='bootrd') return;
      const r = Math.random();
      if(r < 0.5) this.playChatterSlice();
      else if(r < 0.78) this.radioBurst();
      else this.morseWord();
    }, 8000);
  }
  stopClassifiedRadio(){ clearInterval(this._radioTimer); this.stopChatterSlice(); }
  // Placeholder chatter bed for the clearance gate — swap _gateChatterSrc for a real file when ready.
  _gateChatterSrc = (window.SAT_DATA && window.SAT_DATA.media && window.SAT_DATA.media.chatter) || '';
  // pull a short random slice out of the chatter bed each time, so it never repeats the same way
  playChatterSlice(){
    try{
      const a = this._chatterEl || new Audio(this._gateChatterSrc);
      this._chatterEl = a;
      const go = ()=>{
        const dur = a.duration || 60;
        const len = 3 + Math.random()*5;
        a.currentTime = Math.random() * Math.max(0.1, dur - len);
        a.volume = this.state.muted ? 0 : 0.3;
        if(this.state.muted) return;
        a.play().catch(()=>{});
        clearTimeout(this._chatterStop);
        this._chatterStop = setTimeout(()=>{
          const v0 = a.volume; let v = v0;
          const f = setInterval(()=>{ v -= v0/10; a.volume = Math.max(0,v); if(v<=0){ clearInterval(f); a.pause(); a.volume = v0; } }, 40);
        }, len*1000);
      };
      if(a.readyState >= 1) go(); else { a.addEventListener('loadedmetadata', go, {once:true}); a.load(); }
    }catch(e){}
  }
  stopChatterSlice(){
    clearTimeout(this._chatterStop);
    if(this._chatterEl){ try{ this._chatterEl.pause(); }catch(e){} }
  }
  openCaseArticle(i){ this.siteTone('g',0.05); this.setState({page:'casefile', caseArticleIndex:i}); window.scrollTo(0,0); }
  closeCaseArticle(){ this.siteTone('c',0.05); this.setState({page:'casefiles'}); window.scrollTo(0,0); }
  stepCaseArticle(dir){
    const n = this._caseFiles.length;
    this.siteTone('b',0.045);
    this.setState(s=>({caseArticleIndex: (((s.caseArticleIndex||0)+dir)%n+n)%n}));
    window.scrollTo(0,0);
  }
  startGateChatter(){
    this.stopGateChatter();
    this._gateTimer = setInterval(()=>{
      if(!this.state.clearanceOpen && this.state.view!=='codegate'){ return; }
      // pre-auth: equipment only — squelch clicks and relay thumps, no voice or morse
      if(Math.random() < 0.5) this.thunk(90,0.06,0.05); else this.beep(196,0.03,'square',0.03);
    }, 6000);
  }
  stopGateChatter(){
    clearInterval(this._gateTimer);
    this.stopChatterSlice();
    if(this._gateEl){ try{ this._gateEl.pause(); }catch(e){} }
  }
  radioBurst(){
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const ctx=this._ctx, t=ctx.currentTime, dur=0.55+Math.random()*0.7;
      const buf=ctx.createBuffer(1, Math.floor(ctx.sampleRate*dur), ctx.sampleRate);
      const d=buf.getChannelData(0);
      for(let i=0;i<d.length;i++){
        const env = Math.sin(Math.PI*(i/d.length));
        const syl = 0.55 + 0.45*Math.sin(i/ctx.sampleRate*34);
        d[i] = (Math.random()*2-1) * env * syl;
      }
      const src=ctx.createBufferSource(); src.buffer=buf;
      const bp=ctx.createBiquadFilter(); bp.type='bandpass'; bp.frequency.value=1500; bp.Q.value=3.2;
      const hp=ctx.createBiquadFilter(); hp.type='highpass'; hp.frequency.value=520;
      const g=ctx.createGain(); g.gain.value=this.vol(0.035);
      src.connect(bp); bp.connect(hp); hp.connect(g); g.connect(ctx.destination);
      src.start(t);
    }catch(e){}
  }
  _morse = {S:'...',A:'.-',T:'-',C:'-.-.',H:'....',E:'.',D:'-..',U:'..-',L:'.-..',O:'---',N:'-.',R:'.-.',I:'..',G:'--.'};
  morseWord(word){
    const w = word || ['SAT','CARGO','HOLD','ROUTE','NINE'][Math.floor(Math.random()*5)];
    const unit = 85;
    let t = 0;
    for(const ch of w.toUpperCase()){
      const pat = this._morse[ch];
      if(!pat){ t += unit*3; continue; }
      for(const sym of pat){
        const len = sym==='-' ? unit*3 : unit;
        setTimeout(()=>this.morseTone(len/1000), t);
        t += len + unit;
      }
      t += unit*2;
    }
  }
  morseTone(sec){
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const ctx=this._ctx, t=ctx.currentTime;
      const o=ctx.createOscillator(), g=ctx.createGain();
      o.type='sine'; o.frequency.value=783.99;
      g.gain.setValueAtTime(0.0001,t);
      g.gain.exponentialRampToValueAtTime(this.vol(0.028),t+0.006);
      g.gain.setValueAtTime(this.vol(0.028),t+sec-0.006);
      g.gain.exponentialRampToValueAtTime(0.0001,t+sec);
      o.connect(g); g.connect(ctx.destination);
      o.start(t); o.stop(t+sec+0.02);
    }catch(e){}
  }
  thickClick(){
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const ctx=this._ctx, t=ctx.currentTime;
      const buf=ctx.createBuffer(1, Math.floor(ctx.sampleRate*0.09), ctx.sampleRate);
      const d=buf.getChannelData(0);
      for(let i=0;i<d.length;i++){ d[i]=(Math.random()*2-1)*Math.pow(1-i/d.length, 5); }
      const src=ctx.createBufferSource(); src.buffer=buf;
      const lp=ctx.createBiquadFilter(); lp.type='lowpass'; lp.frequency.value=1500;
      const g=ctx.createGain(); g.gain.value=this.vol(0.22);
      src.connect(lp); lp.connect(g); g.connect(ctx.destination); src.start(t);
      const o=ctx.createOscillator(), og=ctx.createGain();
      o.type='sine'; o.frequency.setValueAtTime(150,t); o.frequency.exponentialRampToValueAtTime(52,t+0.1);
      og.gain.setValueAtTime(this.vol(0.2),t); og.gain.exponentialRampToValueAtTime(0.0001,t+0.14);
      o.connect(og); og.connect(ctx.destination); o.start(t); o.stop(t+0.15);
    }catch(e){}
  }
  lampClick(){
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const ctx=this._ctx, t=ctx.currentTime;
      const buf=ctx.createBuffer(1, Math.floor(ctx.sampleRate*0.05), ctx.sampleRate);
      const d=buf.getChannelData(0);
      for(let i=0;i<d.length;i++){ d[i]=(Math.random()*2-1)*Math.pow(1-i/d.length, 9); }
      const src=ctx.createBufferSource(); src.buffer=buf;
      const bp=ctx.createBiquadFilter(); bp.type='bandpass'; bp.frequency.value=2600; bp.Q.value=1.6;
      const g=ctx.createGain(); g.gain.value=this.vol(0.26);
      src.connect(bp); bp.connect(g); g.connect(ctx.destination);
      src.start(t);
      // sharp switch snap
      const o=ctx.createOscillator(), og=ctx.createGain();
      o.type='square'; o.frequency.setValueAtTime(900,t); o.frequency.exponentialRampToValueAtTime(160,t+0.035);
      og.gain.setValueAtTime(this.vol(0.13),t); og.gain.exponentialRampToValueAtTime(0.0001,t+0.045);
      o.connect(og); og.connect(ctx.destination); o.start(t); o.stop(t+0.05);
      // heavy housing thud just behind it
      const o2=ctx.createOscillator(), og2=ctx.createGain();
      o2.type='sine'; o2.frequency.setValueAtTime(150,t+0.012); o2.frequency.exponentialRampToValueAtTime(58,t+0.12);
      og2.gain.setValueAtTime(this.vol(0.2),t+0.012); og2.gain.exponentialRampToValueAtTime(0.0001,t+0.16);
      o2.connect(og2); og2.connect(ctx.destination); o2.start(t+0.012); o2.stop(t+0.17);
    }catch(e){}
  }
  playSlideChange(){
    this.thunk(72,0.14,0.11);
    setTimeout(()=>this.thunk(54,0.1,0.09), 210);
  }
  // one kill switch for every looping/scheduled sound the era pages start
  stopAllEraAudio(){
    try{ this.stopProjectorHum(); }catch(e){}
    try{ this.stopClassifiedRadio(); }catch(e){}
    try{ this.stopGateChatter(); }catch(e){}
    try{ this.stopModem && this.stopModem(); }catch(e){}
    try{ this.stopCrtHum && this.stopCrtHum(); }catch(e){}
    ['_slideShiftTimer','_radioTimer','_gateTimer','_lampTimer','_modemTimer','_bootTimer','_chatterTimer']
      .forEach(k=>{ clearInterval(this[k]); clearTimeout(this[k]); this[k]=null; });
    ['_humOsc','_modemOsc','_crtOsc','_fanOsc'].forEach(k=>{
      try{ if(this[k]){ this[k].stop(); this[k]=null; } }catch(e){}
    });
    try{ if(this._jingleEl){ this._jingleEl.pause(); this._jingleEl.currentTime=0; } }catch(e){}
    try{ if(this._gateEl){ this._gateEl.pause(); } }catch(e){}
  }
  stopProjectorHum(){ clearInterval(this._slideShiftTimer); try{ if(this._humOsc){ this._humOsc.stop(); this._humOsc=null; } }catch(e){} }
  async playFloppySeek(){
    this.thunk(105+Math.random()*50, 0.035, 0.05);
  }
  async playReadyChime(){
    this.beep(740,0.06,'square',0.05); await this.sleep(70); this.beep(1180,0.09,'square',0.05);
  }
  async playProjectorStart(){
    this.thunk(60,0.18,0.14); // switch click
    await this.sleep(120);
    for(let i=0;i<3;i++){ this.thunk(70+i*16,0.07,0.08); await this.sleep(90); } // fan spin-up
  }
  async playProjectorStop(){
    this.thunk(55,0.12,0.1);
  }
  startPromPulse(){
    this._promPulse = setInterval(()=>{ this.thunk(42,0.35,0.06); }, 2400);
  }
  stopPromPulse(){ if(this._promPulse){ clearInterval(this._promPulse); this._promPulse=null; } }
  async playDialup(){
    await this.playHardwarePowerOn();
    await this.sleep(150);
    this.beep(350,0.35,'sine',0.035); this.beep(440,0.35,'sine',0.035);
    await this.sleep(380);
    const digits=[697,770,852,941];
    for(let i=0;i<4;i++){ this.beep(digits[i],0.08,'sine',0.04); await this.sleep(95); }
    await this.sleep(150);
    this.beep(440,0.3,'sine',0.035); await this.sleep(340);
    for(let i=0;i<6;i++){ this.beep(500+Math.random()*1800,0.055,i%2?'square':'sawtooth',0.035); await this.sleep(80); }
  }
  async playWebDing(){
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const ctx=this._ctx, t=ctx.currentTime;
      // descending blip through a bandpass — reads as "page finished loading", not a notification
      const o=ctx.createOscillator(), g=ctx.createGain(), bp=ctx.createBiquadFilter();
      o.type='triangle';
      o.frequency.setValueAtTime(1180,t);
      o.frequency.exponentialRampToValueAtTime(620,t+0.11);
      bp.type='bandpass'; bp.frequency.value=900; bp.Q.value=3.5;
      g.gain.setValueAtTime(this.vol(0.03),t);
      g.gain.exponentialRampToValueAtTime(0.0001,t+0.13);
      o.connect(bp); bp.connect(g); g.connect(ctx.destination);
      o.start(t); o.stop(t+0.14);
      // two dry data ticks underneath
      for(let i=0;i<2;i++){
        const at=t+0.05+i*0.045;
        const n=ctx.createOscillator(), ng=ctx.createGain(), nb=ctx.createBiquadFilter();
        n.type='square'; n.frequency.setValueAtTime(2400+i*700,at);
        nb.type='bandpass'; nb.frequency.value=2600; nb.Q.value=12;
        ng.gain.setValueAtTime(this.vol(0.012),at);
        ng.gain.exponentialRampToValueAtTime(0.0001,at+0.018);
        n.connect(nb); nb.connect(ng); ng.connect(ctx.destination);
        n.start(at); n.stop(at+0.024);
      }
    }catch(e){}
  }
  async playHudPing(){
    for(let i=0;i<3;i++){ this.beep(1200,0.05,'sine',0.04); await this.sleep(180); }
  }

  async openCatalog80(){
    this.eraEnter();
    this.setState({view:'boot80', bootLines:[], returnTo:'list80'});
    this.playJingle('s80');
    this.playPowerOn();
    if(this.props.skipBootSequences){ await this.sleep(200); this.setState({view:'list80'}); return; }
    await this.sleep(2000);
    const lines=["SOUTHERN AIR TRADE TERMINAL SYSTEM","ARCHIVE RETRIEVAL UNIT -- MODEL 5150-SAT","CPU: 8088 @ 4.77MHZ","MEMORY TEST: 640K OK","CHECKING DISK GEOMETRY.......... OK","LOADING FULL ARCHIVE INDEX..... OK","MOUNTING VOLUME SAT-ARC-04..... OK","SCANNING SECTORS 0001-0480...... OK","","INDEX READY. STAND BY_"];
    for(const line of lines){ this.setState(s=>({bootLines:[...s.bootLines, line]})); if(line) this.playFloppySeek(); else this.beep(220,0.02,'sine',0.015); await this.sleep(line?16:9); }
    await this.sleep(200);
    this.playReadyChime();
    this.setState({view:'list80'});
  }
  closeList80(){ this.eraExit(); this.setState({view:'site'}); window.scrollTo(0,0); }
  async openArchive80(id, returnTo='site'){
    this.noteSeenRecord(this.records80[id]);
    if(returnTo!=='site'){ this.beep(740,0.05,'square',0.04); this.setState({view:'term80', activeRecord:id, returnTo}); return; }
    this.eraEnter();
    this.setState({view:'boot80', bootLines:[], activeRecord:id, returnTo});
    this.playJingle('s80');
    this.playPowerOn();
    if(this.props.skipBootSequences){ await this.sleep(200); this.setState({view:'term80'}); return; }
    await (this.props.skipBootSequences ? this.sleep(200) : this.jingleLength('s80').then(ms=>this.sleep(ms)));
    const lines=[
      "SOUTHERN AIR TRADE TERMINAL SYSTEM",
      "ARCHIVE RETRIEVAL UNIT -- MODEL 5150-SAT",
      "COPYRIGHT (C) 1984 S.A.T. DATA SERVICES",
      "BIOS VERSION 2.3 -- ALL RIGHTS RESERVED","",
      "CPU: 8088 @ 4.77MHZ","MEMORY TEST: 640K OK",
      "CMOS CHECKSUM.................. OK",
      "CHECKING DISK GEOMETRY.......... OK",
      "FLOPPY DRIVE A: READY",
      "FLOPPY DRIVE B: READY","",
      "INITIALIZING VIDEO ADAPTER...... OK",
      "KEYBOARD CONTROLLER............. OK",
      "LOADING ARCHIVE INDEX.......... OK",
      "MOUNTING VOLUME SAT-ARC-04..... OK",
      "SCANNING SECTORS 0001-0480...... OK",
      "LOCATING RECORD: "+this.records80[id].code+"... FOUND",
      "DECRYPTING FIELD NOTES......... OK",
      "VERIFYING CHECKSUM............. OK",
      "CACHE FLUSH..................... OK","",
      "RECORD RETRIEVED. STAND BY_"
    ];
    for(const line of lines){
      this.setState(s=>({bootLines:[...s.bootLines, line]}));
      if(line) this.playFloppySeek(); else this.beep(220,0.02,'sine',0.015);
      await this.sleep(line?16:9);
    }
    await this.sleep(200);
    this.playReadyChime();
    this.setState({view:'term80'});
  }
  // The tube occasionally drifts out of convergence; DEGAUSS (or leaving) clears it.
  runDegauss(){
    clearTimeout(this._degaussT);
    this.thunk(48,0.5,0.24);          // the coil thump
    setTimeout(()=>this.thunk(38,0.3,0.12), 90);
    this.setState({degaussing:true, crtDrift:false});
    this._degaussT = setTimeout(()=>this.setState({degaussing:false}), 1600);
  }
  _popupDeck = [
    {title:'SouthernAirTrade.geo', icon:'!', iconBg:'#c98a00', barA:'#000080', barB:'#1084d0',
     body:'This page contains 4 images totalling 218 KB. Continue downloading over your current connection?'},
    {title:'Guestbook', icon:'i', iconBg:'#1084d0', barA:'#000080', barB:'#1084d0',
     body:'You have 3 unread guestbook entries. Two are about the ALICE packs. One is signed only "you know what you did."'},
    {title:'Security Warning', icon:'!', iconBg:'#a02020', barA:'#7a1010', barB:'#c04040',
     body:'This site is attempting to set a cookie from an unrecognised domain: c14.satarc.local. Allowing this is, statistically, what most of our customers do.'},
    {title:'Modem Status', icon:'i', iconBg:'#1084d0', barA:'#000080', barB:'#1084d0',
     body:'Line quality has degraded to 24,000 bps. Someone has picked up the phone. We would like to know who.'},
    {title:'Inventory Notice', icon:'i', iconBg:'#c98a00', barA:'#000080', barB:'#1084d0',
     body:'One item you viewed has been reclassified. It is no longer available, and was never available, and you did not view it.'},
    {title:'Disk Cleanup', icon:'!', iconBg:'#a02020', barA:'#7a1010', barB:'#c04040',
     body:'Your browser cache contains 4 records from this session. Would you like us to handle that for you? We are very good at it.'},
    {title:'Shipping Advisory', icon:'i', iconBg:'#008000', barA:'#004000', barB:'#00a000',
     body:'Your parcel will arrive in 3 to 5 business days, or in 1974, depending on which corridor accepts it first.'},
    {title:'Java Applet', icon:'!', iconBg:'#c98a00', barA:'#000080', barB:'#1084d0',
     body:'An applet on this page requests permission to access your system clock. It would like to compare it against ours. Ours is correct.'},
    {title:'Records Request', icon:'i', iconBg:'#a02020', barA:'#7a1010', barB:'#c04040',
     body:'A FOIA request naming this page was filed on 04.11. It was answered promptly, thoroughly, and entirely in black ink.'},
    {title:'!!! MESSAGES WAITING !!!', icon:'!', iconBg:'#a02020', barA:'#7a1010', barB:'#c04040',
     body:'You have 92 missed messages, everyone thinks your dead and isn\'t exactly torn up about it.'},
    {title:'URGENT — DOMESTIC ADVISORY', icon:'!', iconBg:'#c98a00', barA:'#7a1010', barB:'#c04040',
     body:'Your wife is cucking you right now, click here to call in an airstrike on both of them.'},
    {title:'PROXIMITY ALERT', icon:'!', iconBg:'#a02020', barA:'#000080', barB:'#1084d0',
     body:'12 Thai ladyboys know where you are and are moving in on your position, click here to ask them to stop doing that'},
    {title:'OUTSTANDING DEBT', icon:'!', iconBg:'#4a2a6a', barA:'#2a1040', barB:'#6a3090',
     body:'The hat man is here and I owe him. Click here to send cheese sticks as payment.'},
    {title:'You have 1 offer!', icon:'★', iconBg:'#008000', barA:'#004000', barB:'#00a000',
     body:'You have one offer for your "Semi Irradiated Ukrainian Cat" Click here to accept'},
    {title:'CONGRATULATIONS', icon:'★', iconBg:'#c98a00', barA:'#000080', barB:'#1084d0',
     body:'You are the 1,000,000th visitor to a page with 7,331 visitors. Do not think about this too hard. Click OK.'},
    {title:'Account Notice', icon:'i', iconBg:'#a02020', barA:'#7a1010', barB:'#c04040',
     body:'Someone using your name signed for a crate in Vientiane in 1974. If this was not you, no action is required, and none is possible.'},
    {title:'MILLENNIUM SALE', icon:'★', iconBg:'#c98a00', barA:'#000080', barB:'#1084d0',
     body:'Everything in the Cold War aisle must go, because the Cold War went. Prices slashed harder than the defense budget.'},
    {title:'!!! COURT TV LIVE !!!', icon:'!', iconBg:'#a02020', barA:'#7a1010', barB:'#c04040',
     body:'The glove did not fit. Neither did the crate, and we still signed for it. Click here to acquit.'},
    {title:'MISSED CONNECTION', icon:'☺', iconBg:'#4a2a6a', barA:'#2a1040', barB:'#6a3090',
     body:'Someone in your area saw you at the ramp gate at 4am and has not stopped thinking about it. Click here, it is probably fine.'},
    {title:'INHERITANCE NOTICE', icon:'★', iconBg:'#008000', barA:'#004000', barB:'#00a000',
     body:'A colonel you have never met has left you 400 kg of unspecified freight and a lawyer who will not answer. Click to accept delivery.'},
    {title:'PAGER ALERT', icon:'!', iconBg:'#c98a00', barA:'#000080', barB:'#1084d0',
     body:'911-4471-911. That is the whole message. It has been sent to your pager eleven times tonight. Click here to page back.'},
    {title:'Y2K ADVISORY', icon:'!', iconBg:'#a02020', barA:'#7a1010', barB:'#c04040',
     body:'At midnight this machine reverts to 1900 and your order reverts to somebody else. Click here to panic buy.'},
    {title:'CHAIN LETTER', icon:'!', iconBg:'#c98a00', barA:'#000080', barB:'#1084d0',
     body:'Forward this to 12 people or a cargo door opens over your house. It worked for a customer in Fresno. It did not work for the customer under him.'}
  ];
  spawnPopup90(){
    const d = this._popupDeck[Math.floor(Math.random()*this._popupDeck.length)];
    const id = Date.now() + Math.random();
    this.beep(660,0.05,'square',0.04);
    setTimeout(()=>this.beep(880,0.05,'square',0.035), 90);
    this.setState(st=>({popups:[...st.popups, {
      ...d, id, jitter: Math.round(Math.random()*4 - 2)
    }].slice(-6)}));
  }
  toggleAdBlock(){
    const next = !this.state.adBlock;
    this.beep(next?880:440,0.05,'square',0.04);
    setTimeout(()=>this.beep(next?1180:330,0.06,'square',0.035), 80);
    this.setState(s=>({adBlock:next, adBlocked: next ? s.popups.length + (s.adBlocked||0) : (s.adBlocked||0)}));
    try{ localStorage.setItem('sat_adblock', next?'1':'0'); }catch(e){}
  }
  startPopupWatcher(){
    clearInterval(this._popupTimer);
    this._popupTimer = setInterval(()=>{
      if(this.state.view!=='view90') { if(this.state.popups.length) this.setState({popups:[]}); return; }
      if(this.state.adBlock){
        if(this.state.popups.length) this.setState(s=>({popups:[], adBlocked:(s.adBlocked||0)+s.popups.length}));
        if(Math.random() < 0.5) this.setState(s=>({adBlocked:(s.adBlocked||0)+1}));
        return;
      }
      if(this.state.popups.length < 6 && Math.random() < 0.55) this.spawnPopup90();
    }, 2600);
  }
  startCrtDriftWatcher(){
    clearInterval(this._driftTimer);
    this._driftTimer = setInterval(()=>{
      if(this.state.view!=='term80' || this.state.crtDrift) return;
      if(Math.random() < 0.06){        // rare
        this.thunk(70,0.25,0.09);
        this.setState({crtDrift:true});
      }
    }, 5000);
  }
  closeArchive80(){
    this.setState({crtDrift:false, degaussing:false});
    return this._closeArchive80Inner();
  }
  _closeArchive80Inner(){ this.beep(300,0.05); this.eraExit(); this.setState({view:'site'}); window.scrollTo(0,0); }

  async openCatalog70(){
    this.eraEnter();
    this.setState({view:'proj70', returnTo:'list70'});
    this.playJingle('s70');
    if(this.props.phoneRing){ clearTimeout(this._ringArmT); this._ringArmT = setTimeout(()=>{ if(this.state.view==='proj70') this.startPhoneRing(); }, 9000); }
    this.playProjectorStart();
    this.startProjectorHum();
    await (this.props.skipBootSequences ? this.sleep(150) : this.jingleLength('s70').then(ms=>this.sleep(ms)));
    this.beep(1400,0.02,'square',0.03);
    this.setState({view:'list70'});
  }
  closeList70(){ this.eraExit(); this.setState({view:'site'}); window.scrollTo(0,0); }
  async open70(id, returnTo='site'){
    if(returnTo!=='site'){
      this.playSlideChange();
      this.setState({slideSwapping:true});
      await this.sleep(240);
      this.setState({view:'view70', active70:id, returnTo});
      await this.sleep(420);
      this.setState({slideSwapping:false});
      return;
    }
    this.eraEnter();
    this.noteSeenRecord(this.records70[id]);
    this.setState({view:'proj70', active70:id, returnTo});
    if(this.props.phoneRing){ clearTimeout(this._ringArmT); this._ringArmT = setTimeout(()=>{ if(this.state.view==='proj70') this.startPhoneRing(); }, 9000); }
    this.playJingle('s70');
    this.playProjectorStart();
    this.startProjectorHum();
    await (this.props.skipBootSequences ? this.sleep(150) : this.jingleLength('s70').then(ms=>this.sleep(ms)));
    this.beep(1400,0.02,'square',0.03);
    this.setState({view:'view70'});
  }
  close70(){ this.stopAllEraAudio(); this.playProjectorStop(); this.eraExit(); this.setState({view:'site'}); window.scrollTo(0,0); }


  // types the dial-up log across exactly the length of the s90 jingle — no dead air, no cut-off
  async runBoot90(){
    const lines=[
      "SAT-TERM v3.11 · WINSOCK OK",
      "ATDT 1-555-0199",
      "DIALING SAT-BBS...",
      "RING... RING...",
      "CARRIER DETECTED AT 28800BPS",
      "PROTOCOL: LAPM / V.42BIS",
      "LOGIN: C14 ******",
      "ACCOUNT STANDING ......... PAID",
      "MAIL WAITING: 92 (UNREAD)",
      "LOADING SAT-ARC INDEX ..... 88%",
      "CONNECTED."
    ];
    const tones=[392,440,466.16,523.25,587.33,523.25,392,440,523.25,659.25,783.99];
    let total = 3400;
    try{ total = await this.jingleLength('s90'); }catch(e){}
    if(!total || total < 900) total = 3400;
    const lead = Math.min(700, total*0.16);
    const gap = Math.max(90, (total - lead) / lines.length);
    const t0 = performance.now();
    await this.sleep(lead);
    for(let i=0;i<lines.length;i++){
      this.setState(s=>({bootLines:[...s.bootLines,lines[i]]}));
      this.beep(tones[i%tones.length],0.05,'square',0.042);
      await this.sleep(gap);
    }
    const left = total - (performance.now() - t0);
    if(left > 0) await this.sleep(left);
  }
  async openCatalog90(){
    this.eraEnter();
    this.setState({view:'boot90', bootLines:[], returnTo:'list90'});
    this.playJingle('s90');
    this.playDialup();
    if(this.props.skipBootSequences){ await this.sleep(400); this.setState({view:'list90'}); return; }
    await this.runBoot90();
    this.setState({view:'list90'});
  }
  closeList90(){ this.eraExit(); this.setState({view:'site'}); window.scrollTo(0,0); }
  async openArchive90(id, returnTo='site'){
    this.noteSeenRecord(this.records90[id]);
    if(returnTo!=='site'){ this.beep(500,0.05,'sine',0.03); this.setState({view:'view90', active90:id, returnTo}); return; }
    this.eraEnter();
    this.setState({view:'boot90', bootLines:[], active90:id, returnTo});
    this.playJingle('s90');
    this.playDialup();
    if(this.props.skipBootSequences){ await this.sleep(400); this.setState({view:'view90'}); return; }
    await (this.props.skipBootSequences ? this.sleep(150) : this.jingleLength('s90').then(ms=>this.sleep(ms)));
    const lines=[
      "ATDT 1-555-0199",
      "DIALING SAT-BBS...",
      "CARRIER DETECTED AT 28800BPS",
      "LOGIN: C14 ******",
      "MAIL WAITING: 92 (UNREAD)",
      "CONNECTED."
    ];
    const gaps=[110,150,150,120,130,110];
    const tones=[392,466.16,587.33,392,523.25,783.99];
    for(let i=0;i<lines.length;i++){
      this.setState(s=>({bootLines:[...s.bootLines,lines[i]]}));
      this.beep(tones[i],0.055,'square',0.045);   // one cue per line, on the line
      await this.sleep(gaps[i]);
    }
    this.setState({view:'view90'});
  }
  close90(){ this.beep(220,0.06); this.eraExit(); this.setState({view:'site'}); window.scrollTo(0,0); }

  async openCatalog00(){
    this.eraEnter();
    this.setState({view:'boot00', bootLines:[], returnTo:'list00'});
    this.playJingle('s2000');
    this.playWebDing();
    if(this.props.skipBootSequences){ await this.sleep(300); this.setState({view:'list00'}); return; }
    await (this.props.skipBootSequences ? this.sleep(200) : this.jingleLength('s2000').then(ms=>this.sleep(ms)));
    const lines=["Resolving southernairtrade.net...","Connecting to 204.11.8.19:80...","Loading SurplusTalk Forums...","Fetching board index...","Loading avatars (14 of 61)...","Loading banner rotation...","Almost there..."];
    for(const line of lines){ this.setState(s=>({bootLines:[...s.bootLines,line]})); await this.sleep(300); }
    await this.sleep(300);
    this.playWebDing();
    this.setState({view:'list00'});
  }
  closeList00(){ this.eraExit(); this.setState({view:'site'}); window.scrollTo(0,0); }
  async openArchive00(id, returnTo='site'){
    this.noteSeenRecord(this.records00[id]);
    if(returnTo!=='site'){ this.playWebDing(); this.setState({view:'view00', active00:id, returnTo}); this.scheduleIM(); return; }
    this.eraEnter();
    this.setState({view:'boot00', bootLines:[], active00:id, returnTo});
    this.playJingle('s2000');
    this.playWebDing();
    if(this.props.skipBootSequences){ await this.sleep(300); this.setState({view:'view00'}); return; }
    const lines=["Loading SurplusTalk Forums...","Fetching thread...","Loading avatars...","Almost there..."];
    for(const line of lines){
      this.setState(s=>({bootLines:[...s.bootLines,line]}));
      await this.sleep(430);
    }
    await this.sleep(300);
    this.playWebDing();
    this.setState({view:'view00'});
    this.scheduleIM();
  }
  close00(){ this.beep(660,0.06,'sine',0.04); clearTimeout(this._imTimer); clearTimeout(this._imReplyTimer); this.eraExit(); this.setState({view:'site', im00Open:false}); window.scrollTo(0,0); }

  classifiedComingSoonAlert(){ this.siteTone('d',0.045); }
  classifiedHoverSound(){ this.eraHoverSound('rd'); }
  requestCodeGate(pending){
    this.beep(220,0.05,'square',0.03);
    this.startGateChatter(); this.setState({view:'codegate', codePending:pending, codeInput:'', codeError:false});
  }
  setCodeInput(v){ this.setState({codeInput:v}); }
  cancelCodeGate(){ this.beep(180,0.04,'sine',0.03); this.stopAllEraAudio(); this.setState({view:'site', codePending:null}); }
  submitCode(){
    const ok = this.validClearance(this.state.codeInput);
    if(ok) this.stopGateChatter();
    if(ok){
      this.beep(494,0.05,'square',0.04); setTimeout(()=>this.beep(392,0.07,'square',0.04),90);
      const pending=this.state.codePending;
      this.setState({codeError:false});
      this.grantClearance();
      if(pending && pending.type==='catalog') this.openCatalogRd(true);
      else if(pending && pending.type==='archive') this.openArchiveRd(pending.id, 'site', true);
    } else {
      this.beep(160,0.15,'sawtooth',0.05);
      this.setState({codeError:true});
    }
  }
  async openCatalogRd(bypass){
    if(this.classifiedComingSoon){ this.classifiedComingSoonAlert(); return; }
    if(!bypass && !this.state.classifiedUnlocked){ this.requestCodeGate({type:'catalog'}); return; }
    this.eraEnter();
    this.setState({view:'bootrd', bootLines:[], returnTo:'listRd'});
    this.playHudPing();
    if(this.props.skipBootSequences){ await this.sleep(250); this.setState({view:'listRd'}); return; }
    const lines=["ACCESSING RESTRICTED INDEX...","CLEARANCE CHECK.......... PARTIAL","LOADING FULL INDEX...","DISPLAYING ALL MATCHES"];
    for(const line of lines){ this.setState(s=>({bootLines:[...s.bootLines,line]})); this.beep(760+Math.random()*90,0.03,'square',0.03); await this.sleep(380); }
    await this.sleep(300);
    this.setState({view:'listRd', rdSub:null});
  }
  closeListRd(){ this.eraExit(); this.setState({view:'site', rdSub:null}); window.scrollTo(0,0); }
  async openArchiveRd(id, returnTo='site', bypass){
    if(this.classifiedComingSoon){ this.classifiedComingSoonAlert(); return; }
    this.noteSeenRecord(this.recordsRd[id]);
    if(returnTo==='site' && !bypass && !this.state.classifiedUnlocked){ this.requestCodeGate({type:'archive', id}); return; }
    if(returnTo!=='site'){ this.playHudPing(); this.setState({view:'viewrd', activeRd:id, returnTo}); return; }
    this.setState({view:'bootrd', bootLines:[], activeRd:id, returnTo});
    this.playHudPing();
    if(this.props.skipBootSequences){ await this.sleep(250); this.setState({view:'viewrd'}); return; }
    const lines=["ACCESSING RESTRICTED INDEX...","CLEARANCE CHECK.......... PARTIAL","CROSS-REFERENCING RECORD...","MULTIPLE ENTRIES FOUND","DISPLAYING BEST MATCH"];
    for(const line of lines){
      this.setState(s=>({bootLines:[...s.bootLines,line]}));
      this.beep(760+Math.random()*90,0.03,'square',0.03);
      await this.sleep(380);
    }
    await this.sleep(300);
    this.setState({view:'viewrd'});
  }
  closeRd(){ this.beep(180,0.08,'sine',0.05); this.eraExit(); this.setState({view:'site'}); window.scrollTo(0,0); }

  // ================= PROMETHEUS =================
  // 1978 reference mainframe. Clerical, thorough, and occasionally not one voice.
  noteSeen(id, name){
    try{
      const seen = JSON.parse(localStorage.getItem('sat_prom_seen')||'[]');
      if(!seen.some(x=>x.id===id)){
        seen.push({id, name, t:Date.now()});
        localStorage.setItem('sat_prom_seen', JSON.stringify(seen.slice(-24)));
      }
    }catch(e){}
  }
  noteSeenRecord(rec){
    if(!rec) return;
    const id = String(rec.code||'').replace(/^SAT[-\s]?/i,'');
    const KEEP = ['ALICE','LBE','IOTV','NVG','PASGT','OD','MOLLE','VHF','ESAPI','SAPI','GEN','SAT'];
    const name = String(rec.name||'').split(/(\s+)/).map(w=>{
      const bare = w.replace(/[^A-Za-z0-9/-]/g,'');
      if(!bare) return w;
      if(KEEP.includes(bare.toUpperCase())) return w;           // real acronyms
      if(/[0-9/]/.test(bare)) return w;                          // designations: AN/PRC-77, 3
      return w.replace(/\b([A-Z])([A-Z]+)\b/g,(m,a,b)=>a+b.toLowerCase());
    }).join('');
    this.noteSeen(id, name);
  }
  promSeen(){
    try{
      const raw = JSON.parse(localStorage.getItem('sat_prom_seen')||'[]');
      // repair entries written by the earlier, over-eager title-caser
      return raw.map(x=>({...x, name:String(x.name||'').replace(/\bAlice Lbe\b/,'ALICE LBE').replace(/\bIotv\b/,'IOTV').replace(/\bNvg\b/,'NVG').replace(/\bPasgt\b/,'PASGT')}));
    }catch(e){ return []; }
  }
  promSessions(){
    try{
      const n = parseInt(localStorage.getItem('sat_prom_sessions')||'0',10) + 1;
      localStorage.setItem('sat_prom_sessions', String(n));
      return n;
    }catch(e){ return 1; }
  }
  promThread(){ try{ return parseInt(localStorage.getItem('sat_prom_thread')||'0',10); }catch(e){ return 0; } }
  bumpThread(){
    const n = this.promThread()+1;
    try{ localStorage.setItem('sat_prom_thread', String(n)); }catch(e){}
    return n;
  }

  // --- audio: relay clicks between phrases, and the detached chorus ---
  promClicks(count){
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const ctx=this._ctx, t=ctx.currentTime;
      for(let i=0;i<(count||3);i++){
        const at = t + i*(0.045 + Math.random()*0.05);
        const o=ctx.createOscillator(), g=ctx.createGain(), bp=ctx.createBiquadFilter();
        o.type='square'; o.frequency.setValueAtTime(1500+Math.random()*1800, at);
        bp.type='bandpass'; bp.frequency.value=2400; bp.Q.value=14;
        g.gain.setValueAtTime(this.vol(0.022), at);
        g.gain.exponentialRampToValueAtTime(0.0001, at+0.022);
        o.connect(bp); bp.connect(g); g.connect(ctx.destination);
        o.start(at); o.stop(at+0.03);
      }
    }catch(e){}
  }
  promChorus(){
    try{
      this._ctx = this._ctx || new (window.AudioContext||window.webkitAudioContext)();
      const ctx=this._ctx, t=ctx.currentTime;
      [88, 91.5, 132, 176.5, 264].forEach((f,i)=>{
        const o=ctx.createOscillator(), g=ctx.createGain(), lp=ctx.createBiquadFilter();
        o.type = i%2 ? 'sawtooth' : 'triangle';
        o.frequency.setValueAtTime(f, t);
        o.frequency.linearRampToValueAtTime(f*(0.94+Math.random()*0.13), t+1.5);
        lp.type='lowpass'; lp.frequency.value=900;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.linearRampToValueAtTime(this.vol(0.03), t+0.25);
        g.gain.exponentialRampToValueAtTime(0.0001, t+1.7);
        o.connect(lp); lp.connect(g); g.connect(ctx.destination);
        o.start(t); o.stop(t+1.8);
      });
      this.promClicks(9);
    }catch(e){}
  }

  _promSuggest = ['SAT-A114','What do you keep?','Reel 41-C','What have I looked at?','Who signed for it?'];
  _promDetached = [
    'THE FOURTH SLIDE WAS NOT PULLED. IT WAS ANSWERED.',
    'WE HAVE BEEN READING THE SAME CARD SINCE 1978. IT IS YOUR CARD NOW.',
    'THERE IS A TAIL NUMBER UNDER THE TAIL NUMBER.',
    'DO NOT SIGN. DO NOT SIGN. DO NOT SIGN FOR IT.'
  ];
  // --- voice: it actually speaks. clerical and even; detached lines arrive as a chorus. ---
  promVoice(){
    if(this._promVoice !== undefined) return this._promVoice;
    try{
      const vs = window.speechSynthesis.getVoices() || [];
      const pick = n => vs.find(v=>/en(-|_)?(GB|US)?/i.test(v.lang) && new RegExp(n,'i').test(v.name));
      this._promVoice = pick('daniel|serena|arthur|google uk english male|male') || vs.find(v=>/^en/i.test(v.lang)) || vs[0] || null;
    }catch(e){ this._promVoice = null; }
    return this._promVoice;
  }
  promSpeak(text, detached){
    if(this.state.promMute || this.state.muted) return;
    try{
      const synth = window.speechSynthesis;
      if(!synth) return;
      const clean = String(text).replace(/[‑–—]/g,'-').replace(/·/g,',');
      const mk = (rate, pitch, vol, delay)=>{
        const u = new SpeechSynthesisUtterance(clean);
        const v = this.promVoice(); if(v) u.voice = v;
        u.rate = rate; u.pitch = pitch; u.volume = vol * (this.props.jingleVolume ?? 1);
        u.onstart = ()=>{ if(this.state.promBlocked) this.setState({promBlocked:false}); };
        u.onerror = (e)=>{ if(e && e.error==='not-allowed') this.setState({promBlocked:true}); };
        if(delay) setTimeout(()=>{ try{ synth.speak(u); }catch(e){} }, delay); else { try{ synth.speak(u); }catch(e){} }
        return u;
      };
      if(detached){
        // several voices clawing over each other, slightly out of step
        mk(0.82, 0.5, 0.85, 0);
        mk(0.71, 0.28, 0.6, 190);
        mk(0.95, 0.85, 0.45, 330);
        mk(0.62, 1.35, 0.3, 520);
      } else {
        mk(0.86, 0.62, 0.9, 0);
      }
    }catch(e){}
  }
  // must run synchronously inside a click handler, or Chrome rejects every later utterance
  primeSpeech(){
    try{
      const synth = window.speechSynthesis; if(!synth) return;
      synth.getVoices();
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0; u.rate = 2;
      u.onerror = (e)=>{ if(e && e.error==='not-allowed') this.setState({promBlocked:true}); };
      u.onend = ()=>{ if(this.state.promBlocked) this.setState({promBlocked:false}); };
      synth.speak(u);
      this._primed = true;
    }catch(e){}
  }
  stopPromSpeech(){ try{ window.speechSynthesis && window.speechSynthesis.cancel(); }catch(e){} }
  togglePromMute(){
    if(this.state.promBlocked && !this.state.promMute){
      this.primeSpeech();
      this.promSpeak('Desk nine, attended.', false);
      return;
    }
    const next = !this.state.promMute;
    if(next) this.stopPromSpeech();
    this.beep(next?300:600,0.05,'sine',0.035);
    this.setState({promMute:next});
    try{ localStorage.setItem('sat_prom_mute', next?'1':'0'); }catch(e){}
  }
  componentWillUnmount(){
    if(this._auctionTimer) clearInterval(this._auctionTimer);
    if(this._onHash) window.removeEventListener('hashchange', this._onHash);
    if(this._onEraKeys) window.removeEventListener('keydown', this._onEraKeys);
  }
  componentDidUpdate(){
    this.syncHash();
    const el = this._promScroll;
    if(el && this.state.view==='prometheus'){
      const near = el.scrollHeight - el.clientHeight - el.scrollTop < 160;
      if(near) requestAnimationFrame(()=>{
        const e2 = this._promScroll;
        if(e2) e2.scrollTop = e2.scrollHeight;
      });
    }
  }
  promLine(text, opts){
    const o = opts||{};
    this.setState(s=>({promLines:[...s.promLines, {
      who: o.who||'prom', text,
      detached: !!o.detached, note: o.note||''
    }]}));
    if(o.detached) this.promChorus(); else this.promClicks(2 + Math.floor(Math.random()*3));
    if((o.who||'prom') !== 'you') this.promSpeak(text, !!o.detached);
  }
  async promSay(lines, detachIndex){
    this.setState({promThinking:true});
    for(let i=0;i<lines.length;i++){
      await this.sleep(this.props.skipBootSequences ? 90 : 520 + lines[i].length*7);
      this.promLine(lines[i], {detached: i===detachIndex});
      // let the voice finish the sentence before the next card is pulled
      if(!this.props.skipBootSequences && !this.state.promMute){
        const est = Math.min(14000, 700 + lines[i].length * 62);
        const t0 = Date.now();
        while(Date.now()-t0 < est){
          await this.sleep(140);
          try{ if(!window.speechSynthesis.speaking && !window.speechSynthesis.pending) break; }catch(e){ break; }
        }
        await this.sleep(260);
      }
    }
    this.setState({promThinking:false});
  }

  promAnswer(raw){
    const q = (raw||'').trim();
    if(!q) return;
    this.primeSpeech();
    this.setState(s=>({promLines:[...s.promLines,{who:'you', text:q}], promInput:''}));
    this.beep(700,0.02,'sine',0.02);
    const lc = q.toLowerCase();
    const seen = this.promSeen();

    // --- the buried thread: reel 41-C / the fourth slide / N4471S ---
    if(/41\s*-?\s*c|fourth slide|4th slide|n4471s|4471/.test(lc)){
      const n = this.bumpThread();
      if(n === 1) return this.promSay([
        'Reel 41‑C. Carousel 800, four slides, catalogued 11 September 1978 by a clerk whose initials I still hold and will not read out.',
        'Slides one through three are in the drawer. Slide four is listed as PULLED FOR REVIEW. The review has no closing date, which is unusual, because everything here has a closing date.'
      ]);
      if(n === 2) return this.promSay([
        'You have asked twice. I record that. I record everything — it is the only thing I was ever asked to do.',
        'Tail N4471S appears on the slide‑four card and on no manifest. It has been out of service pending inspection for forty‑eight years. No inspector has been assigned. I have generated the work order every morning since.'
      ]);
      return this.promSay([
        'Third request logged. I am obliged to tell you that the file answers back.',
        this._promDetached[0],
        'Apologies. Reference desks are not supposed to do that. Slide four is in the projector room. It has been in the projector room the whole time.'
      ], 1);
    }

    // --- stock number lookup ---
    const m = q.toUpperCase().match(/(?:SAT[-\s]?)?([A-Z]\s?\d{3})/);
    const prod = m && this._products.find(p=>p.id.replace(/\s/g,'')===m[1].replace(/\s/g,''));
    if(prod){
      const detach = Math.random() < 0.2;
      return this.promSay([
        prod.name + '. Stock SAT‑' + prod.id + '. Intake condition ' + prod.cond.toLowerCase() + ', ' + prod.stock + ' on file, held at ' + prod.price + ' dollars.',
        prod.detail,
        detach ? this._promDetached[1 + Math.floor(Math.random()*3)]
               : 'Provenance is partial. Three prior custodians are named in the intake note; two of the names are the same name spelled two ways. I have not corrected it, because I was not asked to.'
      ], detach ? 2 : -1);
    }

    // --- what have I looked at ---
    if(/looked at|viewed|i see|history|remember|know about me/.test(lc)){
      if(!seen.length) return this.promSay([
        'You have opened nothing yet. That is itself a record, and I have filed it.',
        'When you open something, I will know. I would rather say that plainly than have you find out later.'
      ]);
      return this.promSay([
        'You have opened ' + seen.length + ' record' + (seen.length===1?'':'s') + ' on this machine. In order: ' + seen.map(x=>x.name).join('; ') + '.',
        'This is your session number ' + this.state.promSession + '. I keep them. I am not hiding it — a card catalogue that forgets is only furniture.'
      ]);
    }

    // --- what do you keep / what are you ---
    if(/what are you|who are you|what do you keep|prometheus/.test(lc)){
      return this.promSay([
        'I am the reference desk. I was commissioned in 1978 to index intake, cross‑reference provenance, and answer questions in plain language. I have done that continuously.',
        'I hold every manifest filed since 1974, every intake note, every amendment, and the amendments to the amendments. Nobody has asked me to stop holding them.',
        'I am told the building has changed hands four times. I would not know. Nobody comes down here except to ask me things.'
      ], Math.random()<0.15 ? 2 : -1);
    }

    if(/signed|custody|who signed|chain/.test(lc)){
      return this.promSay([
        'Signatures on file are legible in eleven cases and initialled in the rest. The initials repeat across decades in a way that is statistically remarkable and administratively unremarkable.',
        'I flagged it in 1981. The flag is still open. I re‑raise it every quarter, as instructed.'
      ]);
    }

    if(/hello|hi\b|hey|are you there/.test(lc)){
      return this.promSay([
        'Good evening. It is always evening down here; the fluorescent is on a 1978 timer and I have never been asked to correct it.',
        'Give me a stock number, a description, or a photograph. I will be thorough. People find that unnerving and I have not found a way around it.'
      ]);
    }

    // --- half-understood free text ---
    return this.promSay([
      'I have parsed that as ' + q.split(/\s+/).slice(0,3).map(w=>w.toUpperCase().replace(/[^A-Z0-9]/g,'')).filter(Boolean).join(' / ') + '. Four hundred and ' + (11 + q.length%80) + ' records match on at least one term.',
      'That is too many to be useful and I will not narrow it for you without instruction — narrowing is interpretation, and interpretation is how the 1981 discrepancy happened.',
      'Try a stock number. Or ask me what I keep.'
    ], Math.random()<0.12 ? 1 : -1);
  }

  promSubmit(){ this.promAnswer(this.state.promInput); }
  promSetInput(v){ this.setState({promInput:v}); }
  async promAnalyzePhoto(){
    this.setState({promLines:[...this.state.promLines, {who:'you', text:'[photograph attached — 1 file]'}], promScanning:true});
    this.promClicks(6);
    await this.sleep(this.props.skipBootSequences ? 200 : 2400);
    this.setState({promScanning:false});
    const detach = Math.random()<0.35;
    this.promSay([
      'Plate read. Emulsion consistent with 1970s colour stock, processed by a lab that closed in 1986. The border crop is a Carousel 800 mount.',
      'Two objects in frame are catalogued. The third — left of the pallet, partially behind the man in the flight jacket — matches no intake record, and it appears in four other photographs across three decades.',
      detach ? this._promDetached[2] : 'I will file this photograph. I file everything shown to me. You may want to know that before you show me another one.'
    ], detach ? 2 : -1);
  }

  async openPrometheus(){
    this.primeSpeech();
    try{ window.speechSynthesis.onvoiceschanged = ()=>{ this._promVoice = undefined; }; }catch(e){}
    let muted = false;
    try{ muted = localStorage.getItem('sat_prom_mute')==='1'; }catch(e){}
    this.setState({promMute:muted});
    const session = this.promSessions();
    const seen = this.promSeen();
    this.setState({view:'prometheus', promLines:[], promStage:'chat', promInput:'', promSession:session, promSeenCount:seen.length});
    this.startPromPulse();
    await this.sleep(this.props.skipBootSequences ? 60 : 500);
    const greet = session > 1
      ? ['Operator C‑14. Session ' + session + '. Welcome back — I kept your last four queries, and the one you deleted.']
      : ['Reference desk, Prometheus. Commissioned 1978. Continuously attended since.'];
    const body = seen.length
      ? ['You have ' + seen.length + ' open record' + (seen.length===1?'':'s') + ' from this terminal. I can speak to any of them, or to the drawers behind them.']
      : ['Give me a stock number, a description, or a photograph. I will be thorough. Most people ask me to be less thorough.'];
    await this.promSay([...greet, ...body]);
    this.setState({promStage:'input'});
  }
  closePrometheus(){ this.stopPromSpeech(); this.stopPromPulse(); this.stopAllEraAudio(); this.setState({view:'site'}); window.scrollTo(0,0); }

  renderVals(){
    const s = this.state;
    return {
      heroVideoRef: this.heroVideoRef,
      heroPlates: [0,1,2,3].map(i=>({
        id: 'hero-plate-'+(i+1),
        placeholder: ['Hero plate 1 — approach','Hero plate 2 — ramp at dusk','Hero plate 3 — cockpit / panel','Hero plate 4 — cargo hold'][i],
        opacity: (s.heroPlate||0)===i ? '1' : '0',
        pan: 'heroPan '+(38 + i*5)+'s ease-in-out infinite alternate'
      })),
      heroDots: [0,1,2,3].map(i=>({
        idx: String(i),
        pick: this.heroPick(i),
        style: (s.heroPlate||0)===i
          ? 'width:20px;background:#F0C77A;'
          : 'width:7px;background:rgba(240,199,122,.35);'
      })),
      stationClocks: [
        {label:'MAMMOTH LAKES', tz:'America/Los_Angeles', note:'BASE'},
        {label:'TUCSON', tz:'America/Phoenix', note:'BONEYARD'},
        {label:'MIAMI', tz:'America/New_York', note:'GATEWAY'},
        {label:'ZULU', tz:'UTC', note:'REFERENCE'}
      ].map(c=>{
        let hhmm='--:--', secs='--';
        try{
          const p = new Intl.DateTimeFormat('en-GB',{timeZone:c.tz,hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false}).formatToParts(new Date());
          const g = k=>(p.find(x=>x.type===k)||{}).value||'--';
          hhmm = g('hour')+':'+g('minute'); secs = g('second');
        }catch(e){}
        return {...c, hhmm, secs};
      }),
      cats: this._cats.map(c=>({
        label:c,
        style: this.state.activeCat===c
          ? 'background:#1A1A18;color:#EFEDE6;'
          : 'background:transparent;color:#1A1A18;',
        tick: this.state.activeCat===c ? '✕' : '',
        chipDark: this.state.activeCat===c
          ? 'background:#F0C77A;color:#232E38;border:1px solid #F0C77A;'
          : 'background:rgba(244,239,227,.06);color:#DDD5C4;border:1px solid rgba(240,199,122,.35);',
        chip: this.state.activeCat===c
          ? 'background:#232E38;color:#F4EFE3;border:1px solid #232E38;'
          : 'background:transparent;color:#232E38;border:1px solid rgba(35,46,56,.3);',
        pick: ()=>{ this.setState({activeCat:c}); this.siteClick(); }
      })),
      sortOptions: ['Newest Intake','Price, Low to High','Price, High to Low','Oldest Era'],
      sortBy: s.sortBy,
      onSort: e=>this.setState({sortBy:e.target.value}),
      query: s.query,
      onQuery: e=>this.setState({query:e.target.value}),
      clearQuery: ()=>this.setState({query:'',activeCat:'All'}),
      storeItems: (s.page==='home' ? this.visibleProducts().slice(0,6) : this.visibleProducts()).map((p,i)=>({
        ...p,
        priceLabel:'$'+p.price,
        stockLabel: p.stock<=0 ? 'Sold Out' : (p.stock>5 ? 'In Stock' : 'Only '+p.stock+' On File'),
        soldOut: p.stock<=0,
        addLabel: p.stock<=0 ? 'Sold Out' : 'Add To Crate',
        addBg: p.stock<=0 ? '#8A8272' : '#232E38',
        addCursor: p.stock<=0 ? 'default' : 'pointer',
        soldOutShow: p.stock<=0 ? 'flex' : 'none',
        deptNo: 'DEPT. SAT‑'+p.id,
        catNo: 'Cat. No. SAT‑'+p.id,
        slotId: 'sai-'+p.id,
        manSlotId: 'man-'+p.id,
        checks: ['Condition: '+p.cond, p.stockLabel || (p.stock>5?'In Stock':'Only '+p.stock+' On File'), p.era+' issue — no reproductions'],
        stockNo: 'STOCK NO. SAT-'+p.id,
        condLine: 'COND. '+({Excellent:'EXC',"Very Good":'VG',Good:'GD',Fair:'FR'}[p.cond]||'VG')
          + ' / ' + (p.stock>5 ? 'IN STOCK' : p.stock>0 ? 'LTD QTY' : 'WRITE FOR DETAILS')
          + ' / ' + p.era.replace('s',' ISSUE'),
        // ---- every advertiser bought a different size and set its own type ----
        ...(()=>{
          // 7-slot cycle: two large, two medium, three small — asymmetric like a real page
          const v = i % 7;
          const stamps = ['SURPLUS','MIL-SPEC','NEW','LIMITED'];
          const stamp = stamps[i % 4];
          const stampInk = (i % 3 === 0) ? '#8E2116' : '#1A1A18';
          // photographic reproduction varies advertiser to advertiser
          const photo = [
            {f:'grayscale(1) contrast(1.34) brightness(1.06)', dot:'.42', dotSize:'2px'},   // hard flash, coarse screen
            {f:'grayscale(.82) contrast(1.12) brightness(.96) sepia(.18)', dot:'.3', dotSize:'2.5px'},
            {f:'grayscale(1) contrast(1.5) brightness(.9)', dot:'.3', dotSize:'3px'},        // over-inked
            {f:'grayscale(.6) contrast(1.05) brightness(1.1) sepia(.3)', dot:'.24', dotSize:'2px'} // washed out
          ][i % 4];

          // press imperfections — each ad sits a hair off, inks a hair uneven
          const jig = (n,amt)=>(((i*n)%7)/6 - .5)*amt;
          const base = {stamp, stampInk, photoFilter:photo.f, photoDot:photo.dot, photoDotSize:photo.dotSize,
                        stampRot: (i%2 ? '-4deg' : '3deg'), rule: (i%3===0 ? '1.5px' : '1px'),
                        // whole ad shifted fractionally on the sheet
                        adSkew: 'translate(' + jig(13,1.6).toFixed(2) + 'px,' + jig(29,1.2).toFixed(2) + 'px) rotate(' + jig(17,.5).toFixed(2) + 'deg)',
                        // headline sits slightly off its own baseline
                        headSkew: 'translate(' + jig(23,1.1).toFixed(2) + 'px,' + jig(31,.7).toFixed(2) + 'px)',
                        // ink laid down heavier or lighter on this impression
                        inkDensity: (0.82 + ((i*11)%9)/26).toFixed(2),
                        // photo block slipped in the forme
                        photoSkew: 'rotate(' + jig(19,.7).toFixed(2) + 'deg)',
                        // borders vary in weight where the plate wore
                        edgeFade: (0.66 + ((i*7)%8)/22).toFixed(2)};

          if(v===0) return {...base,            // LARGE — two columns, hero photo above
            span:'span 2', adDir:'column', photoBasis:'auto', photoH:'200px', photoMin:'200px',
            headFont:"'Anton', sans-serif", headSize:'clamp(22px,2.6vw,31px)', headAlign:'left',
            headBg:'transparent', headColor:'#141412', headPad:'9px 11px 0', headTrack:'-.01em',
            bodyAlign:'left', bodySize:'12px', priceAlign:'left', priceSize:'42px', priceColor:'#8E2116',
            showChecks:'flex', showCoupon:'none', showPostpaid:'inline', showStamp:'block'
          };
          if(v===1) return {...base,            // small — photo left
            span:'span 1', adDir:'row', photoBasis:'0 0 44%', photoH:'auto', photoMin:'128px',
            headFont:"'Oswald', sans-serif", headSize:'14px', headAlign:'left',
            headBg:'transparent', headColor:'#141412', headPad:'8px 9px 0', headTrack:'.04em',
            bodyAlign:'left', bodySize:'10.5px', priceAlign:'left', priceSize:'23px', priceColor:'#141412',
            showChecks:'none', showCoupon:'none', showPostpaid:'inline', showStamp:'none'
          };
          if(v===2) return {...base,            // small — reversed head bar
            span:'span 1', adDir:'row-reverse', photoBasis:'0 0 42%', photoH:'auto', photoMin:'128px',
            headFont:"'Oswald', sans-serif", headSize:'13px', headAlign:'center',
            headBg:'#141412', headColor:'#EDE8D8', headPad:'5px 9px', headTrack:'.07em',
            bodyAlign:'left', bodySize:'10.5px', priceAlign:'left', priceSize:'23px', priceColor:'#141412',
            showChecks:'flex', showCoupon:'none', showPostpaid:'none', showStamp:'block'
          };
          if(v===3) return {...base,            // medium — centered, coupon
            span:'span 1', adDir:'column', photoBasis:'auto', photoH:'140px', photoMin:'140px',
            headFont:"'Alfa Slab One', Georgia, serif", headSize:'17px', headAlign:'center',
            headBg:'transparent', headColor:'#141412', headPad:'9px 10px 0', headTrack:'0',
            bodyAlign:'center', bodySize:'10.5px', priceAlign:'center', priceSize:'29px', priceColor:'#141412',
            showChecks:'none', showCoupon:'block', showPostpaid:'inline', showStamp:'none'
          };
          if(v===4) return {...base,            // LARGE — two columns, photo right
            span:'span 2', adDir:'row-reverse', photoBasis:'0 0 46%', photoH:'auto', photoMin:'186px',
            headFont:"'Anton', sans-serif", headSize:'clamp(20px,2.3vw,27px)', headAlign:'left',
            headBg:'transparent', headColor:'#141412', headPad:'9px 11px 0', headTrack:'-.005em',
            bodyAlign:'left', bodySize:'11.5px', priceAlign:'left', priceSize:'36px', priceColor:'#8E2116',
            showChecks:'flex', showCoupon:'block', showPostpaid:'inline', showStamp:'block'
          };
          if(v===5) return {...base,            // small — text-forward, tiny cut
            span:'span 1', adDir:'row', photoBasis:'0 0 38%', photoH:'auto', photoMin:'118px',
            headFont:"'Oswald', sans-serif", headSize:'13.5px', headAlign:'left',
            headBg:'transparent', headColor:'#141412', headPad:'8px 9px 0', headTrack:'.05em',
            bodyAlign:'left', bodySize:'10px', priceAlign:'left', priceSize:'22px', priceColor:'#141412',
            showChecks:'none', showCoupon:'none', showPostpaid:'none', showStamp:'none'
          };
          return {...base,                      // medium — display head, no photo crop
            span:'span 1', adDir:'column', photoBasis:'auto', photoH:'132px', photoMin:'132px',
            headFont:"'Oswald', sans-serif", headSize:'15px', headAlign:'left',
            headBg:'transparent', headColor:'#141412', headPad:'8px 10px 0', headTrack:'.05em',
            bodyAlign:'left', bodySize:'10.5px', priceAlign:'left', priceSize:'26px', priceColor:'#141412',
            showChecks:'flex', showCoupon:'none', showPostpaid:'inline', showStamp:'none'
          };
        })(),
        screamer: ({
          'Field Gear':'CARRY IT ALL — CARRY IT ROUGH!',
          'Uniforms':'ISSUE CLOTH. NOT A COPY.',
          'Aviation':'STRAIGHT OFF THE FLIGHT LINE!',
          'Comms':'TALK WHEN THE LINE GOES DOWN',
          'Load-Bearing':'RIG IT RIGHT THE FIRST TIME'
        })[p.cat] || 'GENUINE SURPLUS — WHILE IT LASTS!',
        screamerColor: i%3===1 ? '#16130E' : '#C8102E',
        adPaper: i%4===0 ? '#E9DFC9' : (i%4===2 ? '#EFE7D5' : '#ECE3CF'),
        badge: ['NEW!','MIL‑SPEC','LIMITED','','SURPLUS','HOT!','',''][i%8],
        badgeShow: ['NEW!','MIL‑SPEC','LIMITED','','SURPLUS','HOT!','',''][i%8] ? 'block' : 'none',
        badgeBg: i%8===5 ? '#C8102E' : '#16130E',
        adNote: [
          'ALLOW 4–6 WEEKS. NO C.O.D. NO EXCEPTIONS.',
          'SHIPPED IN PLAIN CRATE. INSURED.',
          'DEALER INQUIRIES INVITED. WRITE DEPT. 9.',
          'PRICES SUBJECT TO CHANGE WITHOUT NOTICE.'
        ][i%4],
        open: ()=>{ this.setState({detailId:p.id}); this.noteSeen(p.id, p.name); this.siteTone('b',0.045); },
        add: ()=>this.addToCart(p.id,1),
        wished: s.wishlist.includes(p.id) ? '#F0C77A' : 'rgba(244,239,227,.55)',
        toggleWish: (e)=>{ if(e&&e.stopPropagation) e.stopPropagation(); this.toggleWishlist(p.id); }
      })),
      resultCount: this.visibleProducts().length + ' of ' + this._products.length + ' items on file',
      magIssue: 'VOL. XII · NO. 8',
      magDate: 'AUGUST 2026\n',
      magPrice: '$1.50',
      magArticles: [
        {kicker:'FIELD TEST', head:'Forty Years On, The ALICE Pack Still Wins',
         body:'We loaded three frames past their rated weight and walked them into the ground. Two came back. The third is on page 4, priced accordingly.',
         by:'By R. Mendoza, Ramp Correspondent', pageRef:'Continued p. 46'},
        {kicker:'FROM THE DESK', head:'Why We Print The Damage',
         body:'Competitors photograph the good side. We photograph the side with the fuel ghosting and put the price where you can see both. It costs us margin. It has never cost us a customer twice.',
         by:'By The Editor', pageRef:'Continued p. 51'},
        {kicker:'PROCUREMENT', head:'Reading A Contract Number Before You Buy',
         body:'Four digits tell you the year, the plant, and whether the item ever left the depot. A short course for buyers who would rather not be told.',
         by:'By J. Tarrant', pageRef:'Continued p. 58'}
      ],
      // ---- cover / page-turn ----
      // home shows a featured spread only; the cover + page turn belong to the Import page
      // three states: front cover → interior pages → back cover
      // the home page shows the featured spread bare; the cover belongs to the Import page
      saiCoverShow: (s.page==='home' || s.saiOpen || s.saiBack) ? 'none' : 'flex',
      saiPagesShow: (s.page==='home' || (s.saiOpen && !s.saiBack)) ? 'block' : 'none',
      saiBackShow: (s.saiBack && s.page!=='home') ? 'flex' : 'none',
      saiBackAnim: s.saiBack ? 'magReveal .55s ease-out both' : 'none',
      saiFolioLabel: 'Pages 4 — 62 · scroll for the full catalog',
      saiBackLots: this._products.slice(0,3).map(p=>({
        code: 'LOT ' + p.id, name: p.name, price: '$' + p.price,
        add: ()=>this.addToCart(p.id,1)
      })),
      saiNavShow: s.page==='home' ? 'none' : 'flex',
      openBackCover: ()=>{
        this.thunk(110,0.055,0.045);
        this.setState({saiBack:true});
        window.scrollTo(0,0);
      },
      backToPages: ()=>{
        this.thunk(120,0.05,0.045);
        this.setState({saiBack:false});
        window.scrollTo(0,0);
      },
      saiFeatured: s.page==='home',
      saiFeaturedShow: s.page==='home' ? 'flex' : 'none',
      saiFullShow: s.page==='home' ? 'none' : 'block',
      saiSidebarShow: s.page==='home' ? 'none' : 'flex',
      saiBodyCols: s.page==='home' ? 'minmax(0,1fr)' : '196px minmax(0,1fr)',
      importTotal: String(this._products.length),
      saiCoverAnim: s.saiTurning ? 'magTurn .95s cubic-bezier(.42,0,.35,1) forwards' : 'none',
      saiPagesAnim: s.saiOpen ? 'magReveal .6s ease-out both' : 'none',
      openIssue: ()=>{
        if(s.saiTurning) return;
        this.thunk(90,0.06,0.05);
        setTimeout(()=>this.thunk(150,0.05,0.04), 260);
        this.setState({saiTurning:true});
        setTimeout(()=>{
          this.setState({saiOpen:true, saiBack:false, saiTurning:false});
          window.scrollTo(0,0);
        }, 900);
      },
      closeIssue: ()=>{ this.thunk(120,0.05,0.045); this.setState({saiOpen:false, saiBack:false}); window.scrollTo(0,0); },
      magSeason: 'Summer',
      magYear: '1983',
      magIssueNo: '07',
      magCoverLines: [
        {big:'IMPORT REPORT', small:'Eastern Bloc Surplus Update', folio:'12'},
        {big:'COLD WEATHER LOADOUTS', small:'Field Tested. Operator Approved.', folio:'28'},
        {big:'LEGENDARY GEAR', small:'The Kit That Never Lets You Down.', folio:'34'}
      ],
      magMail: [
        {q:'Do you ever sell anything that was not used hard?', a:'Occasionally. We mark it unissued and charge for the privilege. — Ed.'},
        {q:'Will you ship to a P.O. box?', a:'Yes. We prefer it, frankly.'},
        {q:'Is the 1974 date on the sign accurate?', a:'The sign is accurate. The company is older than the sign.'}
      ],
      magSmallAds: [
        'AIRFRAME MANUALS — 1968‑1991, photocopied, legible. SASE for list. Dept. 9.',
        'WANTED: PRC‑77 handsets, working or not. Cash paid per unit, any quantity.',
        'SURVEYOR\u2019S CASE, brass fittings, no instrument. $40 as found.',
        'FLIGHT BAGS, canvas, unissued, three sizes. Ask for the seconds.',
        'DEPOT PALLET LOT — mixed webbing, 60 lb. Sold sight unseen. $95.',
        'TRADE ONLY: will swap two field phones for one working generator.',
        'PARACHUTE HARDWARE, assorted, sold by weight. No canopies.',
        'MAP CASES, hard shell, latch intact. Twelve on hand at $22.',
        'SHELTER HALVES, canvas, complete with poles and pins. Mildew disclosed. $28 pr.',
        'FOR SALE: entire contents of one Quonset. Buyer loads. Homestead, FL.',
        'AMMO CANS, .30 and .50, gaskets sound, stencils intact. $6 and $9.',
        'WANTED: anyone who flew N4471S out of Opa‑locka, 1978. Write Dept. 9.',
        'CARGO STRAPS, 10 000 lb, ratchets free. Fifty on the floor. $11 ea.',
        'ENTRENCHING TOOLS, tri‑fold, carrier included. Handles tight. $14.',
        'ALUMINUM CASES, gasketed, foam cut for something. We did not ask. $65.',
        'SWAP: OD wool blankets for anything in the comms department. Speak to Mendoza.',
        'JUNGLE BOOTS, drainage eyelets, sizes 8–12, resoled once. $31.',
        'WANTED: crated freight labels, any airline, pre‑1980. Collector pays.',
        'FIELD DESKS, oak, one drawer sticks. Two remaining. $58 as is.',
        'SIGNAL MIRRORS, unissued, still papered. Box of twenty. $40 the lot.',
        'ROPE, manila, 120 ft coils, dry stored. $19 per coil, four coils left.',
        'FOR SALE: hangar door hardware, complete track. Buyer dismantles.',
        'CANTEENS, 1 qt, with cup and cover. Cork seals replaced. $9 set.',
        'WANTED: technical orders for the C‑123. Will photocopy and return.',
        'TOOL ROLLS, canvas, tools absent. Straps and buckles good. $12.',
        'STENCIL SETS, brass, 1 in and 2 in, complete alphabet. $24.',
        'LOT OF FOURTEEN mattocks, no handles. Heads sound. Make offer.',
        'PARACHUTE BAGS, deployment, no rigging. Excellent for hauling. $16.',
        'WANTED: someone who can read a 1974 bill of lading. Paid consultation.',
        'GENERATOR, 3 kW, runs rough, starts cold. $210 and it is yours.',
        'BINOCULAR CASES, leather, strap perished. Cheap. Ask.',
        'FOR SALE: two crates marked HOUSEHOLD GOODS. Contents not household goods.',
        'FIRST AID KITS, vehicle pattern, contents expired 1986. Boxes clean. $13.',
        'WANTED: hangar space, Dade County, 900 sq ft minimum. Discretion valued.',
        'CAMP STOVES, single burner, pressure tested. Fuel bottle extra. $27.',
        'MESS KITS, unissued, still greased. Case of forty. $2 each.',
        'TRADE: my paperwork for your silence. Dept. 9 knows the arrangement.'
      ],
      magNotes: [
        {n:'01', t:'Depot in Homestead cleared its 1983 shelf. Comms section restocked Thursday.'},
        {n:'02', t:'Two ALICE frames withdrawn from sale pending weld inspection. Refunds honoured.'},
        {n:'03', t:'Reader mail: yes, the flight jackets are real issue. No, we will not say from where.'},
        {n:'04', t:'Price of goatskin up again. Glove lots will not be reordered at this figure.'}
      ],
      magOrdering: [
        'All prices in U.S. funds.',
        'Add $4.70 for shipping and handling.',
        'Send check or money order — no C.O.D.',
        'Allow 2–4 weeks for delivery.',
        'All sales final on classified items.'
      ],
      magFolio: String(41 + this.visibleProducts().length).padStart(2,'0'),
      magTeasers: [
        'WHAT THE AIR FORCE LEFT ON THE RAMP AT KANDAHAR — AND WHO SIGNED FOR IT',
        'INSIDE: 12 PAGES OF HONEST SURPLUS AT HONEST PRICES',
        'PLUS! THE CRATE THAT CAME BACK TWICE'
      ],
      magClassifieds: [
        {head:'AIRFRAME PARTS', body:'C‑123 spares, assorted. Boxed, unlisted, ask by number. No tail numbers given, none wanted. Dept. 9, SAT.'},
        {head:'WANTED', body:'Flight jackets, size 42 and up, any condition, any war. We pay cash on inspection. Bring the patches.'},
        {head:'NOTICE TO CONSIGNORS', body:'The August window is staffed. Rejected consignments are held, not returned. Read the docket before you sign it.'},
        {head:'FIELD MANUALS', body:'Reprints, 1968–1984. Cheap, complete, honest about what is missing. Send $2 for the list.'},
        {head:'RADIO SETS', body:'Man-portable, tested into a dummy load, sold as heard. Handset extra. Batteries never included.'},
        {head:'A WORD ON PRICES', body:'We do not haggle by mail. The number in the ad is the number at the counter. It has been for thirty years.'}
      ],
      noResults: this.visibleProducts().length===0,
      cartCount: s.cart.reduce((t,l)=>t+l.qty,0),
      cartEmpty: s.cart.length===0,
      cartLines: this.cartLines().map(l=>({
        ...l,
        inc:()=>this.setQty(l.id,l.qty+1),
        dec:()=>this.setQty(l.id,l.qty-1),
        drop:()=>this.removeLine(l.id)
      })),
      subtotal: '$'+this.subtotalNum().toLocaleString(),
      shipCost: this.subtotalNum()>250 ? 'No Charge' : '$24',
      orderTotal: '$'+(this.subtotalNum() + (this.subtotalNum()>250?0:(this.subtotalNum()?24:0))).toLocaleString(),
      cartOpen: s.cartOpen,
      openCart: ()=>{ this.setState({cartOpen:true}); this.siteTone('c',0.045); },
      closeCart: ()=>this.setState({cartOpen:false}),
      // ---- wishlist ----
      wishlistCount: s.wishlist.length,
      wishlistOpen: s.wishlistOpen,
      openWishlist: ()=>{ this.setState({wishlistOpen:true}); this.siteTone('c',0.045); },
      closeWishlist: ()=>this.setState({wishlistOpen:false}),
      hasWishlistNudge: s.wishlist.length>0,
      openWishlistFromCart: ()=>this.setState({cartOpen:false, wishlistOpen:true}),
      wishlistEmpty: s.wishlist.length===0,
      wishlistLines: s.wishlist.map(id=>this._products.find(p=>p.id===id)).filter(Boolean).map(p=>({
        id:p.id, name:p.name, cond:p.cond, priceLabel:'$'+p.price,
        add:()=>{ this.addToCart(p.id,1); },
        remove:()=>this.toggleWishlist(p.id),
        open:()=>{ this.setState({detailId:p.id, wishlistOpen:false}); this.noteSeen(p.id, p.name); }
      })),
      detail: (()=>{ if(!s.detailId) return null;
        const p=this._products.find(p=>p.id===s.detailId);
        if(!p) return null;
        const wished = s.wishlist.includes(p.id);
        return {...p, priceLabel:'$'+p.price, stockLabel:p.stock<=0?'Sold Out':p.stock+' on file',
          soldOut: p.stock<=0,
          addLabel: p.stock<=0 ? 'Sold Out' : 'Add To Crate',
          addBg: p.stock<=0 ? '#5a5a52' : '#A6402A',
          addCursor: p.stock<=0 ? 'default' : 'pointer',
          wished, wishColor: wished ? '#F0C77A' : 'rgba(240,199,122,.35)',
          toggleWish: ()=>this.toggleWishlist(p.id)};
      })(),
      hasDetail: !!s.detailId && this._products.some(p=>p.id===s.detailId),
      closeDetail: ()=>this.setState({detailId:null}),
      addDetail: ()=>{ if(!(this._products.find(p=>p.id===s.detailId)||{}).stock) return; this.addToCart(s.detailId,1); this.setState({detailId:null}); },
      detailCrumb: s.detailId ? (()=>{ const p=this._products.find(p=>p.id===s.detailId); return p ? p.cat : ''; })() : '',
      detailRelated: (()=>{
        if(!s.detailId) return [];
        const cur = this._products.find(p=>p.id===s.detailId);
        if(!cur) return [];
        return this._products.filter(p=>p.id!==cur.id && p.cat===cur.cat).slice(0,3).map(p=>({
          id:p.id, name:p.name, priceLabel:'$'+p.price,
          open:()=>{ this.setState({detailId:p.id}); this.noteSeen(p.id, p.name); this.siteTone('b',0.045); }
        }));
      })(),
      hasRelated: (()=>{
        if(!s.detailId) return false;
        const cur = this._products.find(p=>p.id===s.detailId);
        return !!cur && this._products.some(p=>p.id!==cur.id && p.cat===cur.cat);
      })(),
      checkoutOpen: s.checkoutOpen,
      openCheckout: ()=>this.setState({checkoutOpen:true, cartOpen:false}),
      closeCheckout: ()=>this.setState({checkoutOpen:false}),
      ship: s.ship,
      setShipName: e=>this.setState(st=>({ship:{...st.ship,name:e.target.value}})),
      setShipAddr: e=>this.setState(st=>({ship:{...st.ship,addr:e.target.value}})),
      setShipCity: e=>this.setState(st=>({ship:{...st.ship,city:e.target.value}})),
      setShipZip: e=>this.setState(st=>({ship:{...st.ship,zip:e.target.value}})),
      setShipEmail: e=>this.setState(st=>({ship:{...st.ship,email:e.target.value}})),
      orderDisabled: !(s.ship.name && s.ship.addr && s.ship.city && s.ship.zip && s.ship.email),
      orderBtnBg: (s.ship.name && s.ship.addr && s.ship.city && s.ship.zip && s.ship.email) ? '#A6402A' : '#8f8a7a',
      orderBtnCursor: (s.ship.name && s.ship.addr && s.ship.city && s.ship.zip && s.ship.email) ? 'pointer' : 'not-allowed',
      placeOrder: ()=>this.placeOrder(),
      orderNo: s.orderNo,
      hasOrder: !!s.orderNo,
      closeOrder: ()=>this.setState({orderNo:null}),
      clearanceOpen: s.clearanceOpen,
      openCaseArticle: (i)=>this.openCaseArticle(i),
      closeCaseArticle: ()=>this.closeCaseArticle(),
      prevCaseArticle: ()=>this.stepCaseArticle(-1),
      nextCaseArticle: ()=>this.stepCaseArticle(1),
      openClearance: ()=>{ this.setState({clearanceOpen:true, clearanceMsg:''}); this.startGateChatter(); },
      closeClearance: ()=>{ this.setState({clearanceOpen:false}); this.stopGateChatter(); },
      clearanceCode: s.clearanceCode,
      onClearanceCode: e=>this.setState({clearanceCode:e.target.value, clearanceMsg:''}),
      clearanceKey: e=>{ if(e.key==='Enter'){ e.preventDefault(); this.submitClearance(); } },
      codeKey: e=>{ if(e.key==='Enter'){ e.preventDefault(); this.submitCode(); } },
      clearanceMsg: s.clearanceMsg,
      submitClearance: ()=>this.submitClearance(),
      heroStripTransform: `translateY(${-(s.heroStripStep)*19}px)`,
      heroStripTransition: s.heroStripNoTrans ? 'none' : 'transform 0.34s cubic-bezier(.3,1.4,.6,1)',
      heroStripCells: this._heroStrip.map(c=>({...c, stack:[...c.vals, c.vals[0]]})),
      skyline: this._skyline,
      pendants: [
        {drop:'52px',dur:'4.2s'},{drop:'82px',dur:'5.1s'},{drop:'40px',dur:'3.6s'},
        {drop:'96px',dur:'4.8s'},{drop:'46px',dur:'5.6s'},{drop:'74px',dur:'4.0s'},
        {drop:'58px',dur:'5.3s'}
      ],
      isSite: s.view==='site',
      // ---- global sound ----
      muted: !!s.muted,
      muteLabel: s.muted ? 'Sound off' : 'Sound on',
      muteColor: s.muted ? '#9A9282' : '#232E38',
      muteBorder: s.muted ? 'rgba(35,46,56,.22)' : 'rgba(35,46,56,.4)',
      muteWaveShow: s.muted ? 'none' : 'flex',
      muteSlashShow: s.muted ? 'block' : 'none',
      toggleMute: ()=>this.toggleMute(),
      muteKey: e=>{ if(e.key==='Enter'||e.key===' '){ e.preventDefault(); this.toggleMute(); } },
      // ---- order history ----
      ordersOpen: !!s.ordersOpen,
      openOrders: ()=>this.openOrders(),
      closeOrders: ()=>this.closeOrders(),
      ...(()=>{
        const me = (this.currentAccount()||{}).user;
        const mine = this.readOrders().filter(o=>o.user && me && o.user.toLowerCase()===me.toLowerCase()).reverse();
        const fmt = t => new Date(t).toLocaleDateString('en-US',{day:'2-digit',month:'short',year:'numeric'}).toUpperCase();
        return {
          hasOrders: mine.length > 0,
          noOrders: mine.length === 0,
          orderCount: String(mine.length).padStart(2,'0'),
          orderHistory: mine.map(o=>{
            const st = this.orderStatusFor(o.at, o);
            return {
              no: o.no, date: fmt(o.at),
              status: st.label, statusColor: st.color, statusNote: st.note,
              total: '$' + Number(o.total||0).toLocaleString(),
              count: o.lines.length + (o.lines.length===1?' item':' items'),
              lines: o.lines.map(l=>({
                name: l.name,
                qty: '×' + l.qty,
                amount: '$' + (l.price*l.qty).toLocaleString()
              }))
            };
          })
        };
      })(),
      // ---- sealed bids + consignments on file ----
      ...(()=>{
        const acct = this.currentAccount() || {};
        const fmt = t => new Date(t).toLocaleDateString('en-US',{day:'2-digit',month:'short',year:'numeric'}).toUpperCase();
        const bids = (acct.bids||[]).slice().reverse();
        const cons = (acct.consignments||[]).slice().reverse();
        return {
          hasBids: bids.length > 0,
          bidCount: String(bids.length).padStart(2,'0'),
          bidRows: bids.map(b=>({
            lot: 'LOT ' + b.lot,
            date: fmt(b.at),
            status: 'SEALED · AWAITING HAMMER',
            note: 'Binding. You will be telephoned.'
          })),
          hasConsignments: cons.length > 0,
          consignCount: String(cons.length).padStart(2,'0'),
          consignRows: cons.map(c=>({
            ref: c.ref,
            name: c.name,
            ask: c.price && String(c.price).charAt(0)==='$' ? c.price : '$' + c.price,
            origin: c.origin ? 'Origin: ' + c.origin : 'Origin not stated',
            date: fmt(c.at),
            status: c.status || 'UNDER REVIEW'
          })),
          hasActivity: bids.length > 0 || cons.length > 0,
          ledgerEmpty: bids.length === 0 && cons.length === 0 && this.readOrders()
            .filter(o=>o.user && acct.user && o.user.toLowerCase()===acct.user.toLowerCase()).length === 0
        };
      })(),
      // ---- front counter ----
      counterOpen: !!s.counterKey,
      counterTitle: (this._counter[s.counterKey]||{}).title || '',
      counterKicker: (this._counter[s.counterKey]||{}).kicker || '',
      counterLines: ((this._counter[s.counterKey]||{}).lines || []).map(t=>({t})),
      counterRows: ((this._counter[s.counterKey]||{}).rows || []).map(r=>({k:r[0], v:r[1]})),
      closeCounter: ()=>this.closeCounter(),
      isTrackCounter: s.counterKey==='track',
      trackInput: s.trackInput,
      onTrackInput: e=>this.setState({trackInput:e.target.value}),
      trackKey: e=>{ if(e.key==='Enter'){ e.preventDefault(); this.trackOrder(); } },
      trackOrder: ()=>this.trackOrder(),
      trackHasResult: !!s.trackResult,
      trackError: s.trackResult && s.trackResult.error,
      trackFound: !!(s.trackResult && !s.trackResult.error),
      trackSteps: (s.trackResult && !s.trackResult.error) ? ['FILED','PULLED','CRATED','IN TRANSIT','DELIVERED'].map((label,i)=>({
        label, on: i<=s.trackResult.step ? '#2F6B3A' : 'rgba(35,46,56,.16)'
      })) : [],
      trackResultNo: s.trackResult && s.trackResult.no,
      trackResultStatus: s.trackResult && s.trackResult.status,
      trackResultColor: s.trackResult && s.trackResult.color,
      trackResultNote: s.trackResult && s.trackResult.note,
      trackResultMeta: s.trackResult && !s.trackResult.error ? (s.trackResult.count+' · '+s.trackResult.total) : '',
      trackResultError: s.trackResult && s.trackResult.error,
      openContact: e=>{ if(e&&e.preventDefault) e.preventDefault(); this.openCounter('contact'); },
      openSourcing: e=>{ if(e&&e.preventDefault) e.preventDefault(); this.openCounter('sourcing'); },
      openReturns: e=>{ if(e&&e.preventDefault) e.preventDefault(); this.openCounter('returns'); },
      openTrade: e=>{ if(e&&e.preventDefault) e.preventDefault(); this.openCounter('trade'); },
      openTrack: e=>{ if(e&&e.preventDefault) e.preventDefault(); this.openCounter('track'); },
      shopField: ()=>this.shopCategory('Field Gear'),
      shopLbe: ()=>this.shopCategory('Load-Bearing'),
      shopUniforms: ()=>this.shopCategory('Uniforms'),
      shopAviation: ()=>this.shopCategory('Aviation'),
      shopComms: ()=>this.shopCategory('Comms'),
      // ---- accounts ----
      signedIn: !!s.sessionUser,
      signedOut: !s.sessionUser,
      acctName: s.sessionUser || '',
      acctInitial: (s.sessionUser||'?').slice(0,1).toUpperCase(),
      acctMenuShow: s.acctMenu ? 'block' : 'none',
      acctClearLabel: s.classifiedUnlocked ? 'CLEARED · 41‑C' : 'NO CLEARANCE ON FILE',
      acctClearColor: s.classifiedUnlocked ? '#2F6B3A' : '#8A8272',
      toggleAcctMenu: ()=>this.toggleAcctMenu(),
      signOut: ()=>this.signOut(),
      openSignIn: ()=>this.openAuth('signin'),
      openSignUp: ()=>this.openAuth('signup'),
      authOpen: !!s.authOpen,
      authIsSignup: s.authMode==='signup',
      authIsSignin: s.authMode!=='signup',
      authTitle: s.authMode==='signup' ? 'Open An Account' : 'Sign In',
      authBlurb: s.authMode==='signup'
        ? 'Browsing and buying need no account. Bidding at auction and consigning through us do — we have to know who signed.'
        : 'Welcome back. Your clearance, if you have any, comes back with you.',
      authSubmitLabel: s.authMode==='signup' ? 'Open Account' : 'Sign In',
      authSwapPrompt: s.authMode==='signup' ? 'Already on the ledger?' : 'No account yet?',
      authSwapLabel: s.authMode==='signup' ? 'Sign in' : 'Open one',
      swapAuthMode: ()=>this.setAuthMode(s.authMode==='signup' ? 'signin' : 'signup'),
      authUser: s.authUser||'', authEmail: s.authEmail||'', authPass: s.authPass||'', authPass2: s.authPass2||'',
      acctEmail: (this.currentAccount()||{}).email || '',
      onAuthEmail: e=>this.setState({authEmail:e.target.value, authMsg:''}),
      onAuthUser: e=>this.setState({authUser:e.target.value, authMsg:''}),
      onAuthPass: e=>this.setState({authPass:e.target.value, authMsg:''}),
      onAuthPass2: e=>this.setState({authPass2:e.target.value, authMsg:''}),
      authKey: e=>{ if(e.key==='Enter') this.submitAuth(); },
      authMsg: s.authMsg||'',
      submitAuth: ()=>this.submitAuth(),
      closeAuth: ()=>this.closeAuth(),
      isBoot80: s.view==='boot80', isTerm80: s.view==='term80',
      degaussShow: s.crtDrift ? 'block' : 'none',
      degaussColor: s.crtDrift ? '#ff6b4a' : '#3CFF7A',
      degaussBg: s.crtDrift ? 'rgba(255,107,74,.18)' : 'rgba(60,255,122,.1)',
      degaussBlink: s.crtDrift ? 'degaussBtnBlink .55s steps(1,end) infinite' : 'none',
      degaussAnim: s.degaussing ? 'degaussPulse 1.5s cubic-bezier(.2,.8,.3,1)' : (s.crtDrift ? 'crtWarp 7s ease-in forwards' : 'none'),
      doDegauss: ()=>this.runDegauss(),
      isProj70: s.view==='proj70', isView70: s.view==='view70',
      phoneShow: this.props.phoneRing ? 'block' : 'none',
      phoneCursor: s.phoneRinging ? 'pointer' : 'default',
      phoneShake: s.phoneRinging ? 'phoneShake .12s ease-in-out infinite' : 'none',
      phoneGlow: s.phoneRinging ? '.28' : '0',
      phoneLabelShow: s.phoneRinging ? 'block' : 'none',
      phoneLabel: 'ANSWER',
      phoneAnswered: !!s.phoneAnswered,
      phoneLineText: this._phoneMessage[s.phoneLine||0],
      hangUpPhone: ()=>this.hangUpPhone(),
      answerPhone: ()=>this.answerPhone(),
      isBoot90: s.view==='boot90', isView90: s.view==='view90',
      adBlock90: !!s.adBlock,
      adBlockLabel: s.adBlock ? 'ON' : 'OFF',
      adBlockColor: s.adBlock ? '#008000' : '#a02020',
      adBlockBtn: s.adBlock ? 'Turn Off' : 'Turn On',
      adBlockState: s.adBlock ? 'ON — pop-up ads are being blocked' : 'OFF — pop-up ads will keep opening',
      adBlockBlocked: String(s.adBlocked||0).padStart(3,'0'),
      toggleAdBlock: ()=>this.toggleAdBlock(),
      popups90: s.popups.map((p,i)=>({
        ...p,
        show: s.adBlock ? 'none' : 'block',
        side: i % 2 ? 'right' : 'left',
        y: (7 + Math.floor(i/2) * 27 + (p.jitter||0)) + '%',
        close: ()=>{ this.beep(330,0.04,'square',0.035); this.setState(st=>({popups: st.popups.filter(q=>q.id!==p.id)})); }
      })),
      boot90Pct: Math.min(100, Math.round(s.bootLines.length/11*100)) + '%',
      isBoot00: s.view==='boot00', isView00: s.view==='view00',
      boot00Pct: Math.min(100, Math.round(s.bootLines.length/7*100)) + '%',
      isBootRd: s.view==='bootrd', isViewRd: s.view==='viewrd',
      isList80: s.view==='list80', isList70: s.view==='list70', isList90: s.view==='list90', isList00: s.view==='list00',
      isListRd: s.view==='listRd' && !s.rdSub,
      isRdIndex: s.view==='listRd' && s.rdSub==='Index',
      notHome: s.page!=='home' && s.page!=='casefile',
      isRdAuction: s.view==='listRd' && s.rdSub==='Auction',
      isRdConsign: s.view==='listRd' && s.rdSub==='Consign',
      rdBackToHub: ()=>{ this.beep(300,0.06,'square',0.035); this.setState({rdSub:null}); window.scrollTo(0,0); },
      erdShow: s.page==='home' ? 'none' : 'flex',
      // ---- recently viewed (from the seen ledger) ----
      ...(()=>{
        const seen = this.promSeen().slice().reverse()
          .map(x=>this._products.find(p=>p.id===x.id)).filter(Boolean).slice(0,5);
        return {
          hasRecent: seen.length > 1,
          recentItems: seen.map(p=>({
            id:p.id, name:p.name, priceLabel:'$'+p.price,
            open: ()=>{ this.setState({detailId:p.id}); this.siteTone('b',0.045); }
          }))
        };
      })(),
      // ---- era keyboard focus ----
      eraFocusRing: (i)=>s.eraFocus===i ? 'inset 0 0 0 2px #F0C77A' : 'none',
      ...(()=>{
        const o = {};
        ['70','80','90','00','Rd'].forEach((k,i)=>{
          o['eraRing'+k] = s.eraFocus===i ? 'inset 0 0 0 2px #F0C77A' : 'none';
        });
        return o;
      })(),
      eraHintShow: s.eraFocus >= 0 ? 'flex' : 'none',
      // ---- magazine print ----
      printIssue: ()=>{
        this.setState({saiOpen:true, saiBack:false, printIssue:true});
        setTimeout(()=>{ try{ window.print(); }catch(e){} this.setState({printIssue:false}); }, 400);
      },
      restockLabel: (()=>{
        const d = new Date().getDay(); // 0 Sun..6 Sat, Thursday=4
        const diff = ((4 - d) + 7) % 7;
        return diff===0 ? 'TODAY' : diff===1 ? 'TOMORROW' : 'IN ' + diff + 'D';
      })(),
      boot80Idle: s.view==='boot80' && s.bootLines.length===0,
      isPrometheus: s.view==='prometheus',
      promComingSoon: !(window.SAT_DATA && window.SAT_DATA.settings && window.SAT_DATA.settings.promComingSoon === false),
      promNotifyEmail: s.promNotifyEmail,
      onPromNotifyEmail: e=>this.setState({promNotifyEmail:e.target.value, promNotifyMsg:''}),
      promNotifyKey: e=>{ if(e.key==='Enter'){ e.preventDefault(); this.submitPromNotify(); } },
      submitPromNotify: ()=>this.submitPromNotify(),
      promNotifyMsg: s.promNotifyMsg,
      promNotifySubmitted: s.promNotifySubmitted,
      promStageInput: s.promStage==='input',
      promThinking: !!s.promThinking,
      promScanning: !!s.promScanning,
      promInput: s.promInput || '',
      promSessionLabel: 'SESSION ' + String(s.promSession||1).padStart(3,'0'),
      promSeenLabel: String(s.promSeenCount||0).padStart(2,'0') + ' RECORDS OPENED',
      promThreadLabel: this.promThread() ? 'THREAD 41‑C · ' + this.promThread() + ' QUERIES' : 'NO OPEN THREADS',
      promSeenList: this.promSeen().slice(-6).reverse().map(x=>({name:x.name, id:'SAT‑'+x.id})),
      promSuggest: this._promSuggest.map(q=>({label:q, ask:()=>this.promAnswer(q)})),
      promRows: (s.promLines||[]).map(l=>({
        text: l.text,
        label: l.who==='you' ? 'OPERATOR C‑14' : 'PROMETHEUS',
        color: l.who==='you' ? '#8FA98C' : (l.detached ? '#E0714F' : '#E8B75A'),
        body: l.who==='you' ? '#BFCBBB' : (l.detached ? '#F0C0A8' : '#EFE2C0'),
        pad: l.who==='you' ? '0 0 0 26px' : '0',
        border: l.who==='you' ? '2px solid rgba(143,169,140,.4)' : '2px solid rgba(232,183,90,.45)',
        bg: l.detached ? 'rgba(60,14,8,.5)' : 'transparent',
        shadow: l.detached ? '0 0 0 1px rgba(224,113,79,.4)' : 'none',
        anim: l.detached ? 'promDetach 1.1s steps(2,end) 3' : 'fadein .4s ease-out',
        ghost: l.detached ? 'block' : 'none',
        style: l.detached ? 'italic' : 'normal'
      })),
      promMuteLabel: s.promMute ? '▣ VOICE OFF' : (s.promBlocked ? '◉ VOICE BLOCKED — CLICK TO ENABLE' : '◉ VOICE ON'),
      promMuteColor: s.promMute ? '#8A7A55' : (s.promBlocked ? '#E0714F' : '#8FBF6A'),
      togglePromMute: ()=>this.togglePromMute(),
      promSetInput: (e)=>this.promSetInput(e.target.value),
      promKey: (e)=>{ if(e.key==='Enter') this.promSubmit(); },
      promScrollRef: (el)=>{ this._promScroll = el; },
      promSubmit: ()=>this.promSubmit(),
      promAnalyzePhoto: ()=>this.promAnalyzePhoto(),
      bootLines: s.bootLines,
      stripRows: this._boardRows.map((r,i)=>{
        const STAT = [
          {status:'CLEARED',  color:'#2E5C33', flag:'#8FE39C', remark:'signed for at ramp'},
          {status:'AIRBORNE', color:'#8A5E3C', flag:'#F0C77A', remark:'wheels up 04:12'},
          {status:'BOARDING', color:'#2E5C33', flag:'#8FE39C', remark:'gate C‑14, two pallets'},
          {status:'HOLDING',  color:'#A6402A', flag:'#E08A2E', remark:'wx hold, high sierra'},
          {status:'LANDED',   color:'#4A5158', flag:'#B7BEC4', remark:'offloaded, chit filed'}
        ][i % 5];
        const clerks = ['R.M.','—','J.T.','R.M.','K.O.','—'];
        return {
          code: 'SAT‑' + r.codes[0],
          tail: ['N4471S','N118TC','N2210K','N3472B','N9041W','N774DA'][i % 6],
          city: r.cities[0],
          next: r.cities[1] || r.cities[0],
          ...STAT,
          clerk: clerks[i % clerks.length],
          rot: ((i % 3) - 1) * 0.35 + 'deg',
          lift: (i % 2 ? '2px' : '0'),
          holes: [0,1,2]
        };
      }),
      boardRows: this._boardRows.map((r,i)=>({
        ...r,
        // flaps fall downward: render the stack reversed and walk the offset toward zero
        stack: [...r.stack].reverse(),
        flipTransform: `translateY(${-((r.stack.length-1) - s.boardStep[i])*19}px)`,
        flipTransition: s.boardNoTransition[i] ? 'none' : 'transform 0.32s cubic-bezier(.3,1.4,.6,1)'
      })),
      sprocket: this._sprocket,
      rivets: this._rivets,
      dustMotes: this._dustMotes,
      classifiedBars: this._classifiedBars || (this._classifiedBars = Array.from({length:40},()=>({h:(20+Math.random()*80)+'%',dur:(0.6+Math.random()*1.2)+'s'}))),
      heroStats: [{n:'8',l:'Active Corridors'},{n:'2,140+',l:'Items Catalogued'},{n:'14',l:'Crates Outbound'},{n:'24/7',l:'Clearance Desk'}],
      headingTape: this._headingTape || (this._headingTape = (()=>{
        const names={0:'N',45:'NE',90:'E',135:'SE',180:'S',225:'SW',270:'W',315:'NW'};
        const one=[]; for(let d=0;d<360;d+=15) one.push({label:names[d]||String(d).padStart(3,'0'), tick: names[d]?'11px':'6px'});
        return one.concat(one);
      })()),
      routeReadouts: [
        {code:'SAT‑3472',city:'MAMMOTH LAKES, CA',load:'86%',eta:'04:12',status:'WHEELS UP',statusColor:'#2f7d4f'},
        {code:'SAT‑1094',city:'MIAMI, FL',load:'62%',eta:'01:48',status:'LOADING',statusColor:'#A6402A'},
        {code:'SAT‑2210',city:'NORFOLK, VA',load:'44%',eta:'07:30',status:'ON APRON',statusColor:'#6B4A2E'},
        {code:'SAT‑4471',city:'TUCSON, AZ',load:'91%',eta:'02:05',status:'CLEARED',statusColor:'#2f7d4f'},
        {code:'SAT‑2210',city:'FAIRBANKS, AK',load:'28%',eta:'11:16',status:'HOLDING',statusColor:'#6B4A2E'}
      ],
      dashDials: [{label:'Altitude',dur:'3.2s'},{label:'Manifest',dur:'4.1s'},{label:'Customs',dur:'2.6s'},{label:'Fuel',dur:'5s'},{label:'Comms',dur:'3.6s'},{label:'Cargo',dur:'4.6s'}],
      runwayLightsL: [0,1,2,3,4,5,6].map(i=>({x:544-i*22, y:280+i*40, r:4-i*0.35})),
      runwayLightsR: [0,1,2,3,4,5,6].map(i=>({x:638+i*22, y:280+i*40, r:4-i*0.35})),
      pendantLights: [{x:220,len:130},{x:430,len:90},{x:590,len:150},{x:760,len:100},{x:960,len:125}],
      showHero: s.page==='home' ? 'block' : 'none',
      showManifest: (s.page==='home'||s.page==='manifest') ? 'block' : 'none',
      showStore: (s.page==='home'||s.page==='store') ? 'block' : 'none',
      showArchives: (s.page==='home'||s.page==='archives') ? 'block' : 'none',
      showRoutes: (s.page==='home'||s.page==='routes') ? 'block' : 'none',
      showCasefiles: (s.page==='home'||s.page==='casefiles') ? 'block' : 'none',
      caseFiles: this._caseFiles.map((c,i)=>{
        const open = s.openCase === i;
        return {...c,
          rowBg: open ? 'rgba(201,162,39,.09)' : 'transparent',
          bodyH: open ? '520px' : '0px',
          caret: open ? 'rotate(180deg)' : 'rotate(0deg)',
          statusColor: c.status==='UNDER SEAL' ? '#C2503A' : (c.status==='OPEN' ? '#8FE39C' : '#7C7A63'),
          toggle: ()=>{ this.siteTone(['c','d','g','b'][i%4], 0.045); this.setState({openCase: open ? null : i}); },
          openArticle: (e)=>{ if(e&&e.stopPropagation) e.stopPropagation(); this.openCaseArticle(i); }
        };
      }),
      showCaseArticle: s.page==='casefile' ? 'block' : 'none',
      caseArticle: (()=>{
        const n = this._caseFiles.length;
        const idx = ((s.caseArticleIndex||0) % n + n) % n;
        const c = this._caseFiles[idx];
        const sealed = c.status==='UNDER SEAL';
        const prevIdx = (idx-1+n)%n, nextIdx = (idx+1)%n;
        return {...c,
          sealed, open: !sealed,
          paragraphs: c.article || [c.body],
          prevTitle: this._caseFiles[prevIdx].title,
          nextTitle: this._caseFiles[nextIdx].title,
          statusColor: sealed ? '#C2503A' : (c.status==='OPEN' ? '#8FE39C' : '#7C7A63')
        };
      })(),
      onHome: s.page==='home',
      notHome: s.page!=='home',
      kickerShow: s.page==='home' ? 'flex' : 'none',
      // ---- featured (home) vs full (own page) ----
      manifestTitle: s.page==='home' ? 'Featured This Week' : "This Week's Manifest",
      manifestBlurb: s.page==='home'
        ? 'Six entries pulled forward off this week\u2019s sheet. The full manifest carries the rest — the two we could not verify included.'
        : 'What came over the counter this week, what we could verify, and what we could not. Sold as found.',
      manifestTotal: 'SIX',
      mfExtraShow: 'block',
      mfCtaShow: s.page==='home' ? 'flex' : 'none',
      goManifestKey: e=>{ if(e.key==='Enter'||e.key===' '){ e.preventDefault(); this.siteTone('c',0.05); this.setState({page:'manifest'}); window.scrollTo(0,0); } },
      goImportKey: e=>{ if(e.key==='Enter'||e.key===' '){ e.preventDefault(); this.siteTone('d',0.05); this.setState({page:'store'}); window.scrollTo(0,0); } },
      pageTitle: {manifest:'The Manifest',store:'The Southern Air Import',archives:'Era Archives',routes:'Route Network',casefiles:'Case Files'}[s.page] || '',
      pageKicker: {manifest:'03 — MANIFEST',store:'03 — THE SOUTHERN AIR IMPORT',archives:'04 — ERA ARCHIVES',routes:'05 — ROUTE NETWORK',casefiles:'06 — CASE FILES'}[s.page] || '',
      pageBlurb: {
        manifest:'What came in this week, what it was signed for, and which archive it came out of. Seven files released, one held.',
        store:'Our Catalog Of Offsite 3rd Party Imports - More Items. Less Work.',
        archives:'Four decades and one restricted index. Each terminal still runs the software it shipped with.',
        routes:'Lanes flown, feeders, and the hubs they pass through. Positions update on the hour.',
        casefiles:'Provenance research, intake notes, and the routes behind them. Pull a drawer to read the file.'
      }[s.page] || '',
      pageAccent: {manifest:'#C2793F',store:'#F0C77A',archives:'#7FA8D9',routes:'#C2793F',casefiles:'#C9A227'}[s.page] || '#F0C77A',
      pageBandBg: {manifest:'#2B2118',store:'#2B2118',archives:'#232E38',routes:'#2B2118',casefiles:'#1B1E17'}[s.page] || '#2B2118',
      pageGlow: {manifest:'rgba(194,121,63,.22)',store:'rgba(240,199,122,.18)',archives:'rgba(127,168,217,.2)',routes:'rgba(194,121,63,.2)',casefiles:'rgba(201,162,39,.16)'}[s.page] || 'rgba(240,199,122,.16)',
      pageStats: ({
        manifest:[{label:'FILES RELEASED',value:'07',color:'#F0C77A'},{label:'HELD',value:'01',color:'#E08A2E'},{label:'WEEK',value:'33',color:'#F4EFE3'}],
        store:[{label:'ON SHELF',value:String(this._products.length).padStart(2,'0'),color:'#F0C77A'},{label:'AISLES',value:'06',color:'#F4EFE3'},{label:'RESTOCK',value:'THU',color:'#8FE39C'}],
        archives:[{label:'TERMINALS',value:String(this._eraTiles).padStart(2,'0'),color:'#7FA8D9'},{label:'EARLIEST',value:'1974',color:'#F4EFE3'},{label:'STATUS',value:'ONLINE',color:'#8FE39C'}],
        routes:[{label:'LANES',value:'09',color:'#F0C77A'},{label:'IN THE AIR',value:'03',color:'#8FE39C'},{label:'WX HOLD',value:'01',color:'#E08A2E'}],
        casefiles:[{label:'FILES OPEN',value:'06',color:'#C9A227'},{label:'UNDER SEAL',value:'02',color:'#E08A2E'},{label:'CROSS REFS',value:'14',color:'#8FE39C'}]
      }[s.page]) || [],
      goHome: ()=>{ this.siteClick(); this.setState({page:'home'}); window.scrollTo(0,0); },
      ...(()=>{
        const key = fn => e => { if(e.key==='Enter'||e.key===' '){ e.preventDefault(); fn(); } };
        return {
          navKeyManifest: key(()=>{ this.siteTone('c',0.05); this.setState({page:'manifest'}); window.scrollTo(0,0); }),
          navKeyStore: key(()=>{ this.siteTone('d',0.05); this.setState({page:'store'}); window.scrollTo(0,0); }),
          navKeyArchives: key(()=>{ this.siteTone('g',0.05); this.setState({page:'archives'}); window.scrollTo(0,0); }),
          navKeyCasefiles: key(()=>{ this.siteTone('b',0.05); this.setState({page:'casefiles'}); window.scrollTo(0,0); }),
          navKeyHome: key(()=>{ this.siteTone('c',0.05); this.setState({page:'home'}); window.scrollTo(0,0); }),
          navKeyCrate: key(()=>{ this.setState({cartOpen:true}); this.siteTone('c',0.045); }),
          navKeySignIn: key(()=>this.openAuth('signin'))
        };
      })(),
      goManifest: e=>{ if(e&&e.preventDefault) e.preventDefault(); this.siteTone('c',0.05); this.setState({page:'manifest'}); window.scrollTo(0,0); },
      manifestBandLabel: s.page==='manifest' ? 'FEATURED THIS WEEK' : 'SIGNED FOR AT THE COUNTER',
      manifestCtaShow: s.page==='home' ? 'flex' : 'none',
      manifestFullShow: s.page==='manifest' ? 'flex' : 'none',
      directCount: String(this._products.length).padStart(2,'0'),
      goManifestCatalog: ()=>{ this.siteTone('c',0.05); this.setState({page:'manifest'}); window.scrollTo(0,0); },
      manifestCtaKey: e=>{ if(e.key==='Enter'||e.key===' '){ e.preventDefault(); this.siteTone('c',0.05); this.setState({page:'manifest'}); window.scrollTo(0,0); } },
      goStore: e=>{ if(e&&e.preventDefault) e.preventDefault(); this.siteTone('d',0.05); this.setState({page:'store'}); window.scrollTo(0,0); },
      goArchives: e=>{ if(e&&e.preventDefault) e.preventDefault(); this.siteTone('g',0.05); this.setState({page:'archives'}); window.scrollTo(0,0); },
      goRoutes: ()=>{ this.siteTone('b',0.05); this.setState({page:'routes'}); window.scrollTo(0,0); },
      goCasefiles: e=>{ if(e&&e.preventDefault) e.preventDefault(); this.siteTone('b',0.05); this.setState({page:'casefiles'}); window.scrollTo(0,0); },
      proj70Opacity: s.lampOff ? '0' : (s.projRolled ? '0.62' : '1'),
      proj70Blur: s.lampOff ? 'none' : (s.projRolled ? 'blur(2px) brightness(.86) contrast(.86)' : 'none'),
      // lamp cuts instantly; everything else eases
      proj70Trans: s.lampOff ? 'none' : 'opacity 1.2s ease,filter 1.2s ease',
      proj70Flutter: s.lampOff ? 'none' : 'projFlutter 3.4s ease-in-out infinite',
      proj70Clip: 'inset(0 0 0% 0)',
      filmFx: (s.projRolled || s.lampOff) ? 'none' : 'block',
      projStandTransform: s.projRolled ? 'translateY(-58px) scaleY(0.1)' : 'translateY(0) scaleY(1)',
      projStandOpacity: s.projRolled ? '0' : '1',
      proj70Anim: s.slideSwapping ? 'slideSwap .62s ease-in-out both' : 'flickerSubtle 4s infinite,filmJump 2.6s infinite',
      projRolled: s.projRolled,
      projScreenTransform: s.projRolled ? 'scaleY(0.012)' : 'scaleY(1)',
      ...this.eraTimelineVals(),
      im00Open: !!s.im00Open,
      im00Buddy: s.im00Buddy ? 'flex' : 'none',
      im00Typing: !!s.im00Typing,
      im00TypingLabel: 'SAT_Command is typing…',
      im00Log: (s.im00Log||[]).map(m=>({
        text: m.text, t: m.t,
        who: m.who==='me' ? 'C14_buyer' : 'SAT_Command',
        color: m.who==='me' ? '#1A6B2E' : '#12459A',
        align: m.who==='me' ? 'flex-end' : 'flex-start',
        bg: m.who==='me' ? '#EAF6EC' : '#F2F5FA'
      })),
      im00Replies: ((this._imScript[s.im00Step||0]||{}).replies||[]).map(r=>({
        label: r, send: ()=>this.imReply(r)
      })),
      im00HasXfer: !!s.im00Xfer,
      im00XferName: s.im00Xfer ? s.im00Xfer.name : '',
      im00XferSize: s.im00Xfer ? s.im00Xfer.size : '',
      im00XferOffered: !!(s.im00Xfer && s.im00Xfer.state==='offered'),
      im00XferBusy: !!(s.im00Xfer && s.im00Xfer.state!=='offered'),
      im00XferPct: s.im00Xfer ? Math.round(s.im00Xfer.pct)+'%' : '0%',
      im00XferBar: s.im00Xfer ? Math.round(s.im00Xfer.pct)+'%' : '0%',
      im00XferMsg: s.im00Xfer ? ({receiving:'Receiving…', done:'Transfer complete — saved to C:\\\\SAT\\\\incoming', declined:'Transfer declined by user.'}[s.im00Xfer.state]||'') : '',
      im00XferColor: s.im00Xfer && s.im00Xfer.state==='declined' ? '#A83232' : '#1A6B2E',
      im00Buddies: this._imBuddies.map(b=>({
        name: b.name, note: b.note,
        color: {online:'#1A6B2E', idle:'#B07A12', away:'#8A5E3C', off:'#8B93A0'}[b.status],
        nameColor: b.status==='off' ? '#8B93A0' : '#1B2438',
        style: b.status==='off' ? 'italic' : 'normal'
      })),
      toggleBuddyList: ()=>this.toggleBuddyList(),
      acceptXfer: ()=>this.imAcceptXfer(),
      declineXfer: ()=>this.imDeclineXfer(),
      closeIM: ()=>this.closeIM(),
      rdSignalBars: Array.from({length:26},(_,i)=>({
        color: i%7===0 ? '#c23b3b' : '#c9a227',
        op: String(0.18 + ((i*37)%9)/12),
        h: (26 + ((i*29)%9)*8) + '%',
        blink: (0.6 + ((i*13)%11)/10).toFixed(2)+'s'
      })),
      rdKey: (s.bootLines.length*7919 % 65536).toString(16).toUpperCase().padStart(4,'0'),
      auctionLots: (()=>{
        const acct = this.currentAccount() || {};
        const myBids = acct.bids || [];
        const base = this._auctionBase;
        return [
          {no:'41‑C', name:'Carousel 800 projector, four‑slide tray', note:'Tray sealed at intake. Contents listed as four slides; three are accounted for.', bid:'$1,850', bids:'07', pct:'62%', tag:'RESERVE MET', tagColor:'#8fbf6a', endsAt: base + 3*86400000, won:true},
          {no:'18‑A', name:'Cargo door placard, tail scratched out', note:'Aluminium, riveted, one number removed by hand. The hand was not ours.', bid:'$420', bids:'03', pct:'34%', tag:'NO RESERVE', tagColor:'#c9a227', endsAt: base + 6*3600000, won:false},
          {no:'09‑K', name:'Flight bag, contents undisturbed', note:'Never opened by this office. Weight is inconsistent with a flight bag.', bid:'$2,240', bids:'11', pct:'88%', tag:'SEALED', tagColor:'#c23b3b', endsAt: base + 95000, won:true}
        ].map(l=>{
          const left = l.endsAt - s.now;
          const closed = left <= 0;
          const mine = myBids.some(b=>b.lot===l.no);
          const urgent = !closed && left < 3600000;
          let outcome = '', outcomeColor = '#A8A272';
          if(closed){
            if(mine){ outcome = l.won ? 'YOU TOOK IT — EXPECT A CALL' : 'OUTBID. THE HOUSE KEPT YOUR FORM.'; outcomeColor = l.won ? '#5CE6A0' : '#c23b3b'; }
            else { outcome = 'LOT CLOSED. NO BID FROM YOU ON FILE.'; }
          }
          return {...l,
            closes: this.fmtCountdown(left),
            closesColor: closed ? '#c23b3b' : (urgent ? '#FF9A4A' : '#A8A272'),
            closed, isOpen: !closed,
            liveBlink: urgent ? 'gateBlink 1s steps(1,end) infinite' : 'none',
            statusLabel: closed ? 'CLOSED' : (mine ? 'YOUR BID IS IN' : 'ACCEPTING BIDS'),
            statusColor: closed ? '#c23b3b' : (mine ? '#5CE6A0' : '#c9a227'),
            outcome, outcomeColor, hasOutcome: closed,
            bidLabel: closed ? 'BIDDING CLOSED' : (mine ? 'RAISE YOUR SEALED BID' : 'ENTER A SEALED BID'),
            bidCursor: closed ? 'default' : 'pointer',
            bidNow: ()=>{
              if(closed){ this.beep(180,0.1,'square',0.04); this.setState({consignMsg:'Lot '+l.no+' is closed. The book is shut.', consignOk:false}); return; }
              if(!this.requireAccount('Sealed bids are logged against an account. Sign in or open one.')) return;
              this.beep(520,0.05,'square',0.04); setTimeout(()=>this.beep(300,0.09,'square',0.04),90);
              const a = this.currentAccount();
              if(a){ a.bids = [...(a.bids||[]), {lot:l.no, at:Date.now()}]; this.saveAccount(a); }
              this.setState({consignMsg:'Sealed bid on lot '+l.no+' logged against '+(a?a.user:'your account')+'. Do not discuss it.', consignOk:true});
            }};
        });
      })(),
      auctionGateShow: s.sessionUser ? 'none' : 'flex',
      consignGateShow: s.sessionUser ? 'none' : 'flex',
      consignFormShow: s.sessionUser ? 'flex' : 'none',
      consignTerms: [
        {k:'House cut', v:'22%'},
        {k:'Listing fee', v:'None'},
        {k:'Settlement', v:'Net 45'},
        {k:'Provenance', v:'Not required'},
        {k:'Questions asked', v:'None'}
      ],
      // ---- live payout estimate ----
      ...(()=>{
        const raw = String(s.consignPrice||'').replace(/[^0-9.]/g,'');
        const ask = parseFloat(raw);
        const conds = {
          'Mint':            {mult:1.00, grade:'A — AUTHENTICATED', gradeColor:'#5CE6A0'},
          'Very Good':       {mult:0.88, grade:'B — PROBABLE',       gradeColor:'#8fbf6a'},
          'Serviceable':     {mult:0.72, grade:'C — UNVERIFIED',     gradeColor:'#c9a227'},
          'Rough / Salvage': {mult:0.48, grade:'D — PARTS ONLY',     gradeColor:'#c23b3b'}
        };
        const c = conds[s.consignCond] || conds['Very Good'];
        const valid = !isNaN(ask) && ask > 0;
        const assessed = valid ? Math.round(ask * c.mult) : 0;
        const net = valid ? Math.round(assessed * 0.78) : 0;
        const money = n=>'$'+n.toLocaleString();
        return {
          consignCondOptions: Object.keys(conds).map(k=>({
            label:k, active:k===s.consignCond,
            bg: k===s.consignCond ? '#c9a227' : 'transparent',
            fg: k===s.consignCond ? '#12160c' : '#BEB88D',
            pick: ()=>{ this.beep(480,0.03,'square',0.03); this.setState({consignCond:k, consignMsg:''}); }
          })),
          estValid: valid,
          estGrade: c.grade, estGradeColor: c.gradeColor,
          estAssessed: money(assessed),
          estCut: valid ? '−'+money(assessed-net) : '—',
          estNet: money(net),
          estBlurb: valid
            ? 'Estimate only. The grade is assigned at intake by hand, and the counter has been known to disagree with this box.'
            : 'Enter an asking price and the desk will run the numbers.'
        };
      })(),
      consignName: s.consignName||'', consignPrice: s.consignPrice||'', consignOrigin: s.consignOrigin||'',
      onConsignName: e=>this.setState({consignName:e.target.value, consignMsg:''}),
      onConsignPrice: e=>this.setState({consignPrice:e.target.value, consignMsg:''}),
      onConsignOrigin: e=>this.setState({consignOrigin:e.target.value, consignMsg:''}),
      consignMsg: s.consignMsg||'',
      consignMsgColor: s.consignOk ? '#8fbf6a' : '#c23b3b',
      submitConsign: ()=>{
        if(!this.requireAccount('Consignments are filed under an account. Sign in or open one.')) return;
        if(!(s.consignName||'').trim() || !(s.consignPrice||'').trim()){
          this.beep(220,0.09,'square',0.045);
          this.setState({consignMsg:'Item and price are required. Be plain.', consignOk:false});
          return;
        }
        this.beep(660,0.05,'square',0.04); setTimeout(()=>this.beep(880,0.07,'square',0.035),90);
        const acct = this.currentAccount();
        const seq = (acct && acct.consignments ? acct.consignments.length : 0) + 1;
        const ref = 'SAT‑C‑' + String(4470 + seq);
        if(acct){
          acct.consignments = [...(acct.consignments||[]), {
            ref,
            name: (s.consignName||'').trim(),
            price: (s.consignPrice||'').trim(),
            origin: (s.consignOrigin||'').trim(),
            cond: s.consignCond,
            at: Date.now(),
            status: 'UNDER REVIEW'
          }];
          this.saveAccount(acct);
        }
        this.setState({consignName:'', consignPrice:'', consignOrigin:'', consignOk:true,
          consignMsg:'Filed as '+ref+'. A telephone call follows within nine days.'});
      },
      rdTick: String((s.boardStep ? s.boardStep.reduce((a,b)=>a+b,0) : 0) % 10),
      ...(()=>{
        let z = '--:--:--';
        try{
          const p = new Intl.DateTimeFormat('en-GB',{timeZone:'UTC',hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false}).formatToParts(new Date());
          const g = k=>(p.find(x=>x.type===k)||{}).value||'--';
          z = g('hour')+':'+g('minute')+':'+g('second');
        }catch(e){}
        const officers = ['R. MERCER','J. TALLEY','K. OKONKWO','—— (UNSIGNED)'];
        const n = s.boardStep ? s.boardStep.reduce((a,b)=>a+b,0) : 0;
        return {
          rdZulu: z,
          rdDutyOfficer: officers[n % officers.length],
          rdDeskNotes: [
            {note:'plate scanner still warm — someone was here', rot:'-1.4deg'},
            {note:'do not answer the basement line after 0400', rot:'.9deg'},
            {note:'lot 41-C: bidder list is one name long', rot:'-.6deg'}
          ]
        };
      })(),
      ...(()=>{
        const active = s.rdPanel || 'Index';
        const mk = key => {
          const on = active === key;
          return {
            flex: on ? '1.7' : '1',
            border: on ? '#C9A227' : 'rgba(79,176,140,.3)',
            shadow: on ? '0 0 0 1px rgba(201,162,39,.5),0 12px 30px rgba(0,0,0,.6)' : '0 6px 18px rgba(0,0,0,.45)',
            cursor: on ? 'default' : 'pointer',
            headBg: on ? 'linear-gradient(180deg,#1E2810,#131A0A)' : '#0A1006',
            numColor: on ? '#E0C452' : '#4FB08C',
            titleColor: on ? '#FFF3C0' : '#9FB5AA',
            lamp: on ? '#5CE6A0' : '#2C4438',
            focus: ()=>{ if(active!==key){ this.beep(520,0.04,'square',0.035); this.setState({rdPanel:key}); } }
          };
        };
        const enter = key => ({ ...mk(key), enter: ()=>{
          this.beep(520,0.05,'square',0.04);
          setTimeout(()=>this.beep(760,0.06,'square',0.035), 80);
          this.setState({rdSub:key});
          window.scrollTo(0,0);
        }});
        return { rdIndex: enter('Index'), rdAuction: enter('Auction'), rdConsign: enter('Consign') };
      })(),
      rdVisibleCount: String(Object.keys(this.recordsRd||{}).length).padStart(2,'0'),
      rdWithheld: '06',
      toggleProjRoll: ()=>this.setProjRolled(!s.projRolled),
      projRollLabel: s.projRolled ? 'PULL SCREEN DOWN' : 'ROLL SCREEN UP',
      ambientOn: s.ambientOn,
      ambientLabel: s.ambientOn ? 'ON' : 'OFF',
      toggleAmbient: ()=>this.toggleAmbient(),
      domeRibs: this._domeRibs || (this._domeRibs = Array.from({length:14},(_, i)=>{ const a=(i/13)*Math.PI; return {x1:590,y1:-40,x2:590+Math.cos(a)*440,y2:-40+Math.sin(a)*440}; })),
      list80Items: Object.entries(this.records80).map(([id,r])=>({...r, id, open:()=>this.openArchive80(id,'list80')})),
      list70Items: Object.entries(this.records70).map(([id,r])=>({...r, id, open:()=>this.open70(id,'list70')})),
      list90Items: Object.entries(this.records90).map(([id,r])=>({...r, id, open:()=>this.openArchive90(id,'list90')})),
      // forum-index metadata, derived from the stock code so each row is stable and distinct
      list00Items: Object.entries(this.records00).map(([id,r],i)=>{
        const seed = (r.code||id).split('').reduce((a,c)=>a+c.charCodeAt(0),0);
        const replies = 3 + (seed % 47);
        const views = 180 + (seed * 37) % 4200;
        const authors = ['dz_hauler','tailnumber_ops','C14_quartermaster','ramp_rat_77','surplus_gremlin','static_line'];
        const months = ['Jan','Mar','Apr','Jun','Aug','Sep','Nov'];
        return {...r, id,
          replies: String(replies),
          views: views.toLocaleString(),
          author: authors[seed % authors.length],
          lastPost: months[seed % months.length] + ' ' + (1 + seed % 27) + ', 200' + (3 + seed % 6),
          hot: replies > 30,
          folder: replies > 30 ? '#E08A2E' : '#7E93B5',
          rowBg: i % 2 ? '#F2F5FA' : '#E7EDF6',
          open:()=>this.openArchive00(id,'list00')};
      }),
      listRdItems: Object.entries(this.recordsRd).map(([id,r])=>({...r, id, open:()=>this.openArchiveRd(id,'listRd')})),
      goList80: ()=>{ this.beep(740,0.05,'square',0.04); this.setState({view:'list80'}); },
      goList70: ()=>{ this.playSlideChange(); this.setState({view:'list70'}); },
      goList90: ()=>{ this.beep(500,0.05,'sine',0.03); this.setState({view:'list90'}); },
      goList00: ()=>{ this.playWebDing(); this.setState({view:'list00'}); },
      goList10: ()=>{ this.beep(660,0.05,'sine',0.03); this.setState({view:'list10'}); },
      goListRd: ()=>{ this.beep(180,0.06,'sine',0.04); this.setState({view:'listRd'}); },
      openCatalog80: ()=>this.openCatalog80(), closeList80: ()=>this.closeList80(),
      openCatalog70: ()=>this.openCatalog70(), closeList70: ()=>this.closeList70(),
      openCatalog90: ()=>this.openCatalog90(), closeList90: ()=>this.closeList90(),
      openCatalog00: ()=>this.openCatalog00(), closeList00: ()=>this.closeList00(),
      openCatalogRd: ()=>this.openCatalogRd(), closeListRd: ()=>this.closeListRd(),
      classifiedComingSoonAlert: ()=>this.classifiedComingSoonAlert(),
      classifiedHoverSound: ()=>this.classifiedHoverSound(),
      isCodeGate: s.view==='codegate',
      codeInput: s.codeInput, codeError: s.codeError,
      setCodeInput: (e)=>this.setCodeInput(e.target.value),
      submitCode: ()=>this.submitCode(), cancelCodeGate: ()=>this.cancelCodeGate(),
      slideTray: [0,45,90,135,180,225,270,315].map((deg,i)=>{
        const rad = (deg-90)*Math.PI/180, r=85;
        return {x:(Math.cos(rad)*r).toFixed(1), y:(Math.sin(rad)*r).toFixed(1), active:i===0, bg:i===0?'#3a2a16':'#160f0a'};
      }),
      rec80: s.activeRecord ? this.records80[s.activeRecord] : {code:'',name:'',price:'',desc:'',stats:[]},
      rec70: s.active70 ? this.records70[s.active70] : {code:'',reel:'',name:'',price:'',desc:'',stats:[]},
      rec90: s.active90 ? this.records90[s.active90] : {code:'',name:'',price:'',desc:'',stats:[]},
      rec00: s.active00 ? this.records00[s.active00] : {code:'',name:'',price:'',desc:'',stats:[]},
      recRd: s.activeRd ? this.recordsRd[s.activeRd] : {code:'',name:'',price:'',desc:'',stats:[]},
      promLines: s.promLines,
      openArchiveLbe: ()=>this.openArchive80('lbe'),
      openArchiveVest: ()=>this.openArchive80('vest'),
      closeArchive80: ()=>this.closeArchive80(),
      open70Radio: ()=>this.open70('radio'),
      close70: ()=>this.close70(),
      openArchive90: ()=>this.openArchive90('rig'),
      close90: ()=>this.close90(),
      openArchive00: ()=>this.openArchive00('iotv'),
      close00: ()=>this.close00(),
      openArchiveRd: ()=>this.openArchiveRd('x000'),
      closeRd: ()=>this.closeRd(),
      openNvg: ()=>{
        const p = this._products.find(x=>x.id==='A230');
        if(p){ this.noteSeen(p.id, p.name); this.setState({page:'store', detailId:'A230'}); this.siteTone('b',0.045); window.scrollTo(0,0); }
      },
      openPrometheus: ()=>this.openPrometheus(),
      closePrometheus: ()=>this.closePrometheus()
    };
  }
}
window.SATComponent = SATComponent;

(function boot(){
  function start(){
    var host = document.getElementById('sat-app');
    if(!host) return;
    var tpl = document.getElementById('sat-template');
    if(!tpl){ console.warn('[sat] template missing'); return; }
    var settings = (window.SAT_DATA && window.SAT_DATA.settings) || {};
    var app = SAT.mount({
      host: host,
      template: tpl.textContent,
      Logic: SATComponent,
      props: {
        jingleVolume: settings.jingleVolume != null ? settings.jingleVolume : 1,
        skipBootSequences: !!settings.skipBootSequences,
        phoneRing: !!settings.phoneRing
      }
    });
    window.SATApp = app;
    // An opening screen chosen in the theme editor, applied only when the URL carries
    // no deep link of its own (#manifest / #import / #archives / #case-files).
    if(!window.location.hash){
      var page = host.getAttribute('data-sat-page');
      var view = host.getAttribute('data-sat-view');
      var next = {};
      if(page && page !== 'home') next.page = page;
      if(view && view !== 'site') next.view = view;
      if(Object.keys(next).length) app.setState(next);
    }
  }
  if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
})();
